import {
    Button,
    Card,
    Checkbox,
    DatePicker,
    Form,
    Input,
    message,
    Modal,
    Progress,
    Space
} from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { DEFAULT_PAGE_SIZE } from './Proxies/constants';
import ProxyCreationLogs from '../components/ProxyCreationLogs';
import ProxyTable from '../components/proxy-table';
import ProxyCreatePanel from '../components/proxy-create-panel';
import ProxyEditPanel from '../components/proxy-edit-panel/ProxyEditPanel';
import CustomSelect from '../components/custom-select/CustomSelect';
import ProxySandboxPanel from '../components/proxy-sandbox-panel/ProxySandboxPanel';
import ProxyStatsPanel from '../components/proxy-stats-panel/ProxyStatsPanel';
import SyncStatsModal from '../components/SyncStatsModal';
import CheckResultBanner from '../components/check-result-banner/CheckResultBanner';
import { apiService } from '../services/api';
import { ensureAdminCheckerQueueLoaded, readAdminCheckerQueue, writeAdminCheckerQueue } from '../constants/adminCheckerQueue';
import { isLocationCheckProxy, LOCATION_CHECK_TARIFF_NAME } from '../constants/locationCheck';
import '../styles/sticky-columns.css';
import { initTokenCheck } from '../utils/browserTokenCheck';
import { handleApiError } from '../utils/errorHandler';
import logger from '../utils/logger';
import { resolveOsFingerprintColumnDisplay } from '../utils/osfp-display';
import { mergeModemStatsForPanel } from '../utils/modem-stats-from-ports';

/** Строка передачи в чекер: {protocol}://{domain_host}:{port}:{login}:{password} (как в шаблонах экспорта). */
function buildAdminCheckerConnectionLine(p) {
  if (!p || typeof p !== 'object') {
    return '';
  }
  const raw = String(p.proxy_type || 'http').toUpperCase();
  let protocol = 'http';
  if (raw.includes('SOCK')) {
    protocol = raw.includes('4') && !raw.includes('5') ? 'socks4' : 'socks5';
  }
  const domainHost = String(p.domain_host || p.server_ip || '').trim();
  const port = p.port != null && p.port !== '' ? String(p.port).trim() : '';
  const login = p.login != null ? String(p.login) : '';
  const password = p.password != null ? String(p.password) : '';
  return `${protocol}://${domainHost}:${port}:${login}:${password}`;
}

const sendAgentDebugLog = () => {};

// ============================================================================
// ЕДИНЫЕ ФУНКЦИИ КОНВЕРТАЦИИ СКОРОСТИ
// ============================================================================
/**
 * Конвертирует скорость из КБайт/с в Мб/с
 * Формула: КБайт/с * 8 / 1000 = Мб/с
 * @param {number} kbytesPerSec - Скорость в КБайт/с
 * @returns {number} Скорость в Мб/с (округлено до 2 знаков)
 */
const convertKBytesToMbps = (kbytesPerSec) => {
  if (!kbytesPerSec || kbytesPerSec <= 0) return 0;
  // ИСПРАВЛЕНО: Правильная формула КБайт/с → Мб/с
  // 1 КБайт/с = 8 Кбит/с = 0.008 Мб/с
  // Формула: КБайт/с * 8 / 1000 = Мб/с
  const result = Math.round((kbytesPerSec * 8 / 1000) * 100) / 100;
  // Логирование для отладки
  if (kbytesPerSec > 100) {
    logger.debug('convertKBytesToMbps', {
      input: kbytesPerSec,
      calculation: `${kbytesPerSec} * 8 / 1000`,
      result
    });
  }
  return result;
};

/**
 * Конвертирует скорость из Мб/с в КБайт/с (для отправки в Kraken)
 * Формула: Мб/с * 1000 / 8 = КБайт/с
 * @param {number} mbps - Скорость в Мб/с
 * @returns {number} Скорость в КБайт/с (округлено)
 */
const convertMbpsToKBytes = (mbps) => {
  if (!mbps || mbps <= 0) return 0;
  return Math.round(mbps * 1000 / 8);
};

/** Парсинг списков ID ресурсов / портов для полей Kraken (allow, targetport) */
const parseKrakenIntList = (raw) => {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) {
    return raw.map((x) => parseInt(x, 10)).filter((n) => Number.isFinite(n));
  }
  return String(raw).trim().split(/[,;\s]+/).filter(Boolean)
    .map((x) => parseInt(x, 10))
    .filter((n) => Number.isFinite(n));
};

const krakenIdListsEqual = (a, b) => {
  const norm = (v) => (Array.isArray(v) ? v : []).map((x) => parseInt(x, 10)).filter((n) => Number.isFinite(n));
  return JSON.stringify(norm(a)) === JSON.stringify(norm(b));
};

const formatKrakenIdsInput = (arr) => (Array.isArray(arr) ? arr : []).join(', ');

/** Общее bool-значение Kraken, если у всех выбранных прокси оно совпадает */
const resolveCommonKrakenBool = (values) => {
  if (!values.length) return undefined;
  const normalized = values.map((v) => !!v);
  return normalized.every((v) => v === normalized[0]) ? normalized[0] : undefined;
};

/** Общая строка ID (allow/targetport), если списки совпадают у всех прокси */
const resolveCommonKrakenIntListInput = (lists) => {
  if (!lists.length) return undefined;
  const serialized = lists.map((list) => JSON.stringify(parseKrakenIntList(list)));
  if (!serialized.every((s) => s === serialized[0])) return undefined;
  return formatKrakenIdsInput(parseKrakenIntList(lists[0]));
};

/** Массовое редактирование списков Kraken: пусто — не менять, «0» — очистить у всех */
const resolveBulkKrakenListUpdate = (rawValue, baselineDisplay) => {
  if (rawValue === undefined || rawValue === null) return undefined;
  const trimmed = String(rawValue).trim();
  if (trimmed === '') return undefined;
  if (trimmed === '0') return [];

  const baselineTrimmed = baselineDisplay != null ? String(baselineDisplay).trim() : '';
  if (baselineTrimmed !== '' && trimmed === baselineTrimmed) return undefined;

  return parseKrakenIntList(rawValue);
};

const BULK_KRAKEN_LIST_PLACEHOLDER = 'Не изменять · 0 — очистить у всех';

const mergeKrakenPermissionsIntoProxyRecord = (proxy, updateData) => {
  if (!proxy || !updateData) return proxy;
  const hasPermissionUpdate = (
    updateData.kraken_is_allow !== undefined
    || updateData.kraken_is_ports !== undefined
    || updateData.kraken_allow !== undefined
    || updateData.kraken_targetport !== undefined
  );
  if (!hasPermissionUpdate) return proxy;

  const nextKc = { ...(proxy.kraken_characteristics || {}) };
  if (updateData.kraken_is_allow !== undefined) {
    nextKc.is_allow = updateData.kraken_is_allow;
  }
  if (updateData.kraken_is_ports !== undefined) {
    nextKc.is_ports = updateData.kraken_is_ports;
  }
  if (updateData.kraken_allow !== undefined) {
    nextKc.allow = updateData.kraken_allow;
  }
  if (updateData.kraken_targetport !== undefined) {
    nextKc.targetport = updateData.kraken_targetport;
  }
  return { ...proxy, kraken_characteristics: nextKc };
};

const patchProxiesKrakenPermissions = (proxies, proxyIds, updateData) => {
  if (!Array.isArray(proxies) || !Array.isArray(proxyIds) || !updateData) return proxies;
  const idSet = new Set(proxyIds.map((id) => String(id)));
  return proxies.map((proxy) => {
    const pid = String(proxy?.proxy_id ?? proxy?.id ?? '');
    if (!idSet.has(pid)) return proxy;
    return mergeKrakenPermissionsIntoProxyRecord(proxy, updateData);
  });
};

/**
 * Определяет единицы измерения slimiter и конвертирует в Мб/с
 * В БД: TariffPlan.slimiter хранится в КБайт/с
 * В БД: FlexibleTariff.slimiter может быть в КБайт/с или бит/с (старые данные)
 * В БД: Proxy.slimiter хранится в КБайт/с (конвертируется из бит/с в SQL запросе)
 * @param {number} slimiter - Значение slimiter
 * @param {boolean} isFromTariff - true если значение из тарифа, false если из прокси (КБайт/с)
 * @returns {number} Скорость в Мб/с (округлено до 2 знаков)
 */
const convertSlimiterToMbps = (slimiter, isFromTariff = true) => {
  if (!slimiter || slimiter <= 0) return 0;

  // ИСПРАВЛЕНО: И тарифы, и прокси теперь хранят slimiter в КБайт/с
  // Все значения конвертируются одинаково: КБайт/с → Мб/с
  return convertKBytesToMbps(slimiter);
};

const escapeRegex = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const normalizeOperatorTemplates = (rawTemplates) => {
  if (!Array.isArray(rawTemplates)) return [];
  return rawTemplates
    .filter((item) => item && typeof item === 'object')
    .map((item) => ({
      name: String(item.name || '').trim(),
      patterns: Array.isArray(item.patterns)
        ? item.patterns.map((pattern) => String(pattern || '').trim()).filter(Boolean)
        : [],
    }))
    .filter((item) => item.name);
};

const resolveOperatorByTemplates = (rawOperator, templates) => {
  const value = String(rawOperator || '').trim();
  if (!value || ['unknown', 'none', 'null', 'n/a'].includes(value.toLowerCase())) return 'UNKNOWN';
  const preparedTemplates = normalizeOperatorTemplates(templates);
  if (preparedTemplates.length === 0) return value;

  for (const template of preparedTemplates) {
    for (const pattern of template.patterns) {
      try {
        const regex = new RegExp(`^${escapeRegex(pattern).replace(/\\\*/g, '.*')}$`, 'i');
        if (regex.test(value)) return template.name;
      } catch {
        continue;
      }
    }
  }
  return value;
};

const normalizeOperatorForCompare = (value) => String(value || '').trim().toLowerCase();

const operatorsMatch = (a, b) => {
  const na = normalizeOperatorForCompare(a);
  const nb = normalizeOperatorForCompare(b);
  if (!na || !nb) return false;
  return na === nb || na.includes(nb) || nb.includes(na);
};

const normalizeOperatorFilterDisplayValue = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return '';
  const lower = normalized.toLowerCase();
  if (['unknown', 'none', 'null', 'n/a'].includes(lower)) return 'Неизвестно';
  return normalized;
};

const parseClientLoginFilterFromSearchParams = (params) => {
  const login = String(params.get('client_login') || '').trim();
  if (!login) {
    return null;
  }
  return { client_login: [login] };
};

/** Deep link с NetworkPorts: ?modem=№+38+… — фильтр колонки «Модем» (как в filterValues.modem). */
const parseModemFilterFromSearchParams = (params) => {
  if (!params || typeof params.get !== 'function') {
    return null;
  }
  const raw = String(params.get('modem') || '').trim();
  if (!raw) {
    return null;
  }
  return { modem: [raw] };
};

const Proxies = () => {
  // ИСПРАВЛЕНО: Используем useSearchParams для сохранения номера страницы в URL
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Добавляем состояние для мобильной версии
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [proxies, setProxies] = useState([]);

  // Все состояния должны быть объявлены перед использованием в хуках
  const [clients, setClients] = useState([]);
  const [tariffPlans, setTariffPlans] = useState([]);
  const [proxyTypes, setProxyTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [bulkChecking, setBulkChecking] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [overlayClosing, setOverlayClosing] = useState(false);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [statusData, setStatusData] = useState(null);
  const [proxyPanelExtraStats, setProxyPanelExtraStats] = useState({
    for_sale_operators: {},
    for_sale_operators_by_city: {},
    for_sale_total: 0,
  });
  const lastProxyTableStatsRef = useRef(null);
  const [creatingProxy, setCreatingProxy] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);

  const [editModalVisible, setEditModalVisible] = useState(false);
  const [bulkEditModalVisible, setBulkEditModalVisible] = useState(false);
  const [, setSelectedProxy] = useState(null);
  const [editingProxy, setEditingProxy] = useState(null);
  const [form] = Form.useForm();

  const [bulkEditForm] = Form.useForm();
  const [bulkData, setBulkData] = useState({});
  const [bulkPlacementOperators, setBulkPlacementOperators] = useState([]);
  const bulkKrakenBaselineRef = useRef({});
  const bulkKrakenFetchGenRef = useRef(0);
  const bulkKrakenUserEditedRef = useRef(false);
  const setBulkField = useCallback((field, value) => {
    if (['kraken_is_allow', 'kraken_is_ports', 'kraken_allow', 'kraken_targetport'].includes(field)) {
      bulkKrakenUserEditedRef.current = true;
    }
    setBulkData((prev) => ({ ...prev, [field]: value }));
  }, []);
  const [, setClientMode] = useState('existing'); // 'existing' или 'new'
  const [exportVisible, setExportVisible] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState('');
  const [updatingProxy, setUpdatingProxy] = useState(false);
  // УБРАНО: Состояние трафика - не используется, чтобы не загружать систему
  // const [trafficData, setTrafficData] = useState({});
  // const [loadingTraffic, setLoadingTraffic] = useState(false);
  const [exportTemplates, setExportTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(undefined);
  const [customPattern, setCustomPattern] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [exportScope, setExportScope] = useState('selected'); // all|client|selected|new
  const [exportClient, setExportClient] = useState('');
  const [exportVarsOpen, setExportVarsOpen] = useState(false);
  const [createTplVisible, setCreateTplVisible] = useState(false);
  const [tplForm] = Form.useForm();
  const [inlineTplMode, setInlineTplMode] = useState(false);
  const [inlineTplName, setInlineTplName] = useState('');

  // Убеждаемся, что proxies всегда является массивом (после всех useState, перед useEffect)
  const safeProxies = useMemo(() => {
    const result = Array.isArray(proxies) ? proxies : [];
    return result;
  }, [proxies]);

  const getProxyKey = useCallback((item) => {
    if (!item || typeof item !== 'object') return null;
    const original = item._original || null;
    return original?.proxy_id ?? original?.id ?? item.proxy_id ?? item.id ?? null;
  }, []);

  // ИСПРАВЛЕНО: Оптимизировано логирование - только в dev режиме и с debounce
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const count = Array.isArray(proxies) ? proxies.length : 'invalid';
      // ИСПРАВЛЕНО: Логируем только если количество изменилось значительно
      const timeoutId = setTimeout(() => {
        logger.debug('Изменилось состояние прокси', { count });
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [proxies]);

  // Остальные состояния
  const [availableCities, setAvailableCities] = useState([]);
  const [availableOperators, setAvailableOperators] = useState([]);
  const [allAvailableOperators, setAllAvailableOperators] = useState([]);

  // Настройки модема при создании прокси
  const [selectedTariffForCreate, setSelectedTariffForCreate] = useState(null);
  const [osfpList, setOsfpList] = useState([]);  // Список TCP отпечатков
  const [osfpRefreshLoading, setOsfpRefreshLoading] = useState(false);
  const [, setClientOptions] = useState([]);
  const [availableServers, setAvailableServers] = useState([]);
  const [krakenConnections, setKrakenConnections] = useState([]);
  const [filteredKrakenConnections, setFilteredKrakenConnections] = useState([]);
  const [filteredServers, setFilteredServers] = useState([]);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const [syncing, setSyncing] = useState(false);
  const [checkingProxies, setCheckingProxies] = useState(new Set());
  const [proxyCheckSummary, setProxyCheckSummary] = useState(null);
  /** Баннер вкл/выкл (одиночный и массовый), без полноэкранного шарика */
  const [powerToggleBanner, setPowerToggleBanner] = useState(null);
  const [syncStatsVisible, setSyncStatsVisible] = useState(false);
  const [syncStats, setSyncStats] = useState(null);
  const [filterValues, setFilterValues] = useState({
    client_login: [],
    tariff_plan: [],
    availability_status: [],
    auto_replace_enabled: [],
    server_name: [],
    selected_city: [],
    selected_city_counts: [],
    selected_operator: [],
    modem_reconnect_type: [],
    port: [],
    modem: [],
    proxy_type: [],
    is_active: [],
    is_keepalive: [],
    is_compression: [],
    tcp_connections: [],
    modem_osfp: [],
  });

  const [showCreationLogs] = useState(false);
  const [sandboxVisible, setSandboxVisible] = useState(false);
  const [sandboxStats, setSandboxStats] = useState(null);
  const [operatorTemplates, setOperatorTemplates] = useState([]);
  const [statsVisible, setStatsVisible] = useState(false);
  const [, setStatusLoading] = useState(false);
  const [logsRefreshKey, setLogsRefreshKey] = useState(0);
  const [selectionResetToken, setSelectionResetToken] = useState(0);

  useEffect(() => {
    if (isFirstLoad && !loading) {
      setIsFirstLoad(false);
    }
  }, [isFirstLoad, loading]);

  // UI прогресс создания прокси (минималистичный прогресс + результаты по каждой прокси)

  // ИСПРАВЛЕНО: Восстанавливаем номер страницы из URL при инициализации
  const initialPage = parseInt(searchParams.get('page') || '1', 10);
  const initialPageSize = DEFAULT_PAGE_SIZE;

  const [pagination, setPagination] = useState({
    current: initialPage,
    pageSize: initialPageSize,  // Размер страницы по умолчанию
    total: 0,  // ОПТИМИЗАЦИЯ: Добавлен total для серверной пагинации
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} прокси`,
    pageSizeOptions: ['10', '15', '20', '50', '100'],
    size: 'default'
  });
  const backgroundFetchingRef = useRef(false);
  const manualFetchingRef = useRef(false);
  const abortControllerRef = useRef(null);
  /** Поколение запроса списка: устаревшие ответы не должны затирать более свежий availability. */
  const proxiesFetchGenerationRef = useRef(0);
  /** Какое поколение сейчас владеет каждым локом (чтобы abort не снимал лок у нового запроса и не оставлял залипший). */
  const backgroundFetchGenRef = useRef(0);
  const manualFetchGenRef = useRef(0);
  const filteredInfoRef = useRef(filteredInfo);
  const paginationRef = useRef(pagination);
  const mountedRef = useRef(false);
  const statusLoadedRef = useRef(false);
  const referenceDataLoadedRef = useRef(false);
  const clientsReferenceLoadedRef = useRef(false);
  const clientsReferenceLoadingRef = useRef(false);
  const paginationUpdateRef = useRef(false);
  const tariffLoadWarningShownRef = useRef(false);
  /** Пропустить один sync из URL после применения ?availability= в init (без двойного запроса). */
  const skipAvailabilityUrlSyncOnceRef = useRef(false);

  useEffect(() => { filteredInfoRef.current = filteredInfo; }, [filteredInfo]);
  useEffect(() => { paginationRef.current = pagination; }, [pagination]);

  const normalizeProxyList = useCallback((payload, label = 'список') => {
    if (Array.isArray(payload)) {
      return payload;
    }
    if (payload && Array.isArray(payload.data)) {
      return payload.data;
    }
    if (payload && Array.isArray(payload.items)) {
      return payload.items;
    }
    if (payload && Array.isArray(payload.results)) {
      return payload.results;
    }
    if (payload) {
      try {
        logger.warn(`Не удалось нормализовать ${label}`, {
          payloadType: typeof payload,
          payloadKeys: Object.keys(payload)
        });
      } catch (normalizationError) {
        logger.warn(`Не удалось нормализовать ${label}: ошибка чтения структуры`, {
          message: normalizationError.message
        });
      }
    }
    return [];
  }, []);

  const extraStatsLoadedRef = useRef(false);
  const extraStatsFetchingRef = useRef(false);
  const operatorTemplatesRef = useRef([]);

  const hasCalculatedStats = useCallback((payload) => {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      return false;
    }

    return [
      payload.total_proxies,
      payload.available_proxies,
      payload.unavailable_proxies,
      payload.total_modems,
      payload.available_modems,
      payload.unavailable_modems,
      payload.for_sale_total,
    ].some((value) => Number(value || 0) > 0);
  }, []);

  useEffect(() => {
    operatorTemplatesRef.current = Array.isArray(operatorTemplates) ? operatorTemplates : [];
  }, [operatorTemplates]);

  const loadProxyPanelExtraStats = useCallback(async () => {
    if (document.visibilityState !== 'visible') {
      return;
    }

    if (extraStatsFetchingRef.current) {
      return;
    }

    extraStatsFetchingRef.current = true;
    try {
      const templatesRequest =
        operatorTemplatesRef.current.length > 0
          ? Promise.resolve({ data: { data: { templates: operatorTemplatesRef.current } } })
          : apiService.getOperatorTemplates().catch(() => ({ data: { data: { templates: [] } } }));

      const [templatesResponse, connectionsResponse, warehouseResponse] = await Promise.all([
        templatesRequest,
        apiService.getKrakenConnections().catch(() => ({ data: { connections: [] } })),
        apiService.getDashboardOperatorWarehouseStock().catch(() => ({ data: { rows: [] } })),
      ]);

      const connections = connectionsResponse?.data?.connections
        || connectionsResponse?.data?.data?.connections
        || [];
      const modemStatsForPanel = mergeModemStatsForPanel(connections, []);
      const templates = templatesResponse?.data?.data?.templates || [];
      const normalizedTemplates = normalizeOperatorTemplates(templates);
      setOperatorTemplates(normalizedTemplates);

      const warehousePayload = warehouseResponse?.data?.data || warehouseResponse?.data || {};
      const warehouseRows = Array.isArray(warehousePayload.rows) ? warehousePayload.rows : [];
      const forSaleOperators = {};
      const forSaleOperatorsByCity = { МСК: {}, СПБ: {} };
      warehouseRows.forEach((row) => {
        const label = String(row?.operator || '').trim();
        if (!label) return;
        const spb = Number(row.individual_spb || 0);
        const msk = Number(row.individual_msk || 0);
        const total = spb + msk;
        if (total > 0) forSaleOperators[label] = total;
        if (msk > 0) forSaleOperatorsByCity.МСК[label] = msk;
        if (spb > 0) forSaleOperatorsByCity.СПБ[label] = spb;
      });
      const forSaleOperatorsByCityClean = Object.entries(forSaleOperatorsByCity).reduce((acc, [city, ops]) => {
        if (ops && Object.keys(ops).length > 0) acc[city] = ops;
        return acc;
      }, {});
      const forSaleStats = {
        for_sale_operators: forSaleOperators,
        for_sale_operators_by_city: forSaleOperatorsByCityClean,
        for_sale_total: Object.values(forSaleOperators).reduce((sum, value) => sum + Number(value || 0), 0),
      };

      const nextExtraStats = {
        ...forSaleStats,
        ...(modemStatsForPanel || {}),
      };
      setProxyPanelExtraStats((prev) => {
        if (!hasCalculatedStats(nextExtraStats)) {
          return prev;
        }
        return {
          ...(prev || {}),
          ...nextExtraStats,
        };
      });
      extraStatsLoadedRef.current = true;
    } catch (error) {
      logger.error('Ошибка загрузки расширенной статистики для ProxyStatsPanel:', error);
      if (!extraStatsLoadedRef.current) {
        setProxyPanelExtraStats((prev) => prev || {
          for_sale_operators: {},
          for_sale_operators_by_city: {},
          for_sale_total: 0,
        });
      }
    } finally {
      extraStatsFetchingRef.current = false;
    }
  }, [hasCalculatedStats]);

  useEffect(() => {
    loadProxyPanelExtraStats();

    const interval = setInterval(loadProxyPanelExtraStats, 180000);
    return () => clearInterval(interval);
  }, [loadProxyPanelExtraStats]);

  const mergedStatsData = useMemo(() => ({
    ...(statusData || {}),
    ...(proxyPanelExtraStats || {}),
  }), [statusData, proxyPanelExtraStats]);

  const normalizeAvailabilityApiFilter = (value) => {
    if (value === undefined || value === null) return null;
    const raw = String(value).trim();
    if (!raw) return null;

    const normalized = raw.toLowerCase();
    if (normalized === 'none' || normalized === 'нет данных' || normalized === 'no data' || normalized === 'null') {
      return 'none';
    }
    if (normalized === 'доступно') return 'Доступен';
    if (normalized === 'недоступно') return 'Недоступен';
    return raw;
  };

  /** Deep link / дашборд: ?availability=unavailable → фильтр колонки «Доступность» как «Недоступен». */
  const parseAvailabilityFilterFromSearchParams = (params) => {
    if (!params || typeof params.get !== 'function') {
      return null;
    }
    const raw = (params.get('availability') || '').trim().toLowerCase();
    if (!raw) {
      return null;
    }
    if (raw === 'unavailable' || raw === 'недоступно' || raw === 'offline') {
      return { availability_status: ['Недоступен'] };
    }
    if (raw === 'available' || raw === 'доступно' || raw === 'online') {
      return { availability_status: ['Доступен'] };
    }
    return null;
  };

  // Функция для преобразования фильтров Ant Design в параметры API
  // ИСПРАВЛЕНО: Поддержка множественных значений для всех фильтров
  const convertFiltersToApiParams = useCallback((filters) => {
    const params = {};

    // ИСПРАВЛЕНО: Передаем массив значений вместо одного значения
    if (filters?.client_login && filters.client_login.length > 0) {
      // Если один элемент - передаем как строку для обратной совместимости
      // Если несколько - передаем как массив
      params.clientLogin = filters.client_login.length === 1 ? filters.client_login[0] : filters.client_login;
    }

    if (filters?.tariff_plan && filters.tariff_plan.length > 0) {
      params.tariffPlan = filters.tariff_plan.length === 1 ? filters.tariff_plan[0] : filters.tariff_plan;
    }

    if (filters?.is_active !== null && filters.is_active !== undefined && filters.is_active.length > 0) {
      if (filters.is_active.length === 1) {
        const val = filters.is_active[0];
        if (val === true || val === 'Активно') params.isActive = true;
        else if (val === false || val === 'Неактивно') params.isActive = false;
        else params.isActive = val === true;
      } else {
        params.isActive = null;
      }
    }

    if (filters?.availability_status && filters.availability_status.length > 0) {
      const normalizedAvailability = Array.from(new Set(
        filters.availability_status
          .map((val) => normalizeAvailabilityApiFilter(val))
          .filter(Boolean)
      ));
      if (normalizedAvailability.length > 0) {
        params.availabilityStatus = normalizedAvailability.length === 1
          ? normalizedAvailability[0]
          : normalizedAvailability;
      }
    }

    if (filters?.server_name && filters.server_name.length > 0) {
      // ИСПРАВЛЕНО: Поддержка множественных серверов
      params.serverName = filters.server_name.length === 1 ? filters.server_name[0] : filters.server_name;
    }

    if (filters?.selected_city && filters.selected_city.length > 0) {
      params.selectedCity = filters.selected_city.length === 1 ? filters.selected_city[0] : filters.selected_city;
    }

    if (filters?.selected_operator && filters.selected_operator.length > 0) {
      params.selectedOperator = filters.selected_operator.length === 1 ? filters.selected_operator[0] : filters.selected_operator;
    }

    if (filters?.modem_reconnect_type && filters.modem_reconnect_type.length > 0) {
      params.modemReconnectType = filters.modem_reconnect_type.length === 1 ? filters.modem_reconnect_type[0] : filters.modem_reconnect_type;
    }

    // ИСПРАВЛЕНО: Поддержка множественных портов
    if (filters?.port && filters.port.length > 0) {
      params.port = filters.port.length === 1 ? filters.port[0] : filters.port;
    }

    // ИСПРАВЛЕНО: Поддержка множественных модемов
    if (filters?.modem && filters.modem.length > 0) {
      params.modem = filters.modem.length === 1 ? filters.modem[0] : filters.modem;
    }

    if (filters?.proxy_type && filters.proxy_type.length > 0) {
      params.proxyType = filters.proxy_type.length === 1 ? filters.proxy_type[0] : filters.proxy_type;
    }

    if (filters?.auto_replace_enabled && filters.auto_replace_enabled.length > 0) {
      if (filters.auto_replace_enabled.length === 1) {
        const val = filters.auto_replace_enabled[0];
        const normalizedVal = typeof val === 'string' ? val.trim().toLowerCase() : val;
        if (
          val === true ||
          normalizedVal === 'вкл' ||
          normalizedVal === 'true' ||
          normalizedVal === '1' ||
          normalizedVal === 'on'
        ) {
          params.autoReplaceEnabled = true;
        } else if (
          val === false ||
          normalizedVal === 'выкл' ||
          normalizedVal === 'false' ||
          normalizedVal === '0' ||
          normalizedVal === 'off'
        ) {
          params.autoReplaceEnabled = false;
        }
      }
    }

    if (filters?.is_keepalive && filters.is_keepalive.length > 0) {
      if (filters.is_keepalive.length === 1) {
        const val = filters.is_keepalive[0];
        if (val === true || val === 'Вкл') params.isKeepalive = true;
        else if (val === false || val === 'Выкл') params.isKeepalive = false;
      }
    }

    if (filters?.is_compression && filters.is_compression.length > 0) {
      if (filters.is_compression.length === 1) {
        const val = filters.is_compression[0];
        if (val === true || val === 'Вкл') params.isCompression = true;
        else if (val === false || val === 'Выкл') params.isCompression = false;
      }
    }

    // ИСПРАВЛЕНО: Поддержка фильтрации по скорости
    if (filters?.speed && filters.speed.length > 0) {
      params.speed = filters.speed.length === 1 ? filters.speed[0] : filters.speed;
    }

    // ИСПРАВЛЕНО: Поддержка фильтрации по TCP соединениям
    if (filters?.tcp_connections && filters.tcp_connections.length > 0) {
      params.tcpConnections = filters.tcp_connections.length === 1 ? filters.tcp_connections[0] : filters.tcp_connections;
    }
    if (filters?.modem_osfp && filters.modem_osfp.length > 0) {
      params.modemOsfp = filters.modem_osfp.length === 1 ? filters.modem_osfp[0] : filters.modem_osfp;
    }

    if (filters?.expires_at && filters.expires_at.length > 0) {
      if (filters.expires_at.includes('unlimited')) {
        params.expiresAtUnlimited = true;
      }

      const dateValue = filters.expires_at.find((item) => item !== 'unlimited');
      if (Array.isArray(dateValue)) {
        // Диапазон дат
        if (dateValue[0]) {
          // Преобразуем в формат ISO string для API
          const fromDate = dateValue[0] instanceof Date ? dateValue[0].toISOString().split('T')[0] : dateValue[0];
          params.expiresAtFrom = fromDate;
        }
        if (dateValue[1]) {
          // Преобразуем в формат ISO string для API
          const toDate = dateValue[1] instanceof Date ? dateValue[1].toISOString().split('T')[0] : dateValue[1];
          params.expiresAtTo = toDate;
        }
      } else if (dateValue) {
        // Одна дата
        const singleDate = dateValue instanceof Date ? dateValue.toISOString().split('T')[0] : dateValue;
        params.expiresAtFrom = singleDate;
        params.expiresAtTo = singleDate;
      }
    }

    return params;
  }, []);

  const fetchClientsReferenceData = useCallback(async (forceRefresh = false) => {
    if (clientsReferenceLoadingRef.current) {
      return;
    }
    if (clientsReferenceLoadedRef.current && !forceRefresh) {
      return;
    }

    clientsReferenceLoadingRef.current = true;
    try {
      const clientsRes = await apiService.getClients();
      const nextClients = Array.isArray(clientsRes?.data) ? clientsRes.data : [];
      setClients(nextClients);
      setClientOptions(nextClients.map((client) => ({
        value: client.login,
        label: client.login,
      })));
      clientsReferenceLoadedRef.current = true;
    } catch (error) {
      clientsReferenceLoadedRef.current = false;
      logger.error('fetchClientsReferenceData: не удалось загрузить клиентов', error);
    } finally {
      clientsReferenceLoadingRef.current = false;
    }
  }, []);

  // ОПТИМИЗАЦИЯ: Разделяем загрузку справочных данных и прокси
  // Справочные данные загружаются один раз при монтировании
  const fetchReferenceData = useCallback(async (forceRefresh = false, retryCount = 0) => {
    if (referenceDataLoadedRef.current && !forceRefresh) {
      logger.debug('fetchReferenceData: справочные данные уже загружены, пропускаем');
      return;
    }

    const maxRetries = 2;

    try {
      logger.info('fetchReferenceData: начинаем загрузку справочных данных', { retryCount, forceRefresh });

      // Загружаем только критически важные справочные данные (БЕЗ обращения к Kraken API)
      // Kraken данные (connections, servers) загружаются по требованию, синхронизация происходит в фоне каждые 15 минут
      const [flexibleTariffRes, proxyTypesRes, availableCitiesRes, availableOperatorsRes, filterValuesRes] = await Promise.allSettled([
        apiService.getFlexibleTariffs(),
        apiService.getProxyTypes(),
        apiService.getAvailableCities(),
        apiService.getAvailableOperators(),
        apiService.getProxyFilterValues()
      ]);

      const getSettledData = (result, fallback = null) =>
        result.status === 'fulfilled' ? result.value : fallback;

      const flexibleTariffData = getSettledData(flexibleTariffRes, { data: [] });
      const proxyTypesData = getSettledData(proxyTypesRes, { data: [] });
      const availableCitiesData = getSettledData(availableCitiesRes, { data: { cities: [] } });
      const availableOperatorsData = getSettledData(availableOperatorsRes, { data: { operators: [] } });
      const filterValuesData = getSettledData(filterValuesRes, null);
      setProxyTypes(proxyTypesData.data || []);

      // Используем гибкие тарифы, при отсутствии - пробуем fallback на обычные тарифы
      // ИСПРАВЛЕНО: Проверяем, что ответ - массив (axios оборачивает в .data)
      const flexibleTariffs = Array.isArray(flexibleTariffData.data) ? flexibleTariffData.data : (Array.isArray(flexibleTariffData) ? flexibleTariffData : []);
      logger.info(`fetchReferenceData: получено ${flexibleTariffs.length} гибких тарифов`);
      let formattedTariffs = flexibleTariffs.map(tariff => ({
        id: tariff.id,
        name: tariff.name,
        kraken_bitrate_in: tariff.kraken_bitrate_in,
        kraken_bitrate_out: tariff.kraken_bitrate_out,
        connection_limit: tariff.connection_limit,
        is_udp_enabled: tariff.is_udp_enabled,
        proxy_type: tariff.proxy_type || 'http',
        description: tariff.description,
        kraken_max_connections: tariff.kraken_max_connections,
        slimiter: tariff.slimiter || 0,
        is_limiter: tariff.is_limiter || false,
        rlimiter: tariff.rlimiter || 0,
        maxconn: tariff.maxconn || 0,
        is_flexible: true,
        modem_settings_enabled: tariff.modem_settings_enabled || false
      }));

      // Если гибкие тарифы пусты, пробуем загрузить обычные тарифы как fallback
      if (formattedTariffs.length === 0) {
        logger.warn('fetchReferenceData: гибкие тарифы вернули пустой список, пробуем fallback на обычные тарифы');
        try {
          const tariffPlansRes = await apiService.getTariffPlans();
          // ИСПРАВЛЕНО: Проверяем, что ответ - массив
          const regularTariffs = Array.isArray(tariffPlansRes.data) ? tariffPlansRes.data : (Array.isArray(tariffPlansRes) ? tariffPlansRes : []);
          logger.info(`fetchReferenceData: получено ${regularTariffs.length} обычных тарифов`);
          formattedTariffs = regularTariffs.map(tariff => ({
            id: tariff.id,
            name: tariff.name,
            kraken_bitrate_in: tariff.kraken_bitrate_in || 0,
            kraken_bitrate_out: tariff.kraken_bitrate_out || 0,
            connection_limit: tariff.connection_limit || 0,
            is_udp_enabled: tariff.is_udp_enabled || false,
            proxy_type: tariff.proxy_type || 'http',
            description: tariff.description || '',
            kraken_max_connections: tariff.kraken_max_connections || 250,
            slimiter: tariff.slimiter || 0,
            is_limiter: tariff.is_limiter || false,
            rlimiter: tariff.rlimiter || 0,
            maxconn: tariff.maxconn || 0,
            is_flexible: false,
            modem_settings_enabled: false  // Обычные тарифы не поддерживают настройки модема
          }));
          logger.info(`fetchReferenceData: загружено ${formattedTariffs.length} обычных тарифов как fallback`);
        } catch (tariffFallbackError) {
          logger.error('fetchReferenceData: ошибка загрузки fallback тарифов', tariffFallbackError);
        }
      }

      // Обновляем тарифы только если есть данные, иначе сохраняем предыдущие значения
      if (formattedTariffs.length > 0) {
        setTariffPlans(formattedTariffs);
        tariffLoadWarningShownRef.current = false;
        logger.info(`fetchReferenceData: установлено ${formattedTariffs.length} тарифных планов`);
      } else {
        // Не затираем уже загруженный список, чтобы выпадающий список не пропадал
        const currentTariffCount = tariffPlans.length;
        setTariffPlans(prev => {
          const safePrev = Array.isArray(prev) ? prev : [];
          if (safePrev.length === 0) {
            logger.warn('fetchReferenceData: не удалось загрузить тарифные планы, список пуст');
            if (retryCount >= maxRetries && !tariffLoadWarningShownRef.current) {
              tariffLoadWarningShownRef.current = true;
              message.warning('Не удалось загрузить тарифные планы. Нажмите кнопку "Обновить" или перезагрузите страницу.');
            }
          } else {
            logger.info(`fetchReferenceData: сохраняем предыдущий список из ${safePrev.length} тарифов`);
          }
          return safePrev;
        });

        // ИСПРАВЛЕНО: Повторная попытка только если список действительно пуст и не превышен лимит
        if (currentTariffCount === 0 && retryCount < maxRetries) {
          logger.warn(`fetchReferenceData: тарифы не загружены, повторная попытка ${retryCount + 1}/${maxRetries}`);
          // Небольшая задержка перед повторной попыткой
          await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
          return fetchReferenceData(true, retryCount + 1);
        }
      }
      setAvailableCities(availableCitiesData.data?.cities || []);
      const initialOperators = availableOperatorsData.data?.operators || [];
      setAvailableOperators(initialOperators);
      setAllAvailableOperators(initialOperators);

      // Kraken connections и servers загружаются по требованию (при создании/редактировании прокси)
      // чтобы не обращаться к Kraken API при загрузке страницы

      // Загружаем уникальные значения фильтров из всех прокси
      try {
        logger.debug('fetchReferenceData: полный ответ filterValuesRes', {
          status: filterValuesData?.status,
          data: filterValuesData?.data,
          hasData: !!filterValuesData?.data,
          hasDataData: !!filterValuesData?.data?.data,
          hasSuccess: !!filterValuesData?.data?.success
        });

        // Пробуем разные варианты структуры ответа
        let filterData = null;
        if (filterValuesData?.data?.data) {
          // Формат: { success: true, data: {...} }
          filterData = filterValuesData.data.data;
        } else if (filterValuesData?.data && !filterValuesData.data.success) {
          // Формат: прямо {...}
          filterData = filterValuesData.data;
        }

        logger.debug('fetchReferenceData: извлеченные filterData', filterData);

        if (filterData && typeof filterData === 'object' && !Array.isArray(filterData)) {
          const newFilterValues = {
            client_login: Array.isArray(filterData.client_login) ? filterData.client_login : [],
            tariff_plan: Array.isArray(filterData.tariff_plan) ? filterData.tariff_plan : [],
            availability_status: Array.isArray(filterData.availability_status) ? filterData.availability_status : [],
            auto_replace_enabled: Array.isArray(filterData.auto_replace_enabled) ? filterData.auto_replace_enabled : [],
            server_name: Array.isArray(filterData.server_name) ? filterData.server_name : [],
            selected_city: Array.isArray(filterData.selected_city) ? filterData.selected_city : [],
            selected_city_counts: Array.isArray(filterData.selected_city_counts) ? filterData.selected_city_counts : [],
            selected_operator: Array.isArray(filterData.selected_operator) ? filterData.selected_operator : [],
            modem_reconnect_type: Array.isArray(filterData.modem_reconnect_type) ? filterData.modem_reconnect_type : [],
            port: Array.isArray(filterData.port) ? filterData.port : [],
            modem: Array.isArray(filterData.modem) ? filterData.modem : [],
            proxy_type: Array.isArray(filterData.proxy_type) ? filterData.proxy_type : [],
            is_active: Array.isArray(filterData.is_active) ? filterData.is_active : [],
            is_keepalive: Array.isArray(filterData.is_keepalive) ? filterData.is_keepalive : [],
            is_compression: Array.isArray(filterData.is_compression) ? filterData.is_compression : [],
            tcp_connections: Array.isArray(filterData.tcp_connections) ? filterData.tcp_connections : [],
            modem_osfp: Array.isArray(filterData.modem_osfp) ? filterData.modem_osfp : [],
          };

          setFilterValues(newFilterValues);
          logger.info('fetchReferenceData: уникальные значения фильтров загружены и установлены', {
            client_login: newFilterValues.client_login.length,
            tariff_plan: newFilterValues.tariff_plan.length,
            server_name: newFilterValues.server_name.length,
            selected_city: newFilterValues.selected_city.length,
            selected_city_counts: newFilterValues.selected_city_counts.length,
            selected_operator: newFilterValues.selected_operator.length,
            port: newFilterValues.port.length,
            modem: newFilterValues.modem.length,
            tcp_connections: newFilterValues.tcp_connections.length,
            modem_osfp: newFilterValues.modem_osfp.length
          });
        } else {
          logger.warn('fetchReferenceData: не удалось загрузить уникальные значения фильтров', {
            filterData,
            filterDataType: typeof filterData,
            isArray: Array.isArray(filterData)
          });
        }
      } catch (filterError) {
        logger.error('fetchReferenceData: ошибка обработки уникальных значений фильтров', {
          error: filterError,
          message: filterError?.message,
          stack: filterError?.stack
        });
      }

      referenceDataLoadedRef.current = true;
      logger.info('fetchReferenceData: справочные данные загружены');
    } catch (error) {
      logger.error('fetchReferenceData: ошибка загрузки справочных данных', error);
      // ИСПРАВЛЕНО: Сбрасываем флаг при ошибке, чтобы можно было повторить попытку
      referenceDataLoadedRef.current = false;

      // ИСПРАВЛЕНО: Автоматический повтор при ошибке (если еще не превышен лимит)
      if (retryCount < maxRetries) {
        logger.info(`fetchReferenceData: повторная попытка загрузки ${retryCount + 1}/${maxRetries}`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Увеличиваем задержку с каждой попыткой
        return fetchReferenceData(true, retryCount + 1);
      }

      // Не прерываем загрузку прокси при ошибке справочных данных, но показываем предупреждение
      if (tariffPlans.length === 0 && !tariffLoadWarningShownRef.current) {
        tariffLoadWarningShownRef.current = true;
        message.warning('Не удалось загрузить тарифные планы. Попробуйте обновить страницу или нажмите кнопку "Обновить" в форме.');
      }
    }
  }, [tariffPlans]);

  const handleTariffSelectionChange = (tariffName) => {
    if (!tariffName) {
      setSelectedTariffForCreate(null);
      setOsfpList([]);
      return;
    }

    const selectedTariff = tariffPlans.find(tariff => tariff.name === tariffName);
    setSelectedTariffForCreate(selectedTariff || null);

    if (!selectedTariff || !selectedTariff.modem_settings_enabled) {
      return;
    }

    if (krakenConnections.length === 0) {
      return;
    }

    const connectionId = krakenConnections[0]?.id;
    if (!connectionId) {
      return;
    }

    apiService.getKrakenOsfpList(connectionId)
      .then(res => {
        const list = res.data?.osfp_list || res.data?.data || res.data || [];
        setOsfpList(Array.isArray(list) ? list : []);
      })
      .catch(err => {
        logger.error('Ошибка загрузки списка отпечатков', err);
        setOsfpList([]);
      });
  };

  useEffect(() => {
    if (!modalVisible) {
      return;
    }

    const tariffName = form.getFieldValue('tariff_plan_name');
    if (!tariffName) {
      return;
    }

    const selectedTariff = tariffPlans.find(tariff => tariff.name === tariffName);
    if (!selectedTariff) {
      return;
    }

    setSelectedTariffForCreate(selectedTariff);
  }, [form, tariffPlans, modalVisible]);

  // Функция для загрузки уникальных значений фильтров с учетом текущих фильтров
  const fetchFilterValues = useCallback(async (filters) => {
    try {
      const apiFilters = convertFiltersToApiParams(filters);
      // logger.debug('Запрос зависимых фильтров:', apiFilters);

      const response = await apiService.getProxyFilterValues(apiFilters);
      if (response.data && response.data.success) {
        const newValues = response.data.data;
        setFilterValues(prev => ({
          ...prev,
          ...newValues
        }));
      }
    } catch (error) {
      logger.error('Ошибка загрузки значений фильтров:', error);
    }
  }, [convertFiltersToApiParams]);

  const fetchProxiesOnly = useCallback(async (
    filtersToApply = null,
    showLoading = true,
    paginationOverride = null,
    forceRefresh = false,
    urlProxyIdOverride = undefined
  ) => {
    const isBackground = !showLoading && !paginationOverride;
    const lockRef = isBackground ? backgroundFetchingRef : manualFetchingRef;
    const parseCheckedAtMs = (value) => {
      if (value == null || value === '') return 0;
      const ms = Date.parse(String(value));
      return Number.isFinite(ms) ? ms : 0;
    };
    const mergeAvailabilityPreferFresher = (incomingList, previousList) => {
      if (!Array.isArray(incomingList) || incomingList.length === 0) return incomingList;
      if (!Array.isArray(previousList) || previousList.length === 0) return incomingList;
      const prevById = new Map(
        previousList
          .map((p) => {
            const id = p?.id ?? p?.proxy_id;
            return id == null ? null : [String(id), p];
          })
          .filter(Boolean)
      );
      return incomingList.map((proxy) => {
        const id = proxy?.id ?? proxy?.proxy_id;
        if (id == null) return proxy;
        const prev = prevById.get(String(id));
        if (!prev?.availability_status) return proxy;
        const prevTs = parseCheckedAtMs(prev.last_checked);
        const nextTs = parseCheckedAtMs(proxy.last_checked);
        // Локальный результат ручной проверки новее (или сервер ещё без last_checked) — не откатываем бейдж.
        if (prevTs > nextTs || (prevTs > 0 && nextTs === 0)) {
          return {
            ...proxy,
            availability_status: prev.availability_status,
            last_checked: prev.last_checked,
            quick_check_ip: prev.quick_check_ip ?? proxy.quick_check_ip ?? null,
          };
        }
        return proxy;
      });
    };

    // forceRefresh (после чека) не должен тихо скипаться из‑за фонового поллинга —
    // иначе in-flight ответ со старым «Недоступен» затирает оптимистичный «Доступен».
    if (!forceRefresh) {
      if (isBackground && manualFetchingRef.current) {
        return;
      }
      if (lockRef.current) {
        return;
      }
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const fetchGeneration = ++proxiesFetchGenerationRef.current;
    if (isBackground) {
      backgroundFetchGenRef.current = fetchGeneration;
    } else {
      manualFetchGenRef.current = fetchGeneration;
    }

    try {
      lockRef.current = true;
      if (showLoading) {
        setLoading(true);
        setLoadingProgress(0);
        setLoadingMessage('Загрузка прокси...');
      }

      const currentPagination = paginationOverride || paginationRef.current;
      const filtersToUse = filtersToApply !== null ? filtersToApply : filteredInfoRef.current;
      const apiFilters = convertFiltersToApiParams(filtersToUse);
      const proxyIdFromUrl = urlProxyIdOverride !== undefined
        ? urlProxyIdOverride
        : searchParams.get('proxy_id');
      if (proxyIdFromUrl && String(proxyIdFromUrl).trim() !== '') {
        const parsedProxyId = Number(proxyIdFromUrl);
        if (Number.isFinite(parsedProxyId) && parsedProxyId > 0) {
          apiFilters.proxyId = parsedProxyId;
        }
      }

      const pageSize = currentPagination.pageSize;
      const currentPage = currentPagination.current;
      const limit = pageSize;
      const offset = (currentPage - 1) * pageSize;

      // #region agent log
      sendAgentDebugLog(
        'H1',
        'Proxies.js:fetchProxiesOnly:beforeRequest',
        'Запрос списка прокси перед обновлением таблицы',
        { currentPage, pageSize, limit, offset, forceRefresh, showLoading, filters: apiFilters }
      );
      // #endregion

      const proxyRes = await apiService.getProxiesPaginated({
        limit,
        offset,
        forceRefresh,
        ...apiFilters
      });

      if (controller.signal.aborted) return;

      let mainProxies = [];
      let totalCount = 0;

      if (proxyRes?.data && typeof proxyRes.data === 'object' && !Array.isArray(proxyRes.data) && proxyRes.data.data) {
        mainProxies = normalizeProxyList(proxyRes.data.data, 'основной список прокси');
        totalCount = proxyRes.data.total || mainProxies.length;
      } else {
        mainProxies = normalizeProxyList(proxyRes?.data, 'основной список прокси');
        const headerTotal = proxyRes?.headers?.['x-total-count'];
        totalCount = headerTotal ? parseInt(headerTotal, 10) : mainProxies.length;
      }

      // #region agent log
      sendAgentDebugLog(
        'H3',
        'Proxies.js:fetchProxiesOnly:afterNormalize',
        'Ответ списка прокси после normalize',
        {
          totalCount,
          pageCount: mainProxies.length,
          firstProxyIds: mainProxies.slice(0, 3).map((proxy) => proxy?.id ?? proxy?.proxy_id ?? null),
          firstGuardFlags: mainProxies.slice(0, 3).map((proxy) => Boolean(proxy?.auto_replace_enabled)),
          withModemName: mainProxies.filter((proxy) => Boolean(proxy?.modem_name)).length,
          withReconnectType: mainProxies.filter((proxy) => Boolean(proxy?.modem_reconnect_type)).length,
          withReconnectInterval: mainProxies.filter((proxy) => proxy?.modem_reconnect_interval !== null && proxy?.modem_reconnect_interval !== undefined).length,
          withOsfpId: mainProxies.filter((proxy) => proxy?.modem_osfp_id !== null && proxy?.modem_osfp_id !== undefined && String(proxy?.modem_osfp_id).trim() !== '').length,
          withGuardEnabled: mainProxies.filter((proxy) => proxy?.auto_replace_enabled === true || proxy?.auto_replace_enabled === 1).length,
          withKeepAliveEnabled: mainProxies.filter((proxy) => proxy?.is_keepalive === true || proxy?.is_keepalive === 1).length,
          withCompressionEnabled: mainProxies.filter((proxy) => proxy?.is_compression === true || proxy?.is_compression === 1).length,
          withModemSettingsWhenGuardDisabled: mainProxies.filter((proxy) => {
            const guardEnabled = proxy?.auto_replace_enabled === true || proxy?.auto_replace_enabled === 1;
            const hasModemSettings = Boolean(proxy?.modem_reconnect_type)
              || (proxy?.modem_reconnect_interval !== null && proxy?.modem_reconnect_interval !== undefined)
              || (proxy?.modem_osfp_id !== null && proxy?.modem_osfp_id !== undefined && String(proxy?.modem_osfp_id).trim() !== '');
            return !guardEnabled && hasModemSettings;
          }).length
        }
      );
      // #endregion

      const combinedProxies = mainProxies.map(proxy => ({
        ...proxy,
        checks: null,
        overall_status: null,
        // Внешний IP из быстрой проверки (см. proxy_checker_api): не затирать — иначе в UI только server_ip модема
        quick_check_ip: proxy.quick_check_ip ?? null,
        availability_status: proxy.availability_status ?? null,
        last_checked: proxy.last_checked ?? null
      }));

      if (controller.signal.aborted) return;
      if (fetchGeneration !== proxiesFetchGenerationRef.current) return;

      const safeCombinedProxies = Array.isArray(combinedProxies) ? combinedProxies : [];
      setProxies((prev) => mergeAvailabilityPreferFresher(safeCombinedProxies, prev));

      setPagination(prev => {
        const updated = { ...prev, total: totalCount };
        if (paginationOverride) {
          updated.current = paginationOverride.current;
          updated.pageSize = paginationOverride.pageSize;
        }
        return updated;
      });

      if (showLoading) {
        setLoadingProgress(90);
      }

      return {
        totalCount,
        pageCount: safeCombinedProxies.length,
        ids: safeCombinedProxies.slice(0, 5).map((proxy) => proxy?.id ?? proxy?.proxy_id ?? null)
      };

    } catch (error) {
      if (error?.name === 'AbortError' || error?.name === 'CanceledError' || controller.signal.aborted) return;
      // #region agent log
      sendAgentDebugLog(
        'H9',
        'Proxies.js:fetchProxiesOnly:error',
        'Ошибка загрузки прокси в fetchProxiesOnly',
        {
          showLoading,
          forceRefresh,
          isBackground,
          errorName: error?.name || 'unknown',
          errorMessage: error?.message || 'unknown',
          errorStatus: error?.response?.status ?? null
        }
      );
      // #endregion
      logger.error('fetchProxiesOnly: ошибка загрузки списка прокси', error);
      if (showLoading) {
        // Для ручной полной перезагрузки показываем пустое состояние,
        // но не очищаем данные на фоне, чтобы не было визуальных "пропаданий".
        setProxies([]);
      }
      if (showLoading) {
        message.error('Ошибка загрузки прокси');
      }
      return null;
    } finally {
      // Снимаем только свой лок (по поколению владельца), чтобы abort не ломал параллельный/новый fetch.
      if (isBackground) {
        if (backgroundFetchGenRef.current === fetchGeneration) {
          backgroundFetchingRef.current = false;
        }
      } else if (manualFetchGenRef.current === fetchGeneration) {
        manualFetchingRef.current = false;
      }
      if (fetchGeneration === proxiesFetchGenerationRef.current && showLoading) {
        setLoadingProgress(100);
        setLoadingMessage('Завершено');
        setTimeout(() => {
          setLoading(false);
          setLoadingProgress(0);
          setLoadingMessage('');
        }, 200);
      }
    }
  }, [normalizeProxyList, convertFiltersToApiParams, searchParams]);

  // Полная загрузка данных (для первой загрузки и принудительного обновления)
  const fetchData = useCallback(async (forceRefresh = false, filtersToApply = null) => {
    try {
      const referenceDataPromise = fetchReferenceData(forceRefresh).catch(error => {
        logger.warn('fetchData: справочные данные не загружены, продолжаем с загрузкой прокси', error);
      });
      void fetchClientsReferenceData(forceRefresh);

      const exportTemplatesPromise = apiService.listExportTemplates()
        .then((templatesRes) => {
          setExportTemplates(templatesRes.data?.templates || []);
        })
        .catch(() => {
          // ignore
        });

      const proxiesPromise = fetchProxiesOnly(filtersToApply, true, null, forceRefresh);

      await Promise.all([referenceDataPromise, exportTemplatesPromise, proxiesPromise]);
    } catch (error) {
      logger.error('fetchData: критическая ошибка', error);
      message.error(handleApiError(error));
    }
  }, [fetchClientsReferenceData, fetchReferenceData, fetchProxiesOnly]);

  // Синхронизация ?availability= с фильтром таблицы (навигация с дашборда / смена query без remount).
  useEffect(() => {
    if (!mountedRef.current) {
      return;
    }
    if (skipAvailabilityUrlSyncOnceRef.current) {
      skipAvailabilityUrlSyncOnceRef.current = false;
      return;
    }

    const parsedAvailability = parseAvailabilityFilterFromSearchParams(searchParams);
    const parsedClientLogin = parseClientLoginFilterFromSearchParams(searchParams);
    const parsedModem = parseModemFilterFromSearchParams(searchParams);
    const parsed = {
      ...(parsedAvailability || {}),
      ...(parsedClientLogin || {}),
      ...(parsedModem || {}),
    };
    if (Object.keys(parsed).length === 0) {
      return;
    }

    const next = { ...filteredInfoRef.current, ...parsed };
    filteredInfoRef.current = next;
    setFilteredInfo(next);
    paginationUpdateRef.current = true;
    const newPag = { ...paginationRef.current, current: 1 };
    paginationRef.current = newPag;
    setPagination(newPag);
    void fetchFilterValues(next);
    void fetchProxiesOnly(next, true, newPag);
  }, [searchParams, fetchFilterValues, fetchProxiesOnly]);

  // Поддерживаем колонку "Переподключение/Ротация" в актуальном состоянии.
  // Поллинг не должен быть агрессивным: иначе создается постоянная нагрузка на БД и UI.
  useEffect(() => {
    const reconnectColumnRefreshMs = 30000;

    const intervalId = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (manualFetchingRef.current || backgroundFetchingRef.current) return;
      fetchProxiesOnly(null, false, null, true).catch(() => {});
    }, reconnectColumnRefreshMs);

    return () => clearInterval(intervalId);
  }, [fetchProxiesOnly]);

  // УБРАНО: Функция загрузки трафика - не используется, чтобы не загружать систему
  // Трафик не отображается в UI и не используется в бизнес-логике
  // const fetchTrafficData = async (proxies) => { ... };

  // ИСПРАВЛЕНО: Синхронизируем пагинацию с URL параметрами (для случаев ручного изменения URL)
  // При первой загрузке пагинация уже инициализирована из URL, этот useEffect нужен только
  // для случаев, когда пользователь изменит URL вручную (редкий случай)
  useEffect(() => {
    if (!mountedRef.current) {
      // При первой загрузке пагинация уже инициализирована из URL, пропускаем
      return;
    }

    // ИСПРАВЛЕНО: Пропускаем синхронизацию, если мы сами обновляем пагинацию через onChange
    if (paginationUpdateRef.current) {
      paginationUpdateRef.current = false;
      return;
    }

    const urlPage = parseInt(searchParams.get('page') || '1', 10);

    // Обновляем пагинацию, если она изменилась в URL (например, пользователь изменил URL вручную)
    // Но только если это действительно изменение, а не синхронизация после нашего обновления
    if (urlPage !== pagination.current) {
      logger.debug('useEffect: обновление пагинации из URL', { urlPage, current: pagination.current });
      setPagination(prev => ({
        ...prev,
        current: urlPage
      }));
    }
  }, [isMobile, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mountedRef.current) {
      logger.debug('useEffect: компонент уже инициализирован, повторная инициализация пропущена');
      return;
    }

    logger.info('useEffect: инициализация страницы прокси');
    mountedRef.current = true;

    const urlAvailabilityFilters = parseAvailabilityFilterFromSearchParams(searchParams);
    const urlClientLoginFilters = parseClientLoginFilterFromSearchParams(searchParams);
    const urlModemFilters = parseModemFilterFromSearchParams(searchParams);
    const initialUrlFilters = {
      ...(urlAvailabilityFilters || {}),
      ...(urlClientLoginFilters || {}),
      ...(urlModemFilters || {}),
    };
    if (Object.keys(initialUrlFilters).length > 0) {
      filteredInfoRef.current = initialUrlFilters;
      setFilteredInfo(initialUrlFilters);
      skipAvailabilityUrlSyncOnceRef.current = true;
      void fetchFilterValues(initialUrlFilters);
    }

    const initialSearchParams = new URLSearchParams(searchParams);
    initialSearchParams.set('page', String(initialPage));
    initialSearchParams.set('pageSize', String(initialPageSize));
    setSearchParams(initialSearchParams, { replace: true });

    // Проверяем и исправляем токен при загрузке
    initTokenCheck();
    // При первой загрузке загружаем все данные с пагинацией из URL
    fetchData(true, Object.keys(initialUrlFilters).length > 0 ? initialUrlFilters : null);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Отдельный useEffect для загрузки статистики
  useEffect(() => {
    if (statusLoadedRef.current) {
      logger.debug('loadStatusData: статистика уже загружена, пропускаем повторный вызов');
      return;
    }

    statusLoadedRef.current = true; // Устанавливаем флаг сразу

    const statusFetchingRef = { current: false };

    const loadStatusData = async () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      if (statusFetchingRef.current) {
        return;
      }
      statusFetchingRef.current = true;
      try {
        setStatusLoading(true);
        logger.info('loadStatusData: начинаем загрузку summary-статистики прокси');
        let nextStatusData = null;
        try {
          const summaryRes = await apiService.getStatsSummary();
          nextStatusData = summaryRes?.data;
        } catch (summaryError) {
          logger.warn('loadStatusData: summary недоступен, fallback на /api/stats', summaryError);
          const statusRes = await apiService.getStats();
          nextStatusData = statusRes?.data;
        }
        // last-good: нули/пустой ответ не затирают предыдущие чипы
        setStatusData((prev) => {
          if (!hasCalculatedStats(nextStatusData)) {
            return prev;
          }
          return {
            ...(prev || {}),
            ...nextStatusData,
          };
        });
        logger.info('loadStatusData: статистика загружена', nextStatusData);
      } catch (error) {
        logger.error('loadStatusData: ошибка загрузки статистики прокси', error);
        statusLoadedRef.current = false; // Сбрасываем флаг при ошибке
      } finally {
        statusFetchingRef.current = false;
        setStatusLoading(false);
      }
    };

    loadStatusData();

    // Автоматическое обновление статистики каждые 30 секунд
    const interval = setInterval(() => {
      logger.debug('Автоматическое обновление статистики...');
      loadStatusData();
    }, 120000);

    return () => clearInterval(interval);
  }, [hasCalculatedStats]);


  // ИСПРАВЛЕНО: Перезагружаем тарифы при открытии модальных окон (создание, редактирование, массовое редактирование)
  useEffect(() => {
    if (modalVisible || editModalVisible || bulkEditModalVisible) {
      // Проверяем, что тарифы загружены, если нет - загружаем
      if (tariffPlans.length === 0 || !referenceDataLoadedRef.current) {
        logger.info('useEffect: модальное окно открыто, проверяем загрузку тарифов', {
          modalVisible,
          editModalVisible,
          bulkEditModalVisible,
          tariffPlansCount: tariffPlans.length,
          referenceDataLoaded: referenceDataLoadedRef.current
        });
        fetchReferenceData(true).catch(error => {
          logger.error('useEffect: ошибка перезагрузки тарифов при открытии модального окна', error);
        });
      } else {
        logger.debug('useEffect: тарифы уже загружены, пропускаем перезагрузку', {
          tariffPlansCount: tariffPlans.length
        });
      }
    }
  }, [modalVisible, editModalVisible, bulkEditModalVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  // Устанавливаем начальные значения формы массового редактирования при открытии модального окна
  useEffect(() => {
    if (bulkEditModalVisible && selectedRowKeys.length > 0) {
      const selectedProxies = safeProxies.filter((proxy) => {
        const proxyKey = getProxyKey(proxy);
        return proxyKey !== null && selectedRowKeys.includes(proxyKey);
      });
      if (selectedProxies.length > 0) {
        const uniqueTariffs = [...new Set(selectedProxies.map(p => p.tariff_plan).filter(Boolean))];
        const commonTariff = uniqueTariffs.length === 1 ? uniqueTariffs[0] : undefined;
        let tariffValue = undefined;
        if (commonTariff) {
          const tariffExists = tariffPlans.some(plan => plan.name === commonTariff);
          if (tariffExists) tariffValue = commonTariff;
        }
        setBulkData((prev) => {
          // Не затираем уже выставленные пользователем поля массового редактирования.
          if ((prev?.tariff_plan_name ?? undefined) === tariffValue) {
            return prev;
          }
          return { ...prev, tariff_plan_name: tariffValue };
        });
      }
      // Загружаем Kraken данные (модемы, подключения) при открытии bulk edit если ещё не загружены
      if (krakenConnections.length === 0 || availableServers.length === 0) {
        Promise.all([
          apiService.getActiveKrakenConnections(),
          apiService.getKrakenServers()
        ]).then(([krakenConnectionsRes, krakenServersRes]) => {
          setKrakenConnections(krakenConnectionsRes.data?.connections || []);
          setAvailableServers(krakenServersRes.data?.servers || []);
        }).catch(error => {
          logger.error('Ошибка загрузки Kraken данных для массового редактирования:', error);
        });
      }
    } else if (bulkEditModalVisible && selectedRowKeys.length === 0) {
      setBulkData({});
    }
  }, [bulkEditModalVisible, selectedRowKeys, safeProxies, tariffPlans, getProxyKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const bulkEditSelectionKey = useMemo(() => {
    if (!bulkEditModalVisible || selectedRowKeys.length === 0) return '';
    return [...selectedRowKeys].map(String).sort().join(',');
  }, [bulkEditModalVisible, selectedRowKeys]);

  // Kraken is_allow / is_ports в списке прокси не приходят — подтягиваем live, как в ProxyEditPanel
  useEffect(() => {
    if (!bulkEditModalVisible) {
      bulkKrakenBaselineRef.current = {};
      bulkKrakenUserEditedRef.current = false;
      return undefined;
    }
    if (!bulkEditSelectionKey) return undefined;

    const selectedProxies = safeProxies.filter((proxy) => {
      const proxyKey = getProxyKey(proxy);
      return proxyKey !== null && selectedRowKeys.includes(proxyKey);
    });
    if (selectedProxies.length === 0) return undefined;

    const gen = bulkKrakenFetchGenRef.current + 1;
    bulkKrakenFetchGenRef.current = gen;
    bulkKrakenUserEditedRef.current = false;

    const run = async () => {
      const characteristicsList = await Promise.all(
        selectedProxies.map(async (proxy) => {
          const dbId = proxy.proxy_id ?? proxy.id;
          if (!dbId || !proxy.kraken_proxy_id) return null;
          try {
            const res = await apiService.getProxyCharacteristics(dbId);
            return res?.data && typeof res.data === 'object' ? res.data : null;
          } catch {
            return null;
          }
        }),
      );

      if (gen !== bulkKrakenFetchGenRef.current) return;

      const validCharacteristics = characteristicsList.filter(Boolean);
      const allowValues = validCharacteristics.map((item) => item.is_allow);
      const portsValues = validCharacteristics.map((item) => item.is_ports);
      const allowLists = validCharacteristics.map((item) => item.allow);
      const targetportLists = validCharacteristics.map((item) => item.targetport);

      const baseline = {
        kraken_is_allow: resolveCommonKrakenBool(allowValues),
        kraken_is_ports: resolveCommonKrakenBool(portsValues),
        kraken_allow: resolveCommonKrakenIntListInput(allowLists),
        kraken_targetport: resolveCommonKrakenIntListInput(targetportLists),
      };
      bulkKrakenBaselineRef.current = baseline;

      if (bulkKrakenUserEditedRef.current) return;

      setBulkData((prev) => {
        const next = { ...prev };
        if (baseline.kraken_is_allow !== undefined) {
          next.kraken_is_allow = baseline.kraken_is_allow;
        } else {
          delete next.kraken_is_allow;
        }
        if (baseline.kraken_is_ports !== undefined) {
          next.kraken_is_ports = baseline.kraken_is_ports;
        } else {
          delete next.kraken_is_ports;
        }
        if (baseline.kraken_allow !== undefined) {
          next.kraken_allow = baseline.kraken_allow;
        } else {
          delete next.kraken_allow;
        }
        if (baseline.kraken_targetport !== undefined) {
          next.kraken_targetport = baseline.kraken_targetport;
        } else {
          delete next.kraken_targetport;
        }
        return next;
      });
    };

    run();
    return undefined;
  }, [bulkEditModalVisible, bulkEditSelectionKey, safeProxies, selectedRowKeys, getProxyKey]);

  // Есть ли среди выбранных прокси хотя бы один с модемом (для отображения секции настроек модема в bulk edit)
  // Если выбран тариф без modem_settings_enabled — скрываем блок
  const hasBulkEditModemProxies = useMemo(() => {
    if (!bulkEditModalVisible || selectedRowKeys.length === 0) return false;
    if (bulkData.tariff_plan_name) {
      const selectedTariff = tariffPlans.find(t => t.name === bulkData.tariff_plan_name);
      if (selectedTariff && !selectedTariff.modem_settings_enabled) return false;
    }
    const selectedProxies = safeProxies.filter((p) => {
      const proxyKey = getProxyKey(p);
      return proxyKey !== null && selectedRowKeys.includes(proxyKey);
    });
    return selectedProxies.some(p => p.server_id || p.modem_name);
  }, [bulkEditModalVisible, selectedRowKeys, safeProxies, bulkData.tariff_plan_name, tariffPlans, getProxyKey]);

  const handleRefreshOsfpList = useCallback(async () => {
    if (osfpRefreshLoading) return;

    setOsfpRefreshLoading(true);
    try {
      let connections = Array.isArray(krakenConnections) ? krakenConnections : [];
      if (connections.length === 0) {
        const connectionRes = await apiService.getActiveKrakenConnections();
        connections = connectionRes.data?.connections || [];
        setKrakenConnections(connections);
      }

      let connectionId = null;
      if (bulkEditModalVisible && selectedRowKeys.length > 0 && safeProxies.length > 0) {
        const selectedProxies = safeProxies.filter((p) => {
          const proxyKey = getProxyKey(p);
          return proxyKey !== null && selectedRowKeys.includes(proxyKey);
        });
        const withModem = selectedProxies.find(p => p.server_id || p.modem_name);
        connectionId = withModem && (withModem.kraken_connection_id ?? withModem.connection_id);
      }

      if (!connectionId && editingProxy) {
        connectionId = editingProxy.kraken_connection_id ?? editingProxy.connection_id;
      }

      if (!connectionId && connections.length > 0) {
        connectionId = connections[0]?.id;
      }

      if (!connectionId) {
        message.warning('Не удалось определить подключение Kraken для загрузки TCP отпечатков.');
        return;
      }

      const res = await apiService.getKrakenOsfpList(connectionId);
      const list = res.data?.osfp_list || res.data?.data || res.data || [];
      const normalizedList = Array.isArray(list) ? list : [];
      setOsfpList(normalizedList);

      if (normalizedList.length === 0) {
        message.warning('Список TCP отпечатков пуст для выбранного подключения.');
        return;
      }

      message.success(`TCP отпечатки обновлены: ${normalizedList.length}`);
    } catch (err) {
      logger.error('Ошибка принудительной загрузки списка TCP отпечатков', err);
      message.error('Не удалось обновить TCP отпечатки.');
    } finally {
      setOsfpRefreshLoading(false);
    }
  }, [osfpRefreshLoading, krakenConnections, bulkEditModalVisible, selectedRowKeys, safeProxies, getProxyKey, editingProxy]);

  // Загружаем список TCP отпечатков при появлении модалки массового или одиночного редактирования
  useEffect(() => {
    const needOsfpForBulk = bulkEditModalVisible && selectedRowKeys.length > 0;
    const editTariff = editingProxy ? tariffPlans.find(t => t.name === editingProxy.tariff_plan) : null;
    const needOsfpForEdit = editModalVisible && editingProxy && (editTariff?.modem_settings_enabled || editingProxy.server_id || editingProxy.modem_name);
    const needOsfpList = needOsfpForBulk || needOsfpForEdit;
    if (!needOsfpList) return;

    const resolveConnectionIdAndFetchOsfp = (connections) => {
      let connectionId = null;
      if (bulkEditModalVisible && selectedRowKeys.length > 0 && safeProxies.length > 0) {
        const selectedProxies = safeProxies.filter((p) => {
          const proxyKey = getProxyKey(p);
          return proxyKey !== null && selectedRowKeys.includes(proxyKey);
        });
        const withModem = selectedProxies.find(p => p.server_id || p.modem_name);
        connectionId = withModem && (withModem.kraken_connection_id ?? withModem.connection_id);
      }
      if (!connectionId && editingProxy) {
        connectionId = editingProxy.kraken_connection_id ?? editingProxy.connection_id;
      }
      if (!connectionId && Array.isArray(connections) && connections.length > 0) {
        connectionId = connections[0]?.id;
      }
      if (!connectionId) return;
      apiService.getKrakenOsfpList(connectionId)
        .then(res => {
          const list = res.data?.osfp_list || res.data?.data || res.data || [];
          setOsfpList(Array.isArray(list) ? list : []);
        })
        .catch(err => {
          logger.error('Ошибка загрузки списка TCP отпечатков', err);
          setOsfpList([]);
        });
    };

    if (krakenConnections?.length > 0) {
      resolveConnectionIdAndFetchOsfp(krakenConnections);
      return;
    }
    apiService.getActiveKrakenConnections()
      .then(res => {
        const connections = res.data?.connections || [];
        setKrakenConnections(connections);
        resolveConnectionIdAndFetchOsfp(connections);
      })
      .catch(err => {
        logger.error('Ошибка загрузки подключений Kraken для списка отпечатков', err);
        setOsfpList([]);
      });
  }, [bulkEditModalVisible, hasBulkEditModemProxies, editModalVisible, editingProxy, tariffPlans, krakenConnections, selectedRowKeys, safeProxies, getProxyKey]);

  // Функция для случайного распределения операторов между прокси
  const distributeOperators = (operators, quantity) => {
    if (!operators || operators.length === 0) {
      return Array(quantity).fill(null);
    }

    if (operators.length === 1) {
      return Array(quantity).fill(operators[0]);
    }

    // Случайное распределение операторов
    const distributed = [];
    for (let i = 0; i < quantity; i++) {
      const randomIndex = Math.floor(Math.random() * operators.length);
      distributed.push(operators[randomIndex]);
    }

    return distributed;
  };

  // Обработчик изменения сервера - обновляет список операторов и фильтрует модемы (для формы создания)
  const handleServerChange = async (connectionId) => {
    try {
      if (connectionId) {
        // Загружаем операторов для выбранного сервера
        const operatorsRes = await apiService.getAvailableOperators(connectionId);
        setAvailableOperators(operatorsRes.data?.operators || []);
        logger.info(`Загружены операторы для сервера ${connectionId}: ${operatorsRes.data?.operators?.length || 0}`);

        // Фильтруем модемы по выбранному Kraken подключению
        // eslint-disable-next-line eqeqeq
        const filtered = availableServers.filter(server => String(server.kraken_connection_id) === String(connectionId));
        setFilteredServers(filtered);
        logger.info(`Отфильтровано ${filtered.length} модемов для Kraken сервера ID: ${connectionId}`);
      } else {
        // Если сервер не выбран - загружаем всех операторов
        const operatorsRes = await apiService.getAvailableOperators();
        setAvailableOperators(operatorsRes.data?.operators || []);
        logger.info('Загружены все операторы');

        // Показываем все модемы
        setFilteredServers(availableServers);
      }
      // Сбрасываем выбранных операторов и модем в форме
      form.setFieldsValue({
        mobile_operator: [],
        server_id: undefined
      });
    } catch (error) {
      logger.error('Ошибка при загрузке операторов:', error);
      message.error('Не удалось загрузить список операторов');
    }
  };

  // Обработчик изменения города в форме создания - фильтрует серверы Kraken по городу
  const handleCreateCityChange = (city) => {
    if (city) {
      // Фильтруем Kraken подключения по выбранному городу
      const filtered = krakenConnections.filter(conn => conn.city === city);
      setFilteredKrakenConnections(filtered);
      logger.info(`Отфильтровано ${filtered.length} Kraken серверов для города: ${city}`);
    } else {
      // Если город не выбран - показываем все подключения
      setFilteredKrakenConnections(krakenConnections);
    }
    // Сбрасываем выбор сервера Kraken и модема
    form.setFieldsValue({
      connection_id: undefined,
      server_id: undefined
    });
    setFilteredServers([]);
  };

  // Обработчик изменения города в форме массового редактирования
  const handleBulkEditCityChange = (city) => {
    if (city) {
      const filtered = krakenConnections.filter(conn => conn.city === city);
      setFilteredKrakenConnections(filtered);
    } else {
      setFilteredKrakenConnections(krakenConnections);
    }
    setBulkData(prev => ({ ...prev, connection_id: undefined, server_id: undefined }));
    setFilteredServers([]);
  };

  // Обработчик изменения сервера Kraken в форме массового редактирования
  const handleBulkEditConnectionChange = (connectionId) => {
    if (connectionId) {
      const filtered = availableServers.filter(server => String(server.kraken_connection_id) === String(connectionId));
      setFilteredServers(filtered);
    } else {
      setFilteredServers(availableServers);
    }
    setBulkField('server_id', undefined);
  };

  const handleBulkEditOperatorChange = (operatorValue) => {
    const activeServers = filteredServers.length > 0 ? filteredServers : availableServers;
    const currentServer = activeServers.find((server) => String(server.id) === String(bulkData.server_id));
    const currentServerMatches = currentServer
      ? operatorsMatch(currentServer.mobile_operator, operatorValue)
      : false;

    setBulkField('mobile_operator', operatorValue || undefined);
    if (bulkData.server_id && !currentServerMatches) {
      setBulkField('server_id', undefined);
    }

  };

  useEffect(() => {
    if (!bulkEditModalVisible) return undefined;

    let cancelled = false;

    const applyPlacementOperators = (list, intermediate = false) => {
      if (cancelled) return;
      const nextList = Array.isArray(list) ? list : [];
      setBulkPlacementOperators(nextList);
      if (intermediate) return;
      setBulkData((prev) => {
        const cur = prev.mobile_operator;
        if (cur && (nextList.length === 0 || !nextList.includes(cur))) {
          return { ...prev, mobile_operator: undefined };
        }
        return prev;
      });
    };

    const fallbackFull = () => (allAvailableOperators.length > 0 ? allAvailableOperators : availableOperators);

    const run = async () => {
      const connId = bulkData.connection_id;
      const city = bulkData.city;

      if (connId) {
        applyPlacementOperators([], true);
        try {
          const res = await apiService.getAvailableOperators(connId);
          if (cancelled) return;
          applyPlacementOperators(res.data?.operators || []);
        } catch (err) {
          logger.error('Ошибка загрузки операторов для массового редактирования (подключение)', err);
          applyPlacementOperators([]);
        }
        return;
      }

      if (city) {
        const conns = (krakenConnections || []).filter((c) => c.city === city);
        if (conns.length === 0) {
          applyPlacementOperators([]);
          return;
        }
        applyPlacementOperators([], true);
        try {
          const results = await Promise.all(conns.map((c) => apiService.getAvailableOperators(c.id)));
          if (cancelled) return;
          const uniq = new Set();
          results.forEach((r) => {
            (r.data?.operators || []).forEach((op) => uniq.add(op));
          });
          applyPlacementOperators([...uniq].sort((a, b) => String(a).localeCompare(String(b), 'ru')));
        } catch (err) {
          logger.error('Ошибка загрузки операторов для массового редактирования (город)', err);
          applyPlacementOperators([]);
        }
        return;
      }

      applyPlacementOperators(fallbackFull());
    };

    run();
    return () => { cancelled = true; };
  }, [
    bulkEditModalVisible,
    bulkData.connection_id,
    bulkData.city,
    krakenConnections,
    allAvailableOperators,
    availableOperators,
  ]);

  // Обработчик изменения тарифа в форме массового редактирования
  const handleBulkEditTariffChange = (tariffName) => {
    if (tariffName) {
      const selectedTariff = tariffPlans.find(tariff => tariff.name === tariffName);
      if (selectedTariff) {
        const slimiterRaw = Number(selectedTariff.slimiter) || 0;
        const slimiterMbps = convertSlimiterToMbps(slimiterRaw, true);
        setBulkField('slimiter', slimiterMbps > 0 ? slimiterMbps : undefined);
      }
    } else {
      setBulkField('slimiter', undefined);
    }
  };

  // Обработчик изменения города в форме редактирования - фильтрует серверы Kraken по городу
  const handleEditCityChange = async (city) => {
    if (city) {
      const filtered = krakenConnections.filter(conn => conn.city === city);
      setFilteredKrakenConnections(filtered);
      logger.info(`Отфильтровано ${filtered.length} Kraken серверов для города: ${city}`);
    } else {
      setFilteredKrakenConnections(krakenConnections);
    }
    setFilteredServers([]);
    setAvailableOperators([]);
  };

  const handleEditConnectionChange = async (connectionId) => {
    if (connectionId) {
      const filtered = availableServers.filter(server => String(server.kraken_connection_id) === String(connectionId));
      setFilteredServers(filtered);
      logger.info(`Отфильтровано ${filtered.length} модемов для Kraken сервера ID: ${connectionId}`);
      try {
        const operatorsRes = await apiService.getAvailableOperators(connectionId);
        const operators = operatorsRes?.data?.operators || [];
        setAvailableOperators(operators);
        logger.info(`Загружены операторы для Kraken сервера ${connectionId}: ${operators.length}`);
      } catch (error) {
        logger.error('Ошибка загрузки операторов для формы редактирования:', error);
        setAvailableOperators([]);
      }
    } else {
      setFilteredServers([]);
      setAvailableOperators([]);
    }
  };

  const handleCreateProxy = async (values) => {
    setCreatingProxy(true);
    setLoading(true);
    setLoadingProgress(0);
    setLoadingMessage('');

    // ИСПРАВЛЕНО: Используем Modal.info() как при переподключении прокси
    let creationModal = null;

    // UX: закрываем форму создания сразу — дальше пользователь видит только прогресс
    setModalVisible(false);

    // Показываем модальное окно с прогресс-баром сразу после нажатия кнопки
    creationModal = Modal.info({
      title: 'Создание прокси',
      content: (
        <div>
          <Progress percent={0} status="active" />
          <p style={{ marginTop: 16, textAlign: 'center' }}>Подготовка данных...</p>
        </div>
      ),
      okText: 'Закрыть',
      okButtonProps: { disabled: true },
      maskClosable: false,
      closable: false,
      width: 500
    });

    // Функция обновления прогресса
    const updateProgress = (percent, message, status = 'active') => {
      if (creationModal) {
        // ИСПРАВЛЕНО: Ограничиваем процент от 0 до 100
        const clampedPercent = Math.max(0, Math.min(100, percent));
        logger.debug(`[updateProgress] Обновление прогресса: ${clampedPercent}%, сообщение: ${message}, статус: ${status}`);

        // ИСПРАВЛЕНО: Используем key для принудительного обновления компонента
        creationModal.update({
          content: (
            <div key={`progress-${clampedPercent}-${Date.now()}`}>
              <Progress
                percent={clampedPercent}
                status={status}
                key={`progress-bar-${clampedPercent}`}
              />
              <p style={{ marginTop: 16, textAlign: 'center' }}>
                {message || 'Обработка...'}
              </p>
            </div>
          ),
          okButtonProps: {
            disabled: status !== 'success' && status !== 'exception'
          }
        });
      } else {
        logger.warn('[updateProgress] creationModal не существует');
      }
    };

    const extractKrakenRetryErrors = (responseData) => {
      if (!responseData || typeof responseData !== 'object') return [];
      const warnings = Array.isArray(responseData.warnings) ? responseData.warnings : [];
      return warnings.filter((warningItem) => (
        typeof warningItem === 'string' && warningItem.startsWith('[KRAKEN_RETRY_ERROR]')
      ));
    };

    const reportKrakenRetryErrors = (retryErrors, createdCount, quantity) => {
      if (!Array.isArray(retryErrors) || retryErrors.length === 0) return false;

      const uniqueErrors = [...new Set(retryErrors)];
      const firstError = uniqueErrors[0] || 'Kraken retry error';
      logger.error('Kraken retry failed after 3 attempts', {
        total: uniqueErrors.length,
        createdCount,
        quantity,
        firstError,
        errors: uniqueErrors.slice(0, 10)
      });

      message.error(`❌ Ошибка Kraken после 3 попыток: ${uniqueErrors.length}`);
      updateProgress(
        100,
        `Создано ${createdCount} из ${quantity}. Ошибки Kraken после ретраев: ${uniqueErrors.length}`,
        'exception'
      );
      return true;
    };
    try {
      let clientLogin = values.client_login;

      // Если выбран режим создания нового клиента
      if (values.client_mode === 'new') {
        updateProgress(10, 'Создание клиента...');
        // Сначала создаем клиента
        const clientData = {
          login: values.new_client_login,
          email: values.new_client_email
        };

        const { data: createdClient } = await apiService.createClient(clientData);
        message.success('Клиент успешно создан');

        const normalizedLogin = createdClient?.login || values.new_client_login;
        if (normalizedLogin !== values.new_client_login) {
          message.info(`Логин клиента был нормализован до "${normalizedLogin}" для совместимости с Kraken.`);
        }

        // Обновляем значения формы, чтобы использовать нормализованный логин
        form.setFieldsValue({
          client_login: normalizedLogin,
          new_client_login: normalizedLogin,
        });

        // Обновляем список клиентов
        const clientsRes = await apiService.getClients();
        const nextClients = Array.isArray(clientsRes?.data) ? clientsRes.data : [];
        setClients(nextClients);
        setClientOptions(nextClients.map((client) => ({
          value: client.login,
          label: client.login,
        })));
        clientsReferenceLoadedRef.current = true;

        clientLogin = normalizedLogin;
      }

      // Определяем тип тарифа и создаем прокси соответствующим способом
      const selectedTariff = tariffPlans.find(tariff => tariff.name === values.tariff_plan_name);

      if (selectedTariff && selectedTariff.is_flexible) {
        updateProgress(0, 'Подготовка данных для гибкого тарифа...');
        // Проверяем, что у тарифа есть ID
        if (!selectedTariff.id) {
          const errorText = 'Ошибка: у выбранного тарифа отсутствует ID';
          message.error(errorText);
          logger.error('Выбран тариф без идентификатора', { selectedTariff });

          // Показываем ошибку в прогрессе
          updateProgress(100, errorText, 'exception');
          if (creationModal) {
            setTimeout(() => {
              creationModal.destroy();
            }, 2000);
          }
          setLoading(false);
          setCreatingProxy(false);
          return;
        }

        // Создаем прокси из гибкого тарифа
        const quantity = values.proxy_quantity ?? 1;
        const selectedOperators = values.mobile_operator || [];

        // ИСПРАВЛЕНО: Прогресс-бар на основе времени (1 секунда = 1%, максимум 100 секунд = 100%)
        let timeProgressInterval = null;
        let smoothProgressInterval = null;
        let currentProgress = 0;
        let isCompleted = false;
        const startTime = Date.now();

        // Функция плавного перехода к 100%
        const smoothTransitionTo100 = (fromPercent) => {
          if (smoothProgressInterval) {
            clearInterval(smoothProgressInterval);
          }

          let smoothProgress = fromPercent;
          smoothProgressInterval = setInterval(() => {
            if (smoothProgress < 100) {
              smoothProgress = Math.min(100, smoothProgress + 2); // Увеличиваем на 2% каждые 50мс
              currentProgress = smoothProgress;
              updateProgress(smoothProgress, `Создано ${quantity} прокси из ${quantity}`, 'success');
            } else {
              clearInterval(smoothProgressInterval);
              smoothProgressInterval = null;
            }
          }, 50); // Обновляем каждые 50мс для плавности
        };

        try {
          updateProgress(0, 'Формирование запроса...');

          // Запускаем таймер прогресса по времени (1 секунда = 1%)
          timeProgressInterval = setInterval(() => {
            if (!isCompleted) {
              const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
              currentProgress = Math.min(99, elapsedSeconds); // Максимум 99% по времени
              updateProgress(currentProgress, `Создание ${quantity} прокси из гибкого тарифа... (${elapsedSeconds} сек)`);
            }
          }, 1000); // Обновляем каждую секунду

          // ИСПРАВЛЕНО: Логируем значения из формы перед отправкой для гибкого тарифа
          logger.info(`🔧 Frontend: Создание прокси из гибкого тарифа`);
          logger.info(`   values.is_keepalive: ${values.is_keepalive} (type: ${typeof values.is_keepalive})`);
          logger.info(`   values.is_compression: ${values.is_compression} (type: ${typeof values.is_compression})`);
          // #region agent log
          sendAgentDebugLog(
            'H8',
            'Proxies.js:handleCreateProxy:flexiblePayload',
            'Payload гибкого создания прокси перед API',
            {
              proxy_quantity: quantity,
              auto_replace_enabled: values.auto_replace_enabled ?? null,
              reconnect_link_enabled: values.reconnect_link_enabled ?? null,
              is_keepalive: values.is_keepalive ?? null,
              is_compression: values.is_compression ?? null,
              modem_reconnect_type: values.modem_reconnect_type ?? null,
              modem_reconnect_interval: values.modem_reconnect_interval ?? null,
              modem_osfp_id: values.modem_osfp_id ?? null,
              modem_is_osfp: values.modem_is_osfp ?? null
            }
          );
          // #endregion

          // ИСПРАВЛЕНО: Преобразуем массив операторов в строку или null
          const mobileOperatorValue = Array.isArray(selectedOperators) && selectedOperators.length > 0
            ? selectedOperators[0]  // Берем первый оператор, если массив не пустой
            : null;

          const flexibleProxyData = {
            client_login: clientLogin,
            flexible_tariff_id: selectedTariff.id, // ID гибкого тарифа
            proxy_quantity: quantity, // ИСПРАВЛЕНО: Используем proxy_quantity вместо quantity
            city: values.city || null,
            mobile_operator: mobileOperatorValue, // ИСПРАВЛЕНО: Передаем строку или null, а не массив
            proxy_type: values.proxy_type || "http",  // Добавляем тип прокси
            auto_replace_enabled: values.auto_replace_enabled !== undefined ? values.auto_replace_enabled : false,
            reconnect_link_enabled: values.reconnect_link_enabled !== undefined ? values.reconnect_link_enabled : false,
            // ИСПРАВЛЕНО: Добавлены is_keepalive и is_compression
            is_keepalive: values.is_keepalive !== undefined ? values.is_keepalive : false,
            is_compression: values.is_compression !== undefined ? values.is_compression : false,
            kraken_is_allow: values.kraken_is_allow !== undefined ? values.kraken_is_allow : true,
            kraken_is_ports: values.kraken_is_ports !== undefined ? values.kraken_is_ports : true,
            kraken_allow: parseKrakenIntList(values.kraken_allow),
            kraken_targetport: parseKrakenIntList(values.kraken_targetport),
            connection_id: (values.connection_id === '' || values.connection_id == null) ? null : values.connection_id,  // null = «Любое подключение», поиск по всем
            server_id: (values.server_id === '' || values.server_id == null) ? null : values.server_id,
            // Настройки модема (опционально, только если тариф имеет modem_settings_enabled)
            modem_reconnect_type: values.modem_reconnect_type || null,
            modem_reconnect_interval: values.modem_reconnect_interval || null,
            modem_osfp_id: values.modem_osfp_id || null,
            modem_is_osfp: values.modem_is_osfp !== undefined ? values.modem_is_osfp : null,
            // Тест Band: разрешаем размещение на перегруженных модемах
            allow_overloaded_modems: selectedTariff?.name === LOCATION_CHECK_TARIFF_NAME,
          };
          if (values.expires_days !== undefined && values.expires_days !== null) {
            flexibleProxyData.expires_days = values.expires_days;
          }

          logger.info(`   flexibleProxyData.is_keepalive: ${flexibleProxyData.is_keepalive} (type: ${typeof flexibleProxyData.is_keepalive})`);
          logger.info(`   flexibleProxyData.is_compression: ${flexibleProxyData.is_compression} (type: ${typeof flexibleProxyData.is_compression})`);

          // Запускаем создание прокси
          const result = await apiService.createFlexibleProxy(flexibleProxyData);
          const createdProxies = Array.isArray(result.data) ? result.data : [];
          // #region agent log
          sendAgentDebugLog(
            'H8',
            'Proxies.js:handleCreateProxy:flexibleResponse',
            'Ответ API гибкого создания прокси',
            {
              responseType: Array.isArray(result?.data) ? 'array' : typeof result?.data,
              createdFromArray: createdProxies.length,
              createdCountField: result?.data?.created_count ?? result?.data?.count ?? null
            }
          );
          // #endregion

          // Останавливаем таймер времени
          if (timeProgressInterval) {
            clearInterval(timeProgressInterval);
            timeProgressInterval = null;
          }

          // Обрабатываем ответ
          let createdCount = 0;
          let krakenRetryErrors = [];
          try {
            if (Array.isArray(result.data)) {
              createdCount = createdProxies.length;
            } else if (result.data && typeof result.data === 'object') {
              createdCount = result.data.created_count || result.data.count || 0;
              krakenRetryErrors = extractKrakenRetryErrors(result.data);
            }
          } catch (error) {
            logger.error('Ошибка обработки ответа гибкой прокси', error);
            createdCount = 0;
          }

          isCompleted = true;

          if (createdCount > 0) {
            const hasKrakenRetryErrors = reportKrakenRetryErrors(krakenRetryErrors, createdCount, quantity);
            if (!hasKrakenRetryErrors) {
              message.success(`✅ Создано ${createdCount} прокси из гибкого тарифа${selectedOperators.length > 1 ? ' с распределением по операторам' : ''}`);
            }

            // ИСПРАВЛЕНО: Плавный переход к 100% если создалось раньше или показываем текущий прогресс если позже
            if (!hasKrakenRetryErrors && currentProgress < 100) {
              smoothTransitionTo100(currentProgress);

              // Закрываем модальное окно после завершения плавного перехода (примерно через 2.5 секунды)
              const transitionDuration = ((100 - currentProgress) / 2) * 50; // Время перехода в миллисекундах
              setTimeout(() => {
                if (smoothProgressInterval) {
                  clearInterval(smoothProgressInterval);
                }
                if (creationModal) {
                  creationModal.destroy();
                }
              }, transitionDuration + 500); // +500мс для задержки после завершения
            } else if (!hasKrakenRetryErrors) {
              updateProgress(100, `Создано ${createdCount} прокси из ${quantity}`, 'success');

              // Закрываем модальное окно через 2 секунды после завершения
              if (creationModal) {
                setTimeout(() => {
                  creationModal.destroy();
                }, 2000);
              }
            } else if (creationModal) {
              setTimeout(() => {
                creationModal.destroy();
              }, 3500);
            }
          } else {
            message.error('❌ Не удалось создать прокси из гибкого тарифа');
            updateProgress(currentProgress, `Ошибка: не удалось создать прокси`, 'exception');

            // Закрываем модальное окно через 2 секунды после ошибки
            if (creationModal) {
              setTimeout(() => {
                creationModal.destroy();
              }, 2000);
            }
          }

        } catch (error) {
          const errorMessage = handleApiError(error);
          logger.error('Ошибка создания гибкой прокси', {
            userMessage: errorMessage,
            apiDetail: error?.response?.data,
            status: error?.response?.status,
          });
          message.error(`❌ ${errorMessage}`);

          // Останавливаем таймеры при ошибке
          if (timeProgressInterval) {
            clearInterval(timeProgressInterval);
            timeProgressInterval = null;
          }
          if (smoothProgressInterval) {
            clearInterval(smoothProgressInterval);
            smoothProgressInterval = null;
          }

          isCompleted = true;
          updateProgress(currentProgress, `Ошибка: ${errorMessage}`, 'exception');

          // Закрываем модальное окно через 2 секунды после ошибки
          if (creationModal) {
            setTimeout(() => {
              creationModal.destroy();
            }, 2000);
          }
        }
        } else {
          // Создаем прокси обычным способом
          const quantity = values.proxy_quantity ?? 1;
          const selectedOperators = values.mobile_operator || [];

          // Распределяем операторов между прокси
          const distributedOperators = distributeOperators(selectedOperators, quantity);

          let totalCreated = 0;
          let totalErrors = 0;
          const krakenRetryErrorLines = [];

          updateProgress(0, `Создание ${quantity} прокси...`);

          // * ОПТИМИЗАЦИЯ: Группируем прокси по операторам для создания группами
          // Если операторы не указаны или все одинаковые, создаем все прокси одним запросом
          const uniqueOperators = [...new Set(distributedOperators.filter(op => op))];
          const hasMultipleOperators = uniqueOperators.length > 1;

          if (!hasMultipleOperators && quantity > 1) {
            // Все прокси с одним оператором или без оператора - создаем одним запросом
            logger.info(`🚀 Оптимизация: Создаем ${quantity} прокси одним запросом`);
            updateProgress(10, `Создание ${quantity} прокси одним запросом...`);

            try {
              const proxyData = {
                client_login: clientLogin,
                tariff_plan_name: values.tariff_plan_name,
                proxy_quantity: quantity, // Создаем все прокси одним запросом
                proxy_type: values.proxy_type,
                city: values.city || null,
                mobile_operator: uniqueOperators[0] || null,
                auto_replace_enabled: values.auto_replace_enabled !== undefined ? values.auto_replace_enabled : false,
                reconnect_link_enabled: values.reconnect_link_enabled !== undefined ? values.reconnect_link_enabled : false,
                is_keepalive: values.is_keepalive !== undefined ? values.is_keepalive : false,
                is_compression: values.is_compression !== undefined ? values.is_compression : false,
                kraken_is_allow: values.kraken_is_allow !== undefined ? values.kraken_is_allow : true,
                kraken_is_ports: values.kraken_is_ports !== undefined ? values.kraken_is_ports : true,
                kraken_allow: parseKrakenIntList(values.kraken_allow),
                kraken_targetport: parseKrakenIntList(values.kraken_targetport),
                connection_id: (values.connection_id === '' || values.connection_id == null) ? null : values.connection_id,
                server_id: (values.server_id === '' || values.server_id == null) ? null : values.server_id
              };
              if (values.expires_days !== undefined && values.expires_days !== null) {
                proxyData.expires_days = values.expires_days;
              }

              // #region agent log
              sendAgentDebugLog(
                'H2',
                'Proxies.js:handleCreateProxy:nonFlexiblePayload',
                'Payload обычного создания прокси перед API',
                {
                  proxy_quantity: proxyData.proxy_quantity,
                  tariff_plan_name: proxyData.tariff_plan_name || null,
                  auto_replace_enabled: proxyData.auto_replace_enabled ?? null,
                  is_keepalive: proxyData.is_keepalive ?? null,
                  is_compression: proxyData.is_compression ?? null,
                  modem_reconnect_type: proxyData.modem_reconnect_type ?? null,
                  modem_reconnect_interval: proxyData.modem_reconnect_interval ?? null,
                  modem_osfp_id: proxyData.modem_osfp_id ?? null,
                  modem_is_osfp: proxyData.modem_is_osfp ?? null,
                  has_modem_reconnect_type: Object.prototype.hasOwnProperty.call(proxyData, 'modem_reconnect_type'),
                  has_modem_reconnect_interval: Object.prototype.hasOwnProperty.call(proxyData, 'modem_reconnect_interval'),
                  has_modem_osfp_id: Object.prototype.hasOwnProperty.call(proxyData, 'modem_osfp_id'),
                  has_modem_is_osfp: Object.prototype.hasOwnProperty.call(proxyData, 'modem_is_osfp')
                }
              );
              // #endregion

              logger.info(`🔧 Frontend: Создание ${quantity} прокси одним запросом`);

              // Обновляем прогресс во время выполнения
              const progressInterval = setInterval(() => {
                const currentProgress = Math.min(90, 10 + Math.random() * 80); // Показываем прогресс 10-90%
                updateProgress(currentProgress, `Создание ${quantity} прокси...`);
              }, 1000);

              const result = await apiService.createProxy(proxyData);
              // #region agent log
              sendAgentDebugLog(
                'H8',
                'Proxies.js:handleCreateProxy:nonFlexibleResponse',
                'Ответ API обычного создания прокси',
                {
                  responseType: Array.isArray(result?.data) ? 'array' : typeof result?.data,
                  createdFromArray: Array.isArray(result?.data) ? result.data.length : null,
                  createdCountField: result?.data?.created_count ?? result?.data?.count ?? null
                }
              );
              // #endregion
              clearInterval(progressInterval);

              // Обрабатываем ответ
              let createdCount = 0;
              try {
                if (Array.isArray(result.data)) {
                  createdCount = result.data.length;
                } else if (result.data && typeof result.data === 'object') {
                  createdCount = result.data.created_count || result.data.count || 0;
                  const retryErrors = extractKrakenRetryErrors(result.data);
                  if (retryErrors.length > 0) {
                    krakenRetryErrorLines.push(...retryErrors);
                  }
                }
              } catch (error) {
                logger.error('Ошибка обработки ответа при создании прокси', error);
              }

              totalCreated = createdCount || quantity;
              logger.info(`✅ Создано ${totalCreated} прокси одним запросом`);

            } catch (error) {
              const errorMessage = handleApiError(error);
              logger.error(`Ошибка создания прокси: ${errorMessage}`, error);
              message.error(`Ошибка создания прокси: ${errorMessage}`);
              totalErrors = quantity;
            }
          } else {
            // Разные операторы - создаем по группам или по одной
            // Группируем прокси по операторам
            const operatorGroups = {};
            distributedOperators.forEach((operator, index) => {
              const opKey = operator || 'none';
              if (!operatorGroups[opKey]) {
                operatorGroups[opKey] = [];
              }
              operatorGroups[opKey].push(index);
            });

            logger.info(`🚀 Создание прокси по группам операторов: ${Object.keys(operatorGroups).length} групп`);

            let groupIndex = 0;
            for (const [operator, indices] of Object.entries(operatorGroups)) {
              groupIndex++;
              const groupQuantity = indices.length;
              const currentProgress = Math.round((totalCreated / quantity) * 100);
              updateProgress(currentProgress, `Создание группы ${groupIndex}/${Object.keys(operatorGroups).length} (${groupQuantity} прокси)...`);

              try {
                const proxyData = {
                  client_login: clientLogin,
                  tariff_plan_name: values.tariff_plan_name,
                  proxy_quantity: groupQuantity, // Создаем группу прокси одним запросом
                  proxy_type: values.proxy_type,
                  city: values.city || null,
                  mobile_operator: operator === 'none' ? null : operator,
                  auto_replace_enabled: values.auto_replace_enabled !== undefined ? values.auto_replace_enabled : false,
                  reconnect_link_enabled: values.reconnect_link_enabled !== undefined ? values.reconnect_link_enabled : false,
                  is_keepalive: values.is_keepalive !== undefined ? values.is_keepalive : false,
                  is_compression: values.is_compression !== undefined ? values.is_compression : false,
                  kraken_is_allow: values.kraken_is_allow !== undefined ? values.kraken_is_allow : true,
                  kraken_is_ports: values.kraken_is_ports !== undefined ? values.kraken_is_ports : true,
                  kraken_allow: parseKrakenIntList(values.kraken_allow),
                  kraken_targetport: parseKrakenIntList(values.kraken_targetport),
                  connection_id: (values.connection_id === '' || values.connection_id == null) ? null : values.connection_id,
                  server_id: (values.server_id === '' || values.server_id == null) ? null : values.server_id
                };
                if (values.expires_days !== undefined && values.expires_days !== null) {
                  proxyData.expires_days = values.expires_days;
                }

                logger.info(`🔧 Frontend: Создание группы ${groupIndex} (${groupQuantity} прокси) с оператором: ${operator || 'не указан'}`);

                const result = await apiService.createProxy(proxyData);

                // Обрабатываем ответ
                let createdCount = 0;
                try {
                  if (Array.isArray(result.data)) {
                    createdCount = result.data.length;
                  } else if (result.data && typeof result.data === 'object') {
                    createdCount = result.data.created_count || result.data.count || 0;
                    const retryErrors = extractKrakenRetryErrors(result.data);
                    if (retryErrors.length > 0) {
                      krakenRetryErrorLines.push(...retryErrors);
                    }
                  }
                } catch (error) {
                  logger.error('Ошибка обработки ответа при создании прокси', error);
                }

                totalCreated += createdCount || groupQuantity;

                const progress = Math.round((totalCreated / quantity) * 100);
                updateProgress(progress, `Создано ${totalCreated} прокси из ${quantity}`);

                logger.info(`✅ Создано ${createdCount || groupQuantity} прокси в группе ${groupIndex}`);

              } catch (error) {
                const errorMessage = handleApiError(error);
                logger.error(`Ошибка создания группы ${groupIndex}: ${errorMessage}`, error);
                totalErrors += groupQuantity;

                const progress = Math.round((totalCreated / quantity) * 100);
                updateProgress(progress, `Создано ${totalCreated} прокси из ${quantity}, ошибок: ${totalErrors}`);
              }
            }
          }

        // ИСПРАВЛЕНО: Определяем статус и сообщение для финального прогресса на основе созданных прокси
        const finalProgress = Math.round((totalCreated / quantity) * 100);
        let finalStatus = 'success';
        let finalMessage = `Создано ${totalCreated} прокси из ${quantity}`;

        if (totalErrors === 0) {
          message.success(`✅ Создано ${totalCreated} прокси${selectedOperators.length > 1 ? ' с распределением по операторам' : ''}`);
        } else if (totalCreated > 0) {
          message.warning(`⚠️ Создано ${totalCreated} прокси, ошибок: ${totalErrors}`);
          finalStatus = 'exception';
          finalMessage = `Создано ${totalCreated} прокси из ${quantity}, ошибок: ${totalErrors}`;
        } else {
          message.error(`❌ Не удалось создать прокси. Ошибок: ${totalErrors}`);
          finalStatus = 'exception';
          finalMessage = `Создано 0 прокси из ${quantity}, ошибок: ${totalErrors}`;
        }

        const hasKrakenRetryErrors = reportKrakenRetryErrors(krakenRetryErrorLines, totalCreated, quantity);
        if (hasKrakenRetryErrors) {
          finalStatus = 'exception';
          finalMessage = `Создано ${totalCreated} прокси из ${quantity}, ошибки Kraken после ретраев: ${[...new Set(krakenRetryErrorLines)].length}`;
        }

        updateProgress(finalProgress, finalMessage, finalStatus);

        // Закрываем модальное окно через 2 секунды после завершения
        if (creationModal) {
          setTimeout(() => {
            creationModal.destroy();
          }, 2000);
        }
      }

      setModalVisible(false);
      form.resetFields();
      setClientMode('existing');
      setSelectedTariffForCreate(null);
      setOsfpList([]);

      const newPagination = { ...paginationRef.current, current: 1 };
      const hadFiltersBeforeReset = Object.keys(filteredInfoRef.current || {}).length > 0;
      const emptyFilters = {};
      const totalBeforeRefresh = paginationRef.current?.total ?? null;
      setPagination(newPagination);
      filteredInfoRef.current = emptyFilters;
      setFilteredInfo(emptyFilters);
      fetchFilterValues(emptyFilters);
      const resetSearchParams = new URLSearchParams(searchParams);
      resetSearchParams.set('page', '1');
      setSearchParams(resetSearchParams, { replace: true });
      // #region agent log
      sendAgentDebugLog(
        'H5',
        'Proxies.js:handleCreateProxy:clearFiltersAfterSuccess',
        'Сбрасываем фильтры после успешного создания',
        { hadFiltersBeforeReset }
      );
      // #endregion
      // #region agent log
      sendAgentDebugLog(
        'H1',
        'Proxies.js:handleCreateProxy:beforeRefresh',
        'Перед принудительным refresh после создания',
        {
          newPaginationCurrent: newPagination.current,
          newPaginationPageSize: newPagination.pageSize,
          formWasClosed: true,
          totalBeforeRefresh
        }
      );
      // #endregion
      const refreshResult = await fetchProxiesOnly(emptyFilters, true, newPagination, true);
      // #region agent log
      sendAgentDebugLog(
        'H8',
        'Proxies.js:handleCreateProxy:afterImmediateRefresh',
        'Результат немедленного refresh после создания',
        {
          totalAfterRefresh: refreshResult?.totalCount ?? null,
          pageCountAfterRefresh: refreshResult?.pageCount ?? null,
          firstIdsAfterRefresh: refreshResult?.ids ?? []
        }
      );
      // #endregion

      // Обновляем логи создания прокси
      setLogsRefreshKey(prev => prev + 1);

      setCreatingProxy(false);
      setLoading(false);
    } catch (error) {
      // Улучшенная обработка ошибок
      const errorMessage = handleApiError(error);

      // Логируем полную информацию об ошибке для отладки
      logger.error('Полная информация об ошибке создания прокси', {
        error,
        response: error.response,
        data: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        stack: error.stack
      });

      // Специальная обработка для AxiosError
      if (error.name === 'AxiosError') {
        logger.error('AxiosError details', {
          code: error.code,
          config: error.config,
          request: error.request
        });
      }

      // Отображаем ошибку в прогрессе
      updateProgress(100, `Ошибка: ${errorMessage}`, 'exception');
      message.error(`❌ ${errorMessage}`);

      // Закрываем модальное окно через 2 секунды после ошибки
      if (creationModal) {
        setTimeout(() => {
          creationModal.destroy();
        }, 2000);
      }

      setCreatingProxy(false);
      setLoading(false);
    }
  };


  const handleDeleteProxy = async (proxyId) => {
    try {
      await apiService.deleteProxy(proxyId);
      message.success('Прокси удалена');
      // ОПТИМИЗАЦИЯ: После удаления обновляем только прокси
      await fetchProxiesOnly(null, true);
      setSelectedRowKeys([]);
      setSelectionResetToken((prev) => prev + 1);
    } catch (error) {
      message.error('Ошибка удаления прокси');
    }
  };

  const handleBulkEditProxies = async (values) => {
    if (selectedRowKeys.length === 0) {
      message.warning('Выберите прокси для редактирования');
      return;
    }

    const totalProxies = selectedRowKeys.length;
    try {
      // Подготавливаем данные для массового обновления
      const updateData = {};

      // Добавляем только те поля, которые были изменены
      if (values.slimiter !== undefined) {
        // ИСПРАВЛЕНО: Конвертируем Мб/с в КБайт/с для Kraken API, используем единую функцию конвертации
        updateData.slimiter = values.slimiter !== undefined && values.slimiter !== null && values.slimiter > 0 ? convertMbpsToKBytes(values.slimiter) : 0;
      }
      if (values.rlimiter !== undefined) {
        updateData.rlimiter = values.rlimiter !== undefined && values.rlimiter !== null && values.rlimiter > 0 ? values.rlimiter : 0;
      }
      if (values.maxconn !== undefined) {
        updateData.maxconn = values.maxconn !== undefined && values.maxconn !== null && values.maxconn > 0 ? values.maxconn : 0;
      }
      if (values.is_active !== undefined) {
        updateData.is_active = values.is_active;
      }
      if (values.server_id !== undefined) {
        updateData.server_id = values.server_id || undefined;
      }
      if (values.connection_id !== undefined) {
        updateData.connection_id = values.connection_id || undefined;
      }
      if (values.proxy_type !== undefined) {
        updateData.proxy_type = values.proxy_type;
      }
      if (values.expires_at !== undefined) {
        updateData.expires_at = values.expires_at; // null = Безлимит
      } else if (values.expires_days !== undefined) {
        updateData.expires_days = values.expires_days;
      }
      if (values.city !== undefined) {
        updateData.selected_city = values.city;
      }
      if (values.mobile_operator !== undefined) {
        updateData.selected_operator = values.mobile_operator;
      }
      if (values.auto_replace_enabled !== undefined) {
        updateData.auto_replace_enabled = values.auto_replace_enabled;
      }
      if (values.is_keepalive !== undefined) {
        updateData.is_keepalive = values.is_keepalive;
      }
      if (values.is_compression !== undefined) {
        updateData.is_compression = values.is_compression;
      }
      // Параметры модема (применяются к прокси с модемом при переподключении/автозамене)
      if (values.modem_reconnect_type !== undefined && values.modem_reconnect_type !== null && values.modem_reconnect_type !== '') {
        updateData.modem_reconnect_type = values.modem_reconnect_type;
      }
      if (values.modem_reconnect_type === 'link') {
        updateData.modem_reconnect_interval = null;
      } else if (values.modem_reconnect_interval !== undefined && values.modem_reconnect_interval !== null && values.modem_reconnect_interval > 0) {
        updateData.modem_reconnect_interval = values.modem_reconnect_interval;
      }
      if (values.modem_osfp_id !== undefined && values.modem_osfp_id !== null) {
        updateData.modem_osfp_id = values.modem_osfp_id;
      }
      if (values.modem_is_osfp !== undefined) {
        updateData.modem_is_osfp = values.modem_is_osfp;
      }
      if (values.reconnect_link_enabled !== undefined) {
        updateData.reconnect_link_enabled = values.reconnect_link_enabled;
      }
      const krakenBaseline = bulkKrakenBaselineRef.current || {};
      if (values.kraken_is_allow !== undefined) {
        const baselineAllow = krakenBaseline.kraken_is_allow;
        if (baselineAllow === undefined || values.kraken_is_allow !== baselineAllow) {
          updateData.kraken_is_allow = values.kraken_is_allow;
        }
      }
      if (values.kraken_is_ports !== undefined) {
        const baselinePorts = krakenBaseline.kraken_is_ports;
        if (baselinePorts === undefined || values.kraken_is_ports !== baselinePorts) {
          updateData.kraken_is_ports = values.kraken_is_ports;
        }
      }
      const krakenAllowUpdate = resolveBulkKrakenListUpdate(
        values.kraken_allow,
        krakenBaseline.kraken_allow,
      );
      if (krakenAllowUpdate !== undefined) {
        updateData.kraken_allow = krakenAllowUpdate;
      }
      const krakenTargetportUpdate = resolveBulkKrakenListUpdate(
        values.kraken_targetport,
        krakenBaseline.kraken_targetport,
      );
      if (krakenTargetportUpdate !== undefined) {
        updateData.kraken_targetport = krakenTargetportUpdate;
      }
      // ИСПРАВЛЕНО: Правильная обработка значения тарифа
      if (values.tariff_plan_name !== undefined && values.tariff_plan_name !== null && values.tariff_plan_name !== '') {
        // Убеждаемся, что значение тарифа существует в списке доступных тарифов
        const tariffExists = tariffPlans.some(plan => plan.name === values.tariff_plan_name);
        if (tariffExists) {
          updateData.tariff_plan_name = values.tariff_plan_name;
          logger.debug('Тарифный план добавлен в updateData', {
            tariff_plan_name: values.tariff_plan_name,
            exists: true
          });
        } else {
          logger.warn('Тарифный план не найден в списке доступных тарифов', {
            tariff_plan_name: values.tariff_plan_name,
            availableTariffs: tariffPlans.map(p => p.name)
          });
        }
      } else {
        logger.debug('Тарифный план не указан или пустой', {
          tariff_plan_name: values.tariff_plan_name
        });
      }

      // Как при одиночном редактировании: для миграции нужен город. Массовая форма часто меняет только
      // подключение Kraken / модем, не трогая «Город», а в БД у части прокси пустой selected_city.
      if (updateData.selected_city === undefined || updateData.selected_city === null || String(updateData.selected_city).trim() === '') {
        if (updateData.connection_id != null && updateData.connection_id !== undefined) {
          const conn = (krakenConnections || []).find((c) => String(c.id) === String(updateData.connection_id));
          if (conn?.city) {
            updateData.selected_city = conn.city;
          }
        } else if (updateData.server_id != null && updateData.server_id !== undefined) {
          const srv = (availableServers || []).find((s) => String(s.id) === String(updateData.server_id));
          if (srv?.kraken_connection_id != null) {
            const conn = (krakenConnections || []).find((c) => String(c.id) === String(srv.kraken_connection_id));
            if (conn?.city) {
              updateData.selected_city = conn.city;
            }
          }
        }
      }

      if (Object.keys(updateData).length === 0) {
        message.info('Нет изменений для сохранения');
        setLoading(false);
        setLoadingProgress(0);
        setLoadingMessage('');
        return;
      }

      // Как в одиночном редактировании: миграция на другое подключение / город
      const newConnectionId = values.connection_id ?? undefined;
      const selectedOperatorValue = String(values.mobile_operator || '').trim();
      let forceCrossConnectionMigration = false;
      if (selectedOperatorValue && newConnectionId) {
        const connectionServers = availableServers.filter((server) => (
          String(server.kraken_connection_id ?? '') === String(newConnectionId ?? '')
        ));
        const hasOperatorOnConnection = connectionServers.some((server) => (
          operatorsMatch(server.mobile_operator, selectedOperatorValue)
        ));
        if (!hasOperatorOnConnection) {
          forceCrossConnectionMigration = true;
        }
      }
      const requiresBulkMigrationConfirm = Boolean(
        updateData.selected_city !== undefined
        || updateData.connection_id !== undefined
        || updateData.server_id !== undefined
        || forceCrossConnectionMigration
      );
      if (requiresBulkMigrationConfirm) {
        if (values.kraken_is_allow !== undefined) {
          updateData.kraken_is_allow = values.kraken_is_allow;
        }
        if (values.kraken_is_ports !== undefined) {
          updateData.kraken_is_ports = values.kraken_is_ports;
        }
        const confirmed = await new Promise((resolve) => {
          Modal.confirm({
            title: 'Подтверждение миграции прокси',
            okText: 'Подтвердить',
            cancelText: 'Отмена',
            content: (
              <div>
                <p>
                  Для выбранных прокси будет выполнена миграция (новая прокси 1в1, старая удаляется после успешного создания),
                  как при одиночном редактировании.
                </p>
                {forceCrossConnectionMigration && (
                  <p style={{ marginBottom: 0 }}>
                    Оператор «{selectedOperatorValue}» отсутствует на указанном подключении — прокси будут созданы на подходящем сервере.
                  </p>
                )}
              </div>
            ),
            onOk: () => resolve(true),
            onCancel: () => resolve(false)
          });
        });
        if (!confirmed) {
          return;
        }
      }
      if (forceCrossConnectionMigration) {
        updateData.connection_id = null;
        updateData.server_id = null;
        updateData.force_cross_connection_migration = true;
      }

      // ОПТИМИЗИРОВАНО: Используем фоновую задачу для массового обновления (как в NetworkPorts)
      setLoading(true);
      setLoadingProgress(0);
      setLoadingMessage(`Начинаем обновление ${totalProxies} прокси...`);
      setBulkEditModalVisible(false);

      let progressInterval = null;
      let taskId = null;
      const bulkUpdatedProxyIds = [...selectedRowKeys];
      const bulkKrakenPermissionPatch = (
        updateData.kraken_is_allow !== undefined
        || updateData.kraken_is_ports !== undefined
        || updateData.kraken_allow !== undefined
        || updateData.kraken_targetport !== undefined
      )
        ? {
          kraken_is_allow: updateData.kraken_is_allow,
          kraken_is_ports: updateData.kraken_is_ports,
          kraken_allow: updateData.kraken_allow,
          kraken_targetport: updateData.kraken_targetport,
        }
        : null;

      try {
        // Запускаем фоновую задачу
        const result = await apiService.bulkUpdateProxiesBackground(selectedRowKeys, updateData);
        taskId = result.data.task_id;

        if (!taskId) {
          throw new Error('Не получен task_id для отслеживания прогресса');
        }

        logger.info(`Запущено массовое обновление прокси, task_id: ${taskId}`);

        // Функция для получения прогресса
        const checkProgress = async (taskIdToCheck) => {
          try {
            const progressResponse = await apiService.getBulkUpdateProxiesStatus(taskIdToCheck);
            const progress = progressResponse.data || {};

            if (progress) {
              const completed = progress.completed || 0;
              const total = progress.total || totalProxies;
              const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

              setLoadingProgress(percentage);
              setLoadingMessage(progress.message || `Обновлено ${completed} из ${total} прокси`);

              // Если задача завершена, останавливаем polling
              if (progress.status === 'completed') {
                if (progressInterval) {
                  clearInterval(progressInterval);
                  progressInterval = null;
                }
                setLoadingProgress(100);
                setBulkEditModalVisible(false);
                bulkEditForm.resetFields();
                setSelectedRowKeys([]);
                setSelectionResetToken((prev) => prev + 1);
                // ОПТИМИЗАЦИЯ: После массового редактирования обновляем только прокси
                await fetchProxiesOnly(null, false);
                if (bulkKrakenPermissionPatch) {
                  setProxies((prev) => patchProxiesKrakenPermissions(
                    prev,
                    bulkUpdatedProxyIds,
                    bulkKrakenPermissionPatch,
                  ));
                }

                const successCount = completed - (progress.errors || 0);
                const errorCount = progress.errors || 0;
                const elapsedTime = progress.elapsed_time || 0;

                // Показываем одно сообщение об успехе
                if (successCount > 0) {
                  const syncInfo = progress.kraken_sync_tasks > 0
                    ? ` (синхронизация с Kraken в фоне: ${progress.kraken_sync_tasks})`
                    : '';
                  message.success(`Обновлено ${successCount} прокси за ${elapsedTime}с${syncInfo}`);
                }
                if (errorCount > 0) {
                  message.warning(`Ошибка обновления ${errorCount} прокси`);
                }

                // Скрываем прогресс через небольшую задержку (как в NetworkPorts)
                setTimeout(() => {
                  setLoading(false);
                  setLoadingProgress(0);
                  setLoadingMessage('');
                }, 500);

                return true;
              } else if (progress.status === 'error') {
                if (progressInterval) {
                  clearInterval(progressInterval);
                  progressInterval = null;
                }
                setLoadingProgress(0);
                setLoadingMessage('');
                message.error(`Ошибка обновления: ${progress.message}`);
                // Скрываем модальное окно через небольшую задержку
                setTimeout(() => {
                  setLoading(false);
                }, 500);
                return true;
              }
            }
            return false;
          } catch (error) {
            logger.warning('Ошибка получения прогресса:', error);
            return false;
          }
        };

        // Если получили task_id, начинаем отслеживать прогресс в фоне
        setLoadingMessage(`Всего ${totalProxies} прокси, обновляется...`);

        // Проверяем прогресс каждую секунду
        progressInterval = setInterval(async () => {
          const isCompleted = await checkProgress(taskId);
          if (isCompleted && progressInterval) {
            clearInterval(progressInterval);
            progressInterval = null;
          }
        }, 1000);

        // Также проверяем сразу
        await checkProgress(taskId);

        // Не блокируем выполнение - прогресс обновляется в фоне через setInterval
        // Возвращаем управление, прогресс будет обновляться автоматически
        return; // Выходим, прогресс будет обновляться в фоне через setInterval

      } catch (error) {
        if (progressInterval) {
          clearInterval(progressInterval);
        }
        setLoading(false);
        setLoadingProgress(0);
        setLoadingMessage('');
        logger.error('Ошибка массового обновления прокси:', error);
        message.error(error.response?.data?.detail || 'Ошибка массового обновления прокси');
      }

    } catch (error) {
      setLoading(false);
      setLoadingProgress(0);
      setLoadingMessage('');
      message.error('Ошибка массового редактирования прокси');
    }
  };

  /** Массовое вкл/выкл выбранных прокси (БД + Kraken). Только все включённые или только все отключённые; смесь — предупреждение. */
  const handleBulkDisableProxies = (selected) => {
    const items = Array.isArray(selected) ? selected : [selected];
    const proxyIds = items
      .map((row) => row?._original?.proxy_id ?? row?._original?.id ?? row?.proxy_id ?? row?.id)
      .filter((id) => id !== null && id !== undefined);

    if (proxyIds.length === 0) {
      message.warning('Не удалось определить прокси для операции');
      return;
    }

    const resolveProxy = (id) => safeProxies.find(
      (x) => String(x.id) === String(id) || (x.proxy_id != null && String(x.proxy_id) === String(id))
    );

    const isRecordActive = (p) => {
      if (!p) return true;
      if (p.is_active === false || p.is_active === 0) return false;
      return true;
    };

    const targetIds = proxyIds.filter((id) => resolveProxy(id));
    if (targetIds.length === 0) {
      message.warning('Не найдены данные выбранных прокси в списке');
      return;
    }

    let hasActive = false;
    let hasInactive = false;
    targetIds.forEach((id) => {
      const p = resolveProxy(id);
      if (isRecordActive(p)) hasActive = true;
      else hasInactive = true;
    });

    if (hasActive && hasInactive) {
      message.warning('Выберите только включённые или только отключённые прокси.');
      return;
    }

    const turnOn = hasInactive && !hasActive;
    const updatePayload = { is_active: turnOn };
    const verb = turnOn ? 'Включить' : 'Отключить';
    const verbLower = turnOn ? 'включения' : 'отключения';

    Modal.confirm({
      title: `${verb} ${targetIds.length} прокси в Kraken?`,
      content: 'Статус будет изменён в базе и передан в Kraken для каждой выбранной прокси.',
      okText: verb,
      cancelText: 'Отмена',
      onOk: async () => {
        let progressInterval = null;
        let taskId = null;
        const totalProxies = targetIds.length;
        try {
          setPowerToggleBanner({
            text: `${verb} ${totalProxies} прокси (выполняется в фоне)…`,
            tone: 'success',
            busy: true,
            progress: 0,
            autoHideMs: 0,
          });

          const result = await apiService.bulkUpdateProxiesBackground(targetIds, updatePayload);
          taskId = result.data?.task_id;
          if (!taskId) {
            throw new Error('Не получен task_id для отслеживания прогресса');
          }

          logger.info(`Массовое ${verbLower} прокси, task_id: ${taskId}`);

          const checkProgress = async (taskIdToCheck) => {
            try {
              const progressResponse = await apiService.getBulkUpdateProxiesStatus(taskIdToCheck);
              const progress = progressResponse.data || {};

              if (progress) {
                const completed = progress.completed || 0;
                const total = progress.total || totalProxies;
                const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

                setPowerToggleBanner({
                  text:
                    progress.message ||
                    `${turnOn ? 'Включено' : 'Отключено'} ${completed} из ${total} прокси`,
                  tone: 'success',
                  busy: true,
                  progress: percentage,
                  autoHideMs: 0,
                });

                if (progress.status === 'completed') {
                  if (progressInterval) {
                    clearInterval(progressInterval);
                    progressInterval = null;
                  }
                  setSelectedRowKeys([]);
                  setSelectionResetToken((prev) => prev + 1);
                  void fetchProxiesOnly(null, false).catch(() => {});

                  const successCount = completed - (progress.errors || 0);
                  const errorCount = progress.errors || 0;
                  const elapsedTime = progress.elapsed_time || 0;

                  const syncInfo = progress.kraken_sync_tasks > 0
                    ? `, Kraken: ${progress.kraken_sync_tasks}`
                    : '';
                  const baseText =
                    successCount > 0
                      ? `${turnOn ? 'Включено' : 'Отключено'} ${successCount} из ${totalProxies} прокси за ${elapsedTime}с${syncInfo}`
                      : `Обработано ${totalProxies} прокси за ${elapsedTime}с`;
                  if (errorCount > 0) {
                    message.warning(
                      `Не удалось ${turnOn ? 'включить' : 'отключить'} ${errorCount} прокси`
                    );
                  }

                  setPowerToggleBanner({
                    text: errorCount > 0 ? `${baseText}. Ошибок: ${errorCount}` : baseText,
                    tone: errorCount > 0 ? 'error' : 'success',
                    busy: false,
                    progress: 100,
                    autoHideMs: 6000,
                  });

                  return true;
                }
                if (progress.status === 'error') {
                  if (progressInterval) {
                    clearInterval(progressInterval);
                    progressInterval = null;
                  }
                  setPowerToggleBanner({
                    text: `Ошибка: ${progress.message || `${verbLower} прервано`}`,
                    tone: 'error',
                    busy: false,
                    progress: 0,
                    autoHideMs: 10000,
                  });
                  return true;
                }
              }
              return false;
            } catch (error) {
              logger.warning(`Ошибка получения прогресса ${verbLower}:`, error);
              return false;
            }
          };

          progressInterval = setInterval(async () => {
            const isCompleted = await checkProgress(taskId);
            if (isCompleted && progressInterval) {
              clearInterval(progressInterval);
              progressInterval = null;
            }
          }, 1000);

          await checkProgress(taskId);
        } catch (error) {
          if (progressInterval) {
            clearInterval(progressInterval);
          }
          setPowerToggleBanner({
            text: error.response?.data?.detail || error.message || `Ошибка массового ${verbLower} прокси`,
            tone: 'error',
            busy: false,
            progress: 0,
            autoHideMs: 10000,
          });
          logger.error(`Ошибка массового ${verbLower} прокси:`, error);
        }
      },
    });
  };

  const handleBulkCheckProxies = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Выберите прокси для проверки');
      return;
    }

    const proxyIds = [...selectedRowKeys];
    setBulkChecking(true);

    try {
      const response = await apiService.massCheckProxies(proxyIds, 'quick_availability');
      const data = response?.data?.data || response?.data || {};
      const results = data.results || [];
      const total = data.total || proxyIds.length;

      let availableCount = 0;
      let unavailableCount = 0;
      let errorCount = 0;
      const checkedAt = new Date().toISOString();
      const availabilityById = new Map();

      for (const item of results) {
        if (!item || item.status === 'error') {
          errorCount += 1;
          continue;
        }
        const raw = (item.availability_status || item.status || '').toString().toLowerCase();
        const proxyId = item.proxy_id ?? item.id;
        if (raw === 'available' || raw === 'доступен') {
          availableCount += 1;
          if (proxyId != null) {
            availabilityById.set(String(proxyId), {
              availability_status: 'Доступен',
              last_checked: checkedAt,
              quick_check_ip: item.external_ip != null && String(item.external_ip).trim() !== ''
                ? String(item.external_ip).trim()
                : null,
            });
          }
        } else if (raw === 'unavailable' || raw === 'недоступен') {
          unavailableCount += 1;
          if (proxyId != null) {
            availabilityById.set(String(proxyId), {
              availability_status: 'Недоступен',
              last_checked: checkedAt,
              quick_check_ip: null,
            });
          }
        } else {
          errorCount += 1;
        }
      }

      if (availabilityById.size > 0) {
        setProxies((prev) =>
          prev.map((p) => {
            const pid = p?.id ?? p?.proxy_id;
            if (pid == null) return p;
            const patch = availabilityById.get(String(pid));
            return patch ? { ...p, ...patch } : p;
          })
        );
      }

      if (errorCount === 0) {
        setProxyCheckSummary({
          tone: 'success',
          text: `Проверка прокси (выбранных): прокси ${total}, проверено ${total}, доступно ${availableCount}, недоступно ${unavailableCount}, ошибок 0`,
          content: (
            <>
              {`Проверка прокси (выбранных): прокси ${total}, проверено ${total}, доступно ${availableCount}, `}
              <span className="crb__stat-unavailable">{`недоступно ${unavailableCount}`}</span>
              {`, ошибок 0`}
            </>
          ),
        });
      } else {
        setProxyCheckSummary({
          tone: 'error',
          text: `Проверка прокси (выбранных): прокси ${total}, проверено ${total}, доступно ${availableCount}, недоступно ${unavailableCount}, ошибок ${errorCount}`,
          content: (
            <>
              {`Проверка прокси (выбранных): прокси ${total}, проверено ${total}, доступно ${availableCount}, `}
              <span className="crb__stat-unavailable">{`недоступно ${unavailableCount}`}</span>
              {`, ошибок ${errorCount}`}
            </>
          ),
        });
      }

      await fetchProxiesOnly(null, false, null, true);
    } catch (error) {
      logger.error('Ошибка массовой проверки прокси', error);
      setProxyCheckSummary({
        tone: 'error',
        text: 'Ошибка массовой проверки прокси: ' + (error.response?.data?.detail || error.message),
      });
    } finally {
      setBulkChecking(false);
    }
  };

  const mergeProxiesIntoAdminCheckerQueue = useCallback(async (proxyList) => {
    if (!Array.isArray(proxyList) || proxyList.length === 0) return 0;
    await ensureAdminCheckerQueueLoaded();
    const prev = readAdminCheckerQueue();
    const byId = new Map(prev.map((e) => [e.proxy_id, e]));
    let merged = 0;
    for (const p of proxyList) {
      if (!p || typeof p !== 'object') continue;
      const pid = Number(p.proxy_id ?? p.id);
      if (!Number.isFinite(pid)) continue;
      const host = p.domain_host || p.server_ip || p.host || '—';
      const domainHostRaw = p.domain_host != null && String(p.domain_host).trim() !== '' ? String(p.domain_host).trim() : '';
      const serverIpRaw = p.server_ip != null && String(p.server_ip).trim() !== '' ? String(p.server_ip).trim() : '';
      const proxyLogin = p.login != null ? String(p.login).trim() : '';
      const selectedOpRaw = p.selected_operator ?? p.mobile_operator ?? null;
      const selectedOperator =
        selectedOpRaw != null && String(selectedOpRaw).trim() !== ''
          ? String(selectedOpRaw).trim()
          : null;
      byId.set(pid, {
        proxy_id: pid,
        host: String(host),
        domain_host: domainHostRaw || undefined,
        server_ip: serverIpRaw || undefined,
        port: p.port != null && p.port !== '' ? String(p.port) : '—',
        proxy_type: String(p.proxy_type || 'HTTP'),
        availability: String(p.availability_status || '—'),
        auto_check: '—',
        checked_at: '—',
        login: proxyLogin,
        provider: proxyLogin || '—',
        speed: '—',
        selected_operator: selectedOperator,
        checker_line: buildAdminCheckerConnectionLine(p),
      });
      merged += 1;
    }
    writeAdminCheckerQueue(Array.from(byId.values()));
    return merged;
  }, []);

  const handleAddToCheckerProxies = useCallback(async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Выберите прокси');
      return;
    }
    const proxyList = [];
    for (const id of selectedRowKeys) {
      const pid = Number(id);
      const p = safeProxies.find((x) => {
        const k = x.proxy_id ?? x.id;
        return Number(k) === pid;
      });
      if (p) proxyList.push(p);
    }
    const n = await mergeProxiesIntoAdminCheckerQueue(proxyList);
    if (n === 0) {
      message.warning('Не удалось добавить прокси в чекер');
      return;
    }
    message.success(`В чекер добавлено ${n} прокси`);
    navigate('/proxy-checker');
  }, [selectedRowKeys, safeProxies, navigate, mergeProxiesIntoAdminCheckerQueue]);

  const handleAddTableRowToChecker = useCallback(async (row) => {
    const p = row?._original || safeProxies.find((x) => {
      const k = x.proxy_id ?? x.id;
      return row?.id != null && Number(k) === Number(row.id);
    });
    if (!p) {
      message.warning('Прокси не найден');
      return;
    }
    const n = await mergeProxiesIntoAdminCheckerQueue([p]);
    if (n === 0) {
      message.warning('Не удалось добавить в чекер');
      return;
    }
    message.success('Прокси добавлен в чекер');
    navigate('/proxy-checker');
  }, [safeProxies, navigate, mergeProxiesIntoAdminCheckerQueue]);

  const resolveProxyIdFromRow = useCallback((row) => {
    const orig = row?._original || row;
    const pid = orig?.proxy_id ?? orig?.id ?? row?.id;
    const n = Number(pid);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, []);

  const handleAddRowToLocationCheck = useCallback(async (row) => {
    const proxy = row?._original || safeProxies.find((x) => {
      const k = x.proxy_id ?? x.id;
      return row?.id != null && Number(k) === Number(row.id);
    }) || row;
    if (!isLocationCheckProxy(proxy)) {
      message.warning(`Только тариф «${LOCATION_CHECK_TARIFF_NAME}»`);
      return;
    }
    const proxyId = resolveProxyIdFromRow(proxy);
    if (!proxyId) {
      message.warning('Прокси не найден');
      return;
    }
    try {
      await apiService.addLocationCheckTarget(proxyId);
      message.success('Добавлено в проверку локаций');
      navigate('/location-check');
    } catch (error) {
      message.error(error.response?.data?.detail || error.message || 'Не удалось добавить');
    }
  }, [safeProxies, navigate, resolveProxyIdFromRow]);

  const handleAddToLocationCheckProxies = useCallback(async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Выберите прокси');
      return;
    }
    let added = 0;
    let skipped = 0;
    for (const id of selectedRowKeys) {
      const pid = Number(id);
      const p = safeProxies.find((x) => {
        const k = x.proxy_id ?? x.id;
        return Number(k) === pid;
      });
      if (!p || !isLocationCheckProxy(p)) {
        skipped += 1;
        continue;
      }
      try {
        await apiService.addLocationCheckTarget(pid);
        added += 1;
      } catch (error) {
        skipped += 1;
        console.warn('location-check add failed', pid, error);
      }
    }
    if (added === 0) {
      message.warning(
        skipped > 0
          ? `Не добавлено. Нужен тариф «${LOCATION_CHECK_TARIFF_NAME}» и свободный модем.`
          : 'Нечего добавлять'
      );
      return;
    }
    message.success(`В проверку локаций: ${added}${skipped ? `, пропущено ${skipped}` : ''}`);
    navigate('/location-check');
  }, [selectedRowKeys, safeProxies, navigate]);

  const handleToggleProxyStatus = (proxyId, currentStatus) => {
    const wasActive = currentStatus === true || currentStatus === 1;
    const optimisticNext = !wasActive;
    const verbPhrase = optimisticNext ? 'включение' : 'отключение';

    const patchProxyIsActive = (list, nextActive) =>
      list.map((p) => {
        const pid = p?.id ?? p?.proxy_id;
        if (pid === undefined || pid === null) return p;
        if (String(pid) !== String(proxyId)) return p;
        return { ...p, is_active: nextActive };
      });

    setProxies((prev) => patchProxyIsActive(prev, optimisticNext));

    setPowerToggleBanner({
      text: `Выполняется ${verbPhrase} прокси…`,
      tone: 'success',
      busy: true,
      autoHideMs: 0,
    });

    void (async () => {
      try {
        const response = await apiService.toggleProxyStatus(proxyId);

        if (response.data.success) {
          const newStatus = response.data.new_status;

          if (newStatus !== undefined && newStatus !== null) {
            setProxies((prev) => patchProxyIsActive(prev, Boolean(newStatus)));
          }

          void fetchProxiesOnly(null, false).catch(() => {});

          const krakenSync = response.data.kraken_sync;
          const line = newStatus ? 'Прокси включена' : 'Прокси отключена';
          const suffix = krakenSync?.message
            ? (krakenSync.success ? ` ${krakenSync.message}` : ` — ${krakenSync.message}`)
            : '';
          const okKraken = !krakenSync || krakenSync.success !== false;

          setPowerToggleBanner({
            text: `${line}${suffix}`.trim(),
            tone: okKraken ? 'success' : 'error',
            busy: false,
            autoHideMs: 6000,
          });
        } else {
          setProxies((prev) => patchProxyIsActive(prev, wasActive));
          setPowerToggleBanner({
            text: response.data.message || 'Ошибка переключения статуса',
            tone: 'error',
            busy: false,
            autoHideMs: 10000,
          });
        }
      } catch (error) {
        setProxies((prev) => patchProxyIsActive(prev, wasActive));
        logger.error('Ошибка переключения статуса прокси', error);
        setPowerToggleBanner({
          text: 'Ошибка переключения статуса прокси',
          tone: 'error',
          busy: false,
          autoHideMs: 10000,
        });
      }
    })();
  };

  // Функция для проверки прокси (только быстрая проверка доступности)
  const handleCheckProxy = async (proxyId) => {
    const patchProxyRowById = (prev, id, partial) =>
      prev.map((p) => {
        const pid = p?.id ?? p?.proxy_id;
        if (pid === undefined || pid === null) return p;
        if (String(pid) !== String(id)) return p;
        return { ...p, ...partial };
      });

    try {
      // Добавляем прокси в список проверяемых
      setCheckingProxies((prev) => new Set(prev).add(String(proxyId)));

      // Запускаем только быструю проверку доступности
      const response = await apiService.checkProxy(proxyId, 'quick_availability');

      // Показываем результаты (в проекте встречается несколько форматов ответа)
      const result =
        response?.data?.result ||
        response?.data?.data?.result ||
        response?.data?.results?.quick_availability ||
        response?.data?.data?.results?.quick_availability ||
        null;

      const checkedAtRaw =
        response?.data?.data?.checked_at ||
        response?.data?.checked_at ||
        new Date().toISOString();

      if (result) {
        const normalizedStatus = (result.availability_status || result.status || '').toString().toLowerCase();
        if (normalizedStatus === 'available' || normalizedStatus === 'доступен') {
          const ipRaw = result.details?.external_ip || result.details?.note?.match(/\d+\.\d+\.\d+\.\d+/)?.[0];
          const externalIp =
            ipRaw != null && String(ipRaw).trim() !== '' ? String(ipRaw).trim() : null;
          const responseTime = result.response_time ? `${Math.round(result.response_time)} мс` : '';
          const ipPart = externalIp ? `, IP ${externalIp}` : '';
          const responseTimePart = responseTime ? `, отклик ${responseTime}` : '';
          setProxies((prev) =>
            patchProxyRowById(prev, proxyId, {
              availability_status: 'Доступен',
              last_checked: checkedAtRaw,
              quick_check_ip: externalIp,
            })
          );
          setProxyCheckSummary({
            tone: 'success',
            text: `Проверка прокси #${proxyId}: проверено 1, доступно 1, недоступно 0, ошибок 0${ipPart}${responseTimePart}`,
          });
        } else if (normalizedStatus === 'unavailable' || normalizedStatus === 'недоступен') {
          const errorMsg = result.error_message || 'Прокси недоступен';
          setProxies((prev) =>
            patchProxyRowById(prev, proxyId, {
              availability_status: 'Недоступен',
              last_checked: checkedAtRaw,
              quick_check_ip: null,
            })
          );
          setProxyCheckSummary({
            tone: 'error',
            text: `Проверка прокси #${proxyId}: проверено 1, доступно 0, недоступно 1, ошибок 1 (${errorMsg})`,
          });
        } else {
          const errorMsg = result.error_message || 'Ошибка проверки';
          setProxyCheckSummary({
            tone: 'error',
            text: `Проверка прокси #${proxyId}: проверено 1, доступно 0, недоступно 0, ошибок 1 (${errorMsg})`,
          });
        }
      } else {
        setProxyCheckSummary({
          tone: 'error',
          text: `Проверка прокси #${proxyId}: результат не получен`,
        });
      }

      // Обновляем список без полноэкранного оверлея (showLoading: false)
      await fetchProxiesOnly(null, false, null, true);
    } catch (error) {
      logger.error('Ошибка проверки прокси', error);
      if (error.code === 'ECONNABORTED') {
        setProxyCheckSummary({
          tone: 'error',
          text: `Проверка прокси #${proxyId}: превышено время ожидания`,
        });
      } else {
        setProxyCheckSummary({
          tone: 'error',
          text: `Ошибка проверки прокси #${proxyId}: ${error.message}`,
        });
      }
    } finally {
      // Убираем прокси из списка проверяемых
      setCheckingProxies((prev) => {
        const newSet = new Set(prev);
        newSet.delete(String(proxyId));
        return newSet;
      });
    }
  };

  // ОПТИМИЗАЦИЯ: Группировка сообщений о переподключении для уменьшения количества уведомлений
  const reconnectMessagesQueue = useRef([]);
  const reconnectMessagesTimeout = useRef(null);

  const showGroupedReconnectMessage = (messageData) => {
    // Добавляем сообщение в очередь
    reconnectMessagesQueue.current.push(messageData);

    // Очищаем предыдущий таймаут
    if (reconnectMessagesTimeout.current) {
      clearTimeout(reconnectMessagesTimeout.current);
    }

    // Показываем группированное сообщение через 500мс (накапливаем сообщения за это время)
    reconnectMessagesTimeout.current = setTimeout(() => {
      const messages = reconnectMessagesQueue.current;
      reconnectMessagesQueue.current = [];

      if (messages.length === 1) {
        // Одно сообщение - показываем как обычно
        const msg = messages[0];
        message.success({
          content: msg.content,
          duration: 5
        });
      } else {
        // Несколько сообщений - показываем объединенное
        const uniqueModems = new Set(messages.map(m => m.new_server));
        message.success({
          content: `✅ Успешно переподключено ${messages.length} прокси\nНа модемы: ${Array.from(uniqueModems).join(', ')}`,
          duration: 6
        });
      }
    }, 500);
  };

  const handleReconnectProxy = async (proxyId) => {
    let reconnectModal = null;
    let statusInterval = null;

    try {
      // Запускаем переподключение
      const response = await apiService.reconnectProxy(proxyId);

      if (!response.data.success) {
        message.error(response.data.message || 'Ошибка запуска переподключения прокси');
        return;
      }

      // Показываем модальное окно с прогресс-баром
      reconnectModal = Modal.info({
        title: 'Переподключение прокси',
        content: (
          <div>
            <Progress percent={0} status="active" />
            <p style={{ marginTop: 16, textAlign: 'center' }}>Инициализация...</p>
          </div>
        ),
        okText: 'Закрыть',
        okButtonProps: { disabled: true },
        maskClosable: false,
        closable: false,
        width: 500
      });

      // Функция обновления прогресса
      const updateProgress = async () => {
        try {
          const statusResponse = await apiService.getReconnectStatus(proxyId);
          const statusData = statusResponse.data;

          if (reconnectModal) {
            const statusText = {
              'searching': 'Поиск подходящего модема...',
              'connecting': 'Подключение к новому модему...',
              'completed': 'Переподключение завершено',
              'error': 'Ошибка переподключения'
            };

            reconnectModal.update({
              content: (
                <div>
                  <Progress
                    percent={statusData.progress}
                    status={statusData.status === 'error' ? 'exception' : statusData.status === 'completed' ? 'success' : 'active'}
                  />
                  <p style={{ marginTop: 16, textAlign: 'center' }}>
                    {statusData.message || statusText[statusData.status] || 'Обработка...'}
                  </p>
                </div>
              ),
              okButtonProps: {
                disabled: statusData.status !== 'completed' && statusData.status !== 'error'
              }
            });
          }

          // Если операция завершена или произошла ошибка
          if (statusData.status === 'completed') {
            if (statusInterval) {
              clearInterval(statusInterval);
              statusInterval = null;
            }

            // ИСПРАВЛЕНО: Проверяем наличие result и всех необходимых полей
            if (statusData.result && statusData.result.new_server) {
              const data = statusData.result;
              setTimeout(async () => {
                if (reconnectModal) {
                  reconnectModal.destroy();
                }
                // ОПТИМИЗАЦИЯ: Используем группировку сообщений для уменьшения количества уведомлений
                const details = `Старый модем: ${data.old_server || 'неизвестно'} (${data.old_operator || 'неизвестный оператор'})\nНовый модем: ${data.new_server} (${data.new_operator || 'неизвестный оператор'})\nПорт: ${data.old_port || '?'} → ${data.new_port || data.old_port || '?'}`;

                showGroupedReconnectMessage({
                  content: `Прокси успешно переподключена к модему ${data.new_server}\n${details}`,
                  new_server: data.new_server
                });

                // ОПТИМИЗАЦИЯ: После переподключения обновляем только прокси
                await fetchProxiesOnly(null, false);
              }, 500);
            } else if (statusData.status === 'completed') {
              // Если статус completed, но result отсутствует, показываем простое сообщение
              setTimeout(async () => {
                if (reconnectModal) {
                  reconnectModal.destroy();
                }
                showGroupedReconnectMessage({
                  content: 'Прокси успешно переподключена',
                  new_server: 'неизвестно'
                });
                // ОПТИМИЗАЦИЯ: После переподключения обновляем только прокси
                await fetchProxiesOnly(null, false);
              }, 500);
            }
          } else if (statusData.status === 'error') {
            if (statusInterval) {
              clearInterval(statusInterval);
              statusInterval = null;
            }

            setTimeout(() => {
              if (reconnectModal) {
                reconnectModal.destroy();
              }
              message.error(statusData.message || 'Ошибка переподключения прокси');
            }, 2000);
          }
        } catch (statusError) {
          // Если статус не найден, возможно операция завершилась
          if (statusError.response?.status === 404) {
            if (statusInterval) {
              clearInterval(statusInterval);
              statusInterval = null;
            }
            if (reconnectModal) {
              reconnectModal.destroy();
            }
          }
        }
      };

      // ОПТИМИЗАЦИЯ: Уменьшаем интервал опроса до 1 секунды для более быстрой обратной связи
      // На начальных этапах (прогресс < 50%) опрашиваем каждую секунду для быстрого отображения прогресса
      // Запускаем polling статуса каждые 1000мс (1 секунда)
      statusInterval = setInterval(updateProgress, 1000);
      updateProgress(); // Первый запрос сразу

    } catch (error) {
      if (statusInterval) {
        clearInterval(statusInterval);
      }
      if (reconnectModal) {
        reconnectModal.destroy();
      }
      const errorMessage = handleApiError(error);
      message.error(`Ошибка переподключения прокси: ${errorMessage}`);
      logger.error('Ошибка переподключения прокси', error);
    }
  };

  const handleEditProxy = async (proxy) => {
    setEditingProxy(proxy);

    let connectionsData = krakenConnections;
    let serversData = availableServers;

    // Загружаем Kraken данные по требованию (если еще не загружены)
    // чтобы не обращаться к Kraken API при загрузке страницы
    if (krakenConnections.length === 0 || availableServers.length === 0) {
      try {
        const [krakenConnectionsRes, krakenServersRes] = await Promise.all([
          apiService.getActiveKrakenConnections(), // Загружаем из БД без обращения к Kraken API
          apiService.getKrakenServers() // Загружаем из БД без обращения к Kraken API
        ]);
        connectionsData = krakenConnectionsRes.data?.connections || [];
        serversData = krakenServersRes.data?.servers || [];
        setKrakenConnections(connectionsData);
        setAvailableServers(serversData);
      } catch (error) {
        logger.error('Ошибка загрузки данных для редактирования прокси:', error);
        // Продолжаем работу даже при ошибке загрузки
      }
    }

    // Инициализируем фильтрованные списки на основе данных прокси
    const selectedCity = proxy.selected_city || proxy.city;
    const selectedConnectionId = proxy.connection_id ?? proxy.kraken_connection_id;

    // Фильтруем Kraken подключения по городу, если город выбран
    if (selectedCity) {
      const filteredConns = connectionsData.filter(conn => conn.city === selectedCity);
      setFilteredKrakenConnections(filteredConns);
    } else {
      setFilteredKrakenConnections(connectionsData);
    }

    // Фильтруем модемы по Kraken подключению, если оно выбрано
    if (selectedConnectionId) {
      const filteredMods = serversData.filter(server => String(server.kraken_connection_id) === String(selectedConnectionId));
      setFilteredServers(filteredMods);
      try {
        const operatorsRes = await apiService.getAvailableOperators(selectedConnectionId);
        setAvailableOperators(operatorsRes?.data?.operators || []);
      } catch (error) {
        logger.error('Ошибка загрузки операторов при открытии формы редактирования:', error);
        setAvailableOperators([]);
      }
    } else {
      setFilteredServers([]);
      setAvailableOperators([]);
    }

    // ProxyEditPanel инициализирует форму сам из proxy prop
    setEditModalVisible(true);
  };


  const handleUpdateProxy = async (values) => {
    const actualValues = values;
    // #region agent log
    sendAgentDebugLog(
      'H6',
      'Proxies.js:handleUpdateProxy:entry',
      'Старт редактирования прокси',
      {
        editingProxyId: editingProxy?.proxy_id ?? editingProxy?.id ?? null,
        hasValues: Boolean(values && typeof values === 'object'),
        valueKeys: values && typeof values === 'object' ? Object.keys(values) : []
      }
    );
    // #endregion
    const oldCity = String(editingProxy?.selected_city || editingProxy?.city || '').trim();
    const newCity = String(actualValues.city || oldCity).trim();
    const oldConnectionId = editingProxy?.connection_id ?? editingProxy?.kraken_connection_id ?? null;
    const newConnectionId = actualValues.connection_id ?? oldConnectionId;
    const oldServerId = editingProxy?.server_id ?? null;
    const newServerId = actualValues.server_id ?? oldServerId;
    const selectedOperatorValue = String(
      actualValues.mobile_operator ?? editingProxy?.selected_operator ?? editingProxy?.mobile_operator ?? ''
    ).trim();
    const oldOperatorValueForCrossCheck = String(
      editingProxy?.selected_operator ?? editingProxy?.mobile_operator ?? ''
    ).trim();
    const operatorChangedByUser = selectedOperatorValue.toLowerCase() !== oldOperatorValueForCrossCheck.toLowerCase();

    const cityChanged = oldCity !== newCity;
    const connectionChanged = String(oldConnectionId ?? '') !== String(newConnectionId ?? '');
    const serverChanged = String(oldServerId ?? '') !== String(newServerId ?? '');
    let forceCrossConnectionMigration = false;

    if (operatorChangedByUser && selectedOperatorValue && newConnectionId) {
      const connectionServers = availableServers.filter((server) => (
        String(server.kraken_connection_id ?? '') === String(newConnectionId ?? '')
      ));
      const hasOperatorOnConnection = connectionServers.some((server) => (
        operatorsMatch(server.mobile_operator, selectedOperatorValue)
      ));
      if (!hasOperatorOnConnection) {
        forceCrossConnectionMigration = true;
      }
    }

    // Смена только оператора (в т.ч. когда source connection down / нет оператора на нём)
    // идёт через updateProxy → _apply_operator_change (soft-fail + cross-connection),
    // а не через migrate с залипшим selected_city мёртвого города.
    const requiresMigration = cityChanged || connectionChanged;
    const requiresOperatorCrossConfirm = forceCrossConnectionMigration && !requiresMigration;
    if (requiresMigration || requiresOperatorCrossConfirm) {
      const getConnectionLabel = (connId) => {
        if (!connId) return 'не выбран';
        const found = krakenConnections.find(conn => String(conn.id) === String(connId));
        return found ? `${found.name || `ID ${found.id}`}` : `ID ${connId}`;
      };

      const getServerLabel = (srvId) => {
        if (!srvId) return 'не выбран';
        const found = availableServers.find(server => String(server.id) === String(srvId));
        return found ? `${found.name || `ID ${found.id}`}` : `ID ${srvId}`;
      };

      const changedItems = [];
      if (cityChanged) {
        changedItems.push(`Город: ${oldCity || 'не указан'} -> ${newCity || 'не указан'}`);
      }
      if (connectionChanged) {
        changedItems.push(`Сервер (подключение): ${getConnectionLabel(oldConnectionId)} -> ${getConnectionLabel(newConnectionId)}`);
      }
      if (serverChanged) {
        changedItems.push(`Модем: ${getServerLabel(oldServerId)} -> ${getServerLabel(newServerId)}`);
      }
      if (forceCrossConnectionMigration) {
        changedItems.push(
          `Оператор "${selectedOperatorValue}" отсутствует на текущем сервере. ` +
          'Прокси будет создан на другом сервере, старый прокси будет удалён.'
        );
      }

      const confirmed = await new Promise((resolve) => {
        Modal.confirm({
          title: 'Подтверждение миграции прокси',
          okText: 'Подтвердить',
          cancelText: 'Отмена',
          content: (
            <div>
              <p>Будут изменены поля:</p>
              <ul style={{ paddingLeft: 18, marginBottom: 10 }}>
                {changedItems.map(item => <li key={item}>{item}</li>)}
              </ul>
              <p style={{ marginBottom: 0 }}>
                Будет создан новый прокси с данными старого 1в1, и только после успешного создания старый прокси будет удалён.
              </p>
            </div>
          ),
          onOk: () => resolve(true),
          onCancel: () => resolve(false)
        });
      });

      if (!confirmed) {
        return;
      }
    }

    setUpdatingProxy(true);
    try {
      // ИСПРАВЛЕНО: ВСЕГДА используем values из onFinish - это актуальные значения формы
      // onFinish автоматически передает все значения полей формы при отправке

      // Диагностика в production-режиме через logger
      logger.debug('Обновление прокси', {
        slimiter: values.slimiter,
        rlimiter: values.rlimiter,
        maxconn: values.maxconn
      });

      // ВАЖНО: Используем values из onFinish - это актуальные значения, введенные пользователем
      // getFieldsValue() может возвращать устаревшие значения
      // Преобразуем поля формы в формат, ожидаемый API
      // ИСПРАВЛЕНО: Используем ту же логику, что и при массовом редактировании - добавляем поля только если они определены
      const updateData = {};
      const oldCityValue = editingProxy?.selected_city ?? editingProxy?.city ?? null;
      const oldOperatorValue = editingProxy?.selected_operator ?? editingProxy?.mobile_operator ?? null;
      const oldConnectionValue = editingProxy?.connection_id ?? editingProxy?.kraken_connection_id ?? null;
      const oldServerValue = editingProxy?.server_id ?? null;
      const oldTariffValue = editingProxy?.tariff_plan_name ?? editingProxy?.tariff_plan ?? null;
      const editingProxyExpires = editingProxy?._original?.expires_at ?? editingProxy?.expires_at;
      const oldExpiresDateValue = (editingProxyExpires != null && String(editingProxyExpires).trim() !== '')
        ? String(editingProxyExpires).replace(/\s/g, 'T').slice(0, 10)
        : null;

      const normalizeText = (value) => {
        if (value === undefined || value === null) return '';
        return String(value).trim();
      };
      const normalizeNum = (value) => {
        if (value === undefined || value === null || value === '') return null;
        const n = Number(value);
        return Number.isFinite(n) ? n : null;
      };
      const normalizeBool = (value) => {
        if (value === undefined || value === null) return null;
        if (typeof value === 'boolean') return value;
        if (typeof value === 'number') return value !== 0;
        if (typeof value === 'string') {
          const normalized = value.trim().toLowerCase();
          if (normalized === '') return null;
          if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
          if (['false', '0', 'no', 'off'].includes(normalized)) return false;
        }
        return Boolean(value);
      };
      const isTextChanged = (newValue, oldValue) => normalizeText(newValue) !== normalizeText(oldValue);
      const isNumChanged = (newValue, oldValue) => normalizeNum(newValue) !== normalizeNum(oldValue);
      const isBoolChanged = (newValue, oldValue) => {
        const next = normalizeBool(newValue);
        const prev = normalizeBool(oldValue);
        if (next === null) return false;
        return next !== prev;
      };

      // ИСПРАВЛЕНО: ВСЕГДА отправляем значения лимитеров из values (onFinish)
      // values содержит актуальные значения, введенные пользователем

      // slimiter - отправляем значение из формы
      if (actualValues.slimiter !== undefined && actualValues.slimiter !== null) {
        // Конвертируем Мб/с в КБайт/с для Kraken API
        const nextSlimiter = actualValues.slimiter > 0 ? convertMbpsToKBytes(actualValues.slimiter) : 0;
        if (isNumChanged(nextSlimiter, editingProxy?.slimiter)) {
          updateData.slimiter = nextSlimiter;
        }
      }

      // rlimiter - отправляем значение из формы
      if (actualValues.rlimiter !== undefined && actualValues.rlimiter !== null) {
        const nextRlimiter = actualValues.rlimiter > 0 ? actualValues.rlimiter : 0;
        if (isNumChanged(nextRlimiter, editingProxy?.rlimiter)) {
          updateData.rlimiter = nextRlimiter;
        }
      }

      // maxconn - отправляем значение из формы
      if (actualValues.maxconn !== undefined && actualValues.maxconn !== null) {
        const nextMaxconn = actualValues.maxconn > 0 ? actualValues.maxconn : 0;
        if (isNumChanged(nextMaxconn, editingProxy?.maxconn)) {
          updateData.maxconn = nextMaxconn;
        }
      }
      if (actualValues.port !== undefined) {
        const nextPort = actualValues.port || undefined;
        if (isNumChanged(nextPort, editingProxy?.port)) {
          updateData.port = nextPort;
        }
      }
      if (actualValues.server_id !== undefined) {
        const nextServer = actualValues.server_id || undefined;
        if (isTextChanged(nextServer, oldServerValue)) {
          updateData.server_id = nextServer;
        }
      }
      if (actualValues.connection_id !== undefined) {
        const nextConnection = actualValues.connection_id || undefined;
        if (isTextChanged(nextConnection, oldConnectionValue)) {
          updateData.connection_id = nextConnection;
        }
      }
      if (actualValues.proxy_type !== undefined) {
        const nextProxyType = actualValues.proxy_type || undefined;
        if (isTextChanged(nextProxyType, editingProxy?.proxy_type)) {
          updateData.proxy_type = nextProxyType;
        }
      }
      if (Object.prototype.hasOwnProperty.call(actualValues, 'expires_at')) {
        const nextExpiresAt = actualValues.expires_at;
        const nextExpiresNorm = (nextExpiresAt != null && String(nextExpiresAt).trim() !== '')
          ? String(nextExpiresAt).replace(/\s/g, 'T').slice(0, 10)
          : null;
        const explicitlyUnlimited = (nextExpiresAt == null || String(nextExpiresAt || '').trim() === '');
        const hadExpiry = (oldExpiresDateValue != null && String(oldExpiresDateValue).trim() !== '');
        const changed = (explicitlyUnlimited && hadExpiry) || (!explicitlyUnlimited && nextExpiresNorm !== oldExpiresDateValue);
        if (changed) {
          updateData.expires_at = nextExpiresAt; // null = Безлимит
        }
      } else if (actualValues.expires_days !== undefined) {
        updateData.expires_days = actualValues.expires_days;
      }
      if (actualValues.city !== undefined) {
        if (isTextChanged(actualValues.city, oldCityValue)) {
          updateData.selected_city = actualValues.city; // Преобразуем city в selected_city
        }
      }
      if (actualValues.mobile_operator !== undefined) {
        if (isTextChanged(actualValues.mobile_operator, oldOperatorValue)) {
          updateData.selected_operator = actualValues.mobile_operator; // Преобразуем mobile_operator в selected_operator
        }
      }
    if (Object.prototype.hasOwnProperty.call(actualValues, 'modem_reconnect_type')) {
      const nextReconnectType = actualValues.modem_reconnect_type || null;
      if (isTextChanged(nextReconnectType, editingProxy?.modem_reconnect_type ?? null)) {
        updateData.modem_reconnect_type = nextReconnectType;
      }
    }
    if (Object.prototype.hasOwnProperty.call(actualValues, 'modem_reconnect_interval')) {
      const nextReconnectInterval = actualValues.modem_reconnect_interval ?? null;
      if (isNumChanged(nextReconnectInterval, editingProxy?.modem_reconnect_interval ?? null)) {
        updateData.modem_reconnect_interval = nextReconnectInterval;
      }
    }
    if (Object.prototype.hasOwnProperty.call(actualValues, 'modem_osfp_id')) {
      const nextOsfpId = actualValues.modem_osfp_id ?? null;
      if (isTextChanged(nextOsfpId, editingProxy?.modem_osfp_id ?? null)) {
        updateData.modem_osfp_id = nextOsfpId;
      }
    }
    if (Object.prototype.hasOwnProperty.call(actualValues, 'modem_is_osfp')) {
      if (isBoolChanged(actualValues.modem_is_osfp, editingProxy?.modem_is_osfp)) {
        updateData.modem_is_osfp = actualValues.modem_is_osfp;
      }
    }
      if (actualValues.is_active !== undefined) {
        if (isBoolChanged(actualValues.is_active, editingProxy?.is_active)) {
          updateData.is_active = actualValues.is_active;
        }
      }
      if (actualValues.auto_replace_enabled !== undefined) {
        if (isBoolChanged(actualValues.auto_replace_enabled, editingProxy?.auto_replace_enabled)) {
          updateData.auto_replace_enabled = actualValues.auto_replace_enabled;
        }
      }
      if (actualValues.reconnect_link_enabled !== undefined) {
        if (isBoolChanged(actualValues.reconnect_link_enabled, editingProxy?.reconnect_link_enabled)) {
          updateData.reconnect_link_enabled = actualValues.reconnect_link_enabled;
        }
      }
      // Отправляем keepalive/compression только если реально изменились
      if (actualValues.is_keepalive !== undefined) {
        if (isBoolChanged(actualValues.is_keepalive, editingProxy?.is_keepalive)) {
          updateData.is_keepalive = actualValues.is_keepalive;
        }
      }
      if (actualValues.is_compression !== undefined) {
        if (isBoolChanged(actualValues.is_compression, editingProxy?.is_compression)) {
          updateData.is_compression = actualValues.is_compression;
        }
      }

      const kc = editingProxy?.kraken_characteristics || {};
      if (actualValues.kraken_is_allow !== undefined) {
        if (Object.prototype.hasOwnProperty.call(kc, 'is_allow')) {
          if (isBoolChanged(actualValues.kraken_is_allow, kc.is_allow)) {
            updateData.kraken_is_allow = actualValues.kraken_is_allow;
          }
        } else {
          updateData.kraken_is_allow = actualValues.kraken_is_allow;
        }
      }
      if (actualValues.kraken_is_ports !== undefined) {
        if (Object.prototype.hasOwnProperty.call(kc, 'is_ports')) {
          if (isBoolChanged(actualValues.kraken_is_ports, kc.is_ports)) {
            updateData.kraken_is_ports = actualValues.kraken_is_ports;
          }
        } else {
          updateData.kraken_is_ports = actualValues.kraken_is_ports;
        }
      }
      if (actualValues.kraken_allow !== undefined) {
        const nextAllow = Array.isArray(actualValues.kraken_allow)
          ? actualValues.kraken_allow.map((x) => parseInt(x, 10)).filter((n) => Number.isFinite(n))
          : parseKrakenIntList(actualValues.kraken_allow);
        if (!krakenIdListsEqual(nextAllow, kc.allow)) {
          updateData.kraken_allow = nextAllow;
        }
      }
      if (actualValues.kraken_targetport !== undefined) {
        const nextTp = Array.isArray(actualValues.kraken_targetport)
          ? actualValues.kraken_targetport.map((x) => parseInt(x, 10)).filter((n) => Number.isFinite(n))
          : parseKrakenIntList(actualValues.kraken_targetport);
        if (!krakenIdListsEqual(nextTp, kc.targetport)) {
          updateData.kraken_targetport = nextTp;
        }
      }

      // Смена города/сервера/подключения: всегда тащим текущие is_allow/is_ports.
      // Даже если тумблеры не меняли — иначе после migrate они включаются.
      if (requiresMigration || serverChanged || forceCrossConnectionMigration) {
        if (actualValues.kraken_is_allow !== undefined) {
          updateData.kraken_is_allow = actualValues.kraken_is_allow;
        }
        if (actualValues.kraken_is_ports !== undefined) {
          updateData.kraken_is_ports = actualValues.kraken_is_ports;
        }
      }

      if (actualValues.tariff_plan_name !== undefined && actualValues.tariff_plan_name !== null && actualValues.tariff_plan_name !== '') {
        if (isTextChanged(actualValues.tariff_plan_name, oldTariffValue)) {
          updateData.tariff_plan_name = actualValues.tariff_plan_name;
        }
      }

      // Только при реальной migrate (город/подключение): не фиксируем мёртвый connection.
      // При operator-only cross оставляем connection_id — backend сам уйдёт на другой Kraken.
      if (forceCrossConnectionMigration && requiresMigration) {
        updateData.connection_id = null;
        updateData.server_id = null;
      }

      // Для migrateProxy город обязателен всегда: если сервер/подключение меняется,
      // передаем текущий город даже если пользователь его явно не изменял.
      if (requiresMigration && !Object.prototype.hasOwnProperty.call(updateData, 'selected_city')) {
        const fallbackCity = normalizeText(actualValues.city || oldCityValue);
        if (fallbackCity) {
          updateData.selected_city = fallbackCity;
        } else {
          message.error('Не указан город для переноса прокси');
          setUpdatingProxy(false);
          return;
        }
      }

      // При миграции всегда переносим дату окончания действия прокси
      if (requiresMigration && !Object.prototype.hasOwnProperty.call(updateData, 'expires_at')) {
        const migrationExpires = Object.prototype.hasOwnProperty.call(actualValues, 'expires_at')
          ? actualValues.expires_at
          : (editingProxyExpires != null && String(editingProxyExpires).trim() !== '')
            ? String(editingProxyExpires).replace(/\s/g, 'T').slice(0, 19)
            : null;
        updateData.expires_at = migrationExpires;
      }

      if (Object.keys(updateData).length === 0) {
        // #region agent log
        sendAgentDebugLog(
          'H6',
          'Proxies.js:handleUpdateProxy:noChanges',
          'Редактирование завершено без изменений',
          {
            editingProxyId: editingProxy?.proxy_id ?? editingProxy?.id ?? null
          }
        );
        // #endregion
        message.info('Нет изменений для сохранения');
        setUpdatingProxy(false);
        return;
      }

      logger.debug('Данные для отправки в API', {
        updateData,
        slimiter_in_updateData: updateData.slimiter,
        actualValues_slimiter: actualValues.slimiter,
        values_slimiter: values.slimiter
      });

      const executeUpdateRequest = async (proxyId) => (
        requiresMigration
          ? apiService.migrateProxy(proxyId, updateData)
          : apiService.updateProxy(proxyId, updateData)
      );

      let targetProxyId = editingProxy?.proxy_id ?? editingProxy?.id;
      let response;
      try {
        response = await executeUpdateRequest(targetProxyId);
      } catch (requestError) {
        const shouldRetryWithResolvedId = (
          requestError?.response?.status === 404 &&
          editingProxy?.kraken_proxy_id
        );
        if (!shouldRetryWithResolvedId) {
          if (requestError?.response?.status === 404) {
            message.warning('Прокси был удалён или перенесён. Список обновлён.');
            setEditModalVisible(false);
            setEditingProxy(null);
            setSelectedRowKeys([]);
            setSelectionResetToken((prev) => prev + 1);
            try { await fetchProxiesOnly(null, false); } catch (_) { /* ignore */ }
            setUpdatingProxy(false);
            return;
          }
          throw requestError;
        }

        const refreshed = await apiService.getProxiesPaginated({
          limit: 100000,
          offset: 0,
          forceRefresh: true
        });
        const freshProxies = Array.isArray(refreshed?.data)
          ? refreshed.data
          : (Array.isArray(refreshed?.data?.proxies) ? refreshed.data.proxies : []);
        const resolvedProxy = freshProxies.find((p) => (
          String(p?.kraken_proxy_id ?? '') === String(editingProxy?.kraken_proxy_id ?? '')
        ));
        const resolvedProxyId = resolvedProxy?.proxy_id ?? resolvedProxy?.id ?? null;

        if (!resolvedProxyId) {
          message.warning('Прокси был удалён или перенесён. Список обновлён.');
          setEditModalVisible(false);
          setEditingProxy(null);
          setSelectedRowKeys([]);
          setSelectionResetToken((prev) => prev + 1);
          try { await fetchProxiesOnly(null, false); } catch (_) { /* ignore */ }
          setUpdatingProxy(false);
          return;
        }

        targetProxyId = resolvedProxyId;
        response = await executeUpdateRequest(targetProxyId);
      }

      if (requiresMigration) {
        if (response?.data?.warning) {
          message.warning(response.data.warning);
        } else {
          message.success('Новый прокси успешно создан, старый удалён');
        }
      } else {
        if (response?.data?.warning) {
          message.warning(response.data.warning);
        }
        message.success('Прокси успешно обновлена');
      }

      setEditModalVisible(false);
      setEditingProxy(null);
      setSelectedRowKeys([]);
      setSelectionResetToken((prev) => prev + 1);

      const filtersSnapshot = filteredInfoRef.current || {};
      await fetchProxiesOnly(filtersSnapshot, false, paginationRef.current, true);
      if (
        updateData.kraken_is_allow !== undefined
        || updateData.kraken_is_ports !== undefined
        || updateData.kraken_allow !== undefined
        || updateData.kraken_targetport !== undefined
      ) {
        setProxies((prev) => patchProxiesKrakenPermissions(prev, [targetProxyId], updateData));
      }
      fetchFilterValues(filtersSnapshot);
      // #region agent log
      sendAgentDebugLog(
        'H6',
        'Proxies.js:handleUpdateProxy:success',
        'Редактирование успешно завершено и таблица перезагружена',
        {
          editingProxyId: targetProxyId,
          updatedFieldsCount: Object.keys(updateData).length
        }
      );
      // #endregion
    } catch (error) {
      if (requiresMigration && error?.response?.status === 409) {
        message.warning(
          error?.response?.data?.detail
            || 'Миграция этой прокси уже выполняется. Дождитесь завершения.'
        );
        setUpdatingProxy(false);
        return;
      }
      // #region agent log
      sendAgentDebugLog(
        'H6',
        'Proxies.js:handleUpdateProxy:error',
        'Ошибка редактирования прокси',
        {
          editingProxyId: editingProxy?.proxy_id ?? editingProxy?.id ?? null,
          message: error?.message || 'unknown'
        }
      );
      // #endregion
      message.error(handleApiError(error));
    } finally {
      setUpdatingProxy(false);
    }
  };

  // const handleSyncProxies = async () => {
  //   try {
  //     setSyncing(true);
  //     const response = await apiService.syncKrakenProxies();
  //     message.success(`Синхронизировано: ${response.data.result.synced_count} новых прокси, обновлено: ${response.data.result.updated_count}`);
  //     fetchData();
  //   } catch (error) {
  //     message.error(error.response?.data?.detail || 'Ошибка синхронизации прокси');
  //   } finally {
  //     setSyncing(false);
  //   }
  // };

  const handleForceSync = useCallback(async () => {
    try {
      setSyncing(true);
      // Согласно архитектурным принципам: двусторонняя синхронизация
      // 1. Сначала получаем актуальные данные из Kraken
      const syncResponse = await apiService.syncProxiesWithKraken();
      const syncResult = syncResponse.data;

      // НОВОЕ: Сохраняем статистику для отображения в модальном окне
      setSyncStats(syncResult);

      const failedConnections = syncResult.summary?.connections_failed || 0;
      const syncErrors = syncResult.summary?.errors || 0;
      if (syncResult.status === 'partial' || failedConnections > 0 || syncErrors > 0) {
        message.warning(
          `Синхронизация из Kraken завершена частично: ошибок ${syncErrors}, подключений с ошибкой ${failedConnections}`
        );
      }

      let pushedCount = 0;
      try {
        const pendingResponse = await apiService.getPendingKrakenPushCount();
        const pendingCount = pendingResponse.data?.pending_count ?? 0;
        if (pendingCount > 0) {
          const toKrakenResponse = await apiService.syncToKraken();
          const toKrakenResult = toKrakenResponse.data?.result;
          pushedCount = toKrakenResult?.proxies?.synced_count ?? 0;
        }
      } catch (toKrakenError) {
        logger.warn('Ошибка синхронизации в Kraken:', toKrakenError);
        const detail = handleApiError(toKrakenError);
        message.warning(
          `Синхронизация из Kraken завершена, но отправка в Kraken завершилась с ошибкой: ${detail}`
        );
      }

      const deletedCount = syncResult.summary?.deleted || 0;
      const noUpdateNeeded = syncResult.summary?.no_update_needed || 0;
      const deletedMessage = deletedCount > 0 ? `, удалено ${deletedCount}` : '';
      const noUpdateMessage = noUpdateNeeded > 0 ? `, не требовало обновления ${noUpdateNeeded}` : '';
      const pushMessage = pushedCount > 0 ? `, отправлено ${pushedCount} в Kraken` : '';
      message.success(
        `Синхронизация завершена: получено ${syncResult.summary?.added || 0} добавлено, ${syncResult.summary?.updated || 0} обновлено${noUpdateMessage}${deletedMessage}${pushMessage}`
      );

      // НОВОЕ: Открываем модальное окно с детальной статистикой
      setSyncStatsVisible(true);

      // ИСПРАВЛЕНО: передаем forceRefresh: true для обновления данных после синхронизации
      fetchData(true);
    } catch (error) {
      logger.error('Ошибка синхронизации прокси:', error);
      message.error(handleApiError(error));
    } finally {
      setSyncing(false);
    }
  }, [fetchData]);

  // ==================== FILTER OPTIONS для ProxyTable ====================
  const proxyTableFilterOptions = useMemo(() => {
    const operatorOptionsRaw = Array.isArray(filterValues.selected_operator) ? filterValues.selected_operator : [];
    const canonicalOperators = Array.from(
      new Set(
        operatorOptionsRaw
          .map((operatorValue) => resolveOperatorByTemplates(operatorValue, operatorTemplates))
          .map((operatorValue) => normalizeOperatorFilterDisplayValue(operatorValue))
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b, 'ru'));

    return {
      client: filterValues.client_login || [],
      tariff: filterValues.tariff_plan || [],
      availability: filterValues.availability_status || [],
      guard: filterValues.auto_replace_enabled || [],
      server: filterValues.server_name || [],
      modem: filterValues.modem || [],
      port: filterValues.port || [],
      type: filterValues.proxy_type || [],
      geo: filterValues.selected_city || [],
      operator: canonicalOperators,
      rotation: filterValues.modem_reconnect_type || [],
      osFingerprint: filterValues.modem_osfp || [],
    };
  }, [filterValues, operatorTemplates]);

  // ==================== MAPPING для ProxyTable ====================
  const proxyTableData = useMemo(() => {
    const mapped = safeProxies.map((p) => ({
      id: p.id,
      client: p.client_login || '',
      tariff: p.tariff_plan || '',
      availability: p.availability_status || 'Нет данных',
      guard: p.auto_replace_enabled ? 'Вкл' : 'Выкл',
      server: p.server_name || '',
      modem: (() => {
        const name = p.modem_name || '';
        if (name.startsWith('№')) return name;
        const oct = (p.server_ip || '').split('.')[2];
        return oct ? `№ ${oct} ${name}` : name;
      })(),
      port: p.port || '',
      type: p.proxy_type || 'HTTP',
      geo: p.selected_city || '',
      operator: p.selected_operator || '',
      rotation: p.modem_reconnect_type || 'none',
      rotationType: p.modem_reconnect_type || '',
      rotationTimer: p.modem_reconnect_interval ? `${p.modem_reconnect_interval}s` : '',
      osFingerprint: resolveOsFingerprintColumnDisplay(p, osfpList),
      _original: p,
    }));

    // #region agent log
    sendAgentDebugLog(
      'H4',
      'Proxies.js:proxyTableData:mapped',
      'Состояние данных после маппинга для таблицы',
      {
        mappedCount: mapped.length,
        withModemColumnValue: mapped.filter((item) => Boolean(item?.modem)).length,
        withRotationColumnValue: mapped.filter((item) => Boolean(item?.rotationType)).length,
        withOsfpColumnValue: mapped.filter((item) => item?.osFingerprint && item.osFingerprint !== '—').length,
        withKeepAliveEnabled: mapped.filter((item) => item?._original?.is_keepalive === true || item?._original?.is_keepalive === 1).length,
        withCompressionEnabled: mapped.filter((item) => item?._original?.is_compression === true || item?._original?.is_compression === 1).length,
        withRotationWhenGuardOff: mapped.filter((item) => {
          const guardEnabled = item?._original?.auto_replace_enabled === true || item?._original?.auto_replace_enabled === 1;
          return !guardEnabled && Boolean(item?.rotationType);
        }).length
      }
    );
    // #endregion

    if (!sortedInfo.columnKey) return mapped;

    const colKeyMap = {
      client_login: 'client',
      tariff_plan: 'tariff',
      availability_status: 'availability',
      auto_replace_enabled: 'guard',
      server_name: 'server',
      modem: 'modem',
      port: 'port',
      proxy_type: 'type',
      selected_city: 'geo',
      selected_operator: 'operator',
      modem_reconnect_type: 'rotation',
      modem_osfp: 'osFingerprint',
      expires_at: 'expires_at',
    };
    const sortKey = colKeyMap[sortedInfo.columnKey] || sortedInfo.columnKey;
    const dir = sortedInfo.order === 'descend' ? -1 : 1;

    return [...mapped].sort((a, b) => {
      if (sortKey === 'expires_at') {
        const parseExp = (row) => {
          const raw = row?._original?.expires_at;
          if (raw == null || String(raw).trim() === '') return null;
          const t = new Date(String(raw).replace(' ', 'T')).getTime();
          return Number.isNaN(t) ? null : t;
        };
        const at = parseExp(a);
        const bt = parseExp(b);
        if (at == null && bt == null) return 0;
        if (at == null) return 1;
        if (bt == null) return -1;
        return dir * (at - bt);
      }
      const aVal = a[sortKey] ?? '';
      const bVal = b[sortKey] ?? '';
      if (sortKey === 'port') return dir * ((Number(aVal) || 0) - (Number(bVal) || 0));
      return dir * String(aVal).localeCompare(String(bVal), 'ru');
    });
  }, [safeProxies, sortedInfo, osfpList]);

  const proxyTableStats = useMemo(() => {
    const cities = Array.isArray(filterValues.selected_city_counts)
      ? filterValues.selected_city_counts
        .map((item) => ({
          name: item?.name || item?.value || '',
          count: Number(item?.count || 0),
        }))
        .filter((item) => item.name)
      : [];

    const modemAvailable = proxyPanelExtraStats?.available_modems ?? statusData?.available_modems ?? 0;
    const modemUnavailable = proxyPanelExtraStats?.unavailable_modems ?? statusData?.unavailable_modems ?? 0;

    const nextStats = {
      total: statusData?.total_proxies || pagination.total || 0,
      available: statusData?.available_proxies || 0,
      unavailable: statusData?.unavailable_proxies || 0,
      modemAvailable,
      modemUnavailable,
      cities,
    };

    if (hasCalculatedStats({
      total_proxies: nextStats.total,
      available_proxies: nextStats.available,
      unavailable_proxies: nextStats.unavailable,
      available_modems: nextStats.modemAvailable,
      unavailable_modems: nextStats.modemUnavailable,
    })) {
      lastProxyTableStatsRef.current = nextStats;
      return nextStats;
    }

    return lastProxyTableStatsRef.current || nextStats;
  }, [
    statusData,
    filterValues.selected_city_counts,
    pagination.total,
    proxyPanelExtraStats,
    hasCalculatedStats,
  ]);

  const handleProxyTableSort = useCallback((field, direction) => {
    // Маппинг полей ProxyTable -> ключей Ant Design
    const fieldMap = {
      client: 'client_login',
      tariff: 'tariff_plan',
      availability: 'availability_status',
      guard: 'auto_replace_enabled',
      server: 'server_name',
      modem: 'modem',
      port: 'port',
      type: 'proxy_type',
      geo: 'selected_city',
      operator: 'selected_operator',
      rotation: 'modem_reconnect_type',
      osFingerprint: 'modem_osfp',
      expires_at: 'expires_at',
    };
    const columnKey = fieldMap[field] || field;
    setSortedInfo({ columnKey, order: direction === 'asc' ? 'ascend' : 'descend' });
  }, []);

  const handleProxyTableFilter = useCallback(async (column, values) => {
    const fieldMap = {
      client: 'client_login',
      tariff: 'tariff_plan',
      availability: 'availability_status',
      guard: 'auto_replace_enabled',
      server: 'server_name',
      modem: 'modem',
      port: 'port',
      type: 'proxy_type',
      geo: 'selected_city',
      operator: 'selected_operator',
      rotation: 'modem_reconnect_type',
      status: 'is_active',
      osFingerprint: 'modem_osfp',
      expires_at: 'expires_at',
    };
    const filterKey = fieldMap[column] || column;
    let normalizedValues = Array.isArray(values) ? values : [];

    if (column === 'operator') {
      const availableOperatorValues = Array.isArray(filterValues.selected_operator) ? filterValues.selected_operator : [];
      const expandedRawValues = normalizedValues.flatMap((selectedValue) => {
        const selectedNormalized = normalizeOperatorFilterDisplayValue(selectedValue);
        if (!selectedNormalized) return [];
        const selectedCanonical = resolveOperatorByTemplates(selectedNormalized, operatorTemplates);
        return availableOperatorValues.filter((rawOperatorValue) => {
          const rawCanonical = normalizeOperatorFilterDisplayValue(resolveOperatorByTemplates(rawOperatorValue, operatorTemplates));
          return operatorsMatch(rawCanonical, selectedCanonical);
        });
      });
      normalizedValues = Array.from(new Set(expandedRawValues.length > 0 ? expandedRawValues : normalizedValues));
    }

    const newFilters = { ...filteredInfoRef.current, [filterKey]: normalizedValues.length > 0 ? normalizedValues : null };
    const newPagination = { ...paginationRef.current, current: 1 };

    paginationUpdateRef.current = true;
    setPagination(newPagination);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', '1');
    setSearchParams(newSearchParams, { replace: true });

    // Синхронизируем ref сразу, чтобы пагинация всегда видела актуальные фильтры
    filteredInfoRef.current = newFilters;
    setFilteredInfo(newFilters);
    fetchFilterValues(newFilters);
    await fetchProxiesOnly(newFilters, true, newPagination);
  }, [searchParams, setSearchParams, fetchProxiesOnly, fetchFilterValues, filterValues.selected_operator, operatorTemplates]);

  const handleClearAllTableFilters = useCallback(async () => {
    const emptyFilters = {};
    const newPagination = { ...paginationRef.current, current: 1 };

    paginationUpdateRef.current = true;
    setPagination(newPagination);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', '1');
    newSearchParams.delete('proxy_id');
    setSearchParams(newSearchParams, { replace: true });

    // Синхронизируем ref сразу, чтобы пагинация/refresh не использовали старые фильтры
    filteredInfoRef.current = emptyFilters;
    setFilteredInfo(emptyFilters);
    fetchFilterValues(emptyFilters);
    await fetchProxiesOnly(emptyFilters, true, newPagination, true, null);
  }, [searchParams, setSearchParams, fetchProxiesOnly, fetchFilterValues]);

  const handleProxyTablePageChange = useCallback(async (page) => {
    const newPagination = { ...paginationRef.current, current: page };
    paginationUpdateRef.current = true;
    setPagination(newPagination);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', page.toString());
    setSearchParams(newSearchParams, { replace: true });
    // При смене страницы всегда передаём активные фильтры явно
    await fetchProxiesOnly(filteredInfoRef.current || {}, true, newPagination);
  }, [searchParams, setSearchParams, fetchProxiesOnly]);

  const handleProxyTablePageSizeChange = useCallback(async (size) => {
    const newPagination = { ...paginationRef.current, current: 1, pageSize: size };
    paginationUpdateRef.current = true;
    setPagination(newPagination);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', '1');
    newSearchParams.set('pageSize', size.toString());
    setSearchParams(newSearchParams, { replace: true });
    // При смене размера страницы всегда передаём активные фильтры явно
    await fetchProxiesOnly(filteredInfoRef.current || {}, true, newPagination);
  }, [searchParams, setSearchParams, fetchProxiesOnly]);

  const tableActiveFilters = useMemo(() => {
    const proxyIdFromUrl = searchParams.get('proxy_id');
    if (!proxyIdFromUrl || String(proxyIdFromUrl).trim() === '') {
      return filteredInfo;
    }
    return {
      ...(filteredInfo || {}),
      __proxy_id: [String(proxyIdFromUrl)],
    };
  }, [filteredInfo, searchParams]);

  const handleProxyTableRowAction = useCallback((row) => {
    const proxy = row._original || safeProxies.find((p) => p.id === row.id);
    if (proxy) {
      setSelectedProxy(proxy);
    }
  }, [safeProxies]);

  const handleProxyTableAdd = useCallback(async () => {
    logger.debug('Нажата кнопка создания прокси');
    setModalVisible(true);
    try {
      const [availableOperatorsRes, krakenConnectionsRes, krakenServersRes] = await Promise.all([
        apiService.getAvailableOperators(),
        apiService.getActiveKrakenConnections(),
        apiService.getKrakenServers()
      ]);
      const freshOperators = availableOperatorsRes.data?.operators || [];
      setAvailableOperators(freshOperators);
      setAllAvailableOperators(freshOperators);
      setKrakenConnections(krakenConnectionsRes.data?.connections || []);
      setAvailableServers(krakenServersRes.data?.servers || []);
      form.setFieldsValue({ connection_id: '', server_id: undefined });
    } catch (error) {
      logger.error('Ошибка загрузки данных для создания прокси:', error);
    }
  }, [form]);

  const handleProxyTableSelectionChange = useCallback((selected) => {
    const normalizedSelectedIds = (Array.isArray(selected) ? selected : [])
      .map((row) => getProxyKey(row))
      .filter((proxyId) => proxyId !== null && proxyId !== undefined);
    setSelectedRowKeys([...new Set(normalizedSelectedIds)]);
  }, [getProxyKey]);

  const handleProxyTableSync = useCallback(() => {
    handleForceSync();
  }, [handleForceSync]);

  const handleProxyTableRefresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  const handleProxyTableExport = useCallback(() => {
    setExportVisible(true);
  }, []);

  const handleCloseExportModal = useCallback(() => {
    setExportVisible(false);
    setExportResult('');
    setExporting(false);
    setExportScope('selected');
    setExportClient('');
    setExportVarsOpen(false);
    setCustomPattern('');
    setSelectedTemplate(undefined);
    setInlineTplMode(false);
    setInlineTplName('');
  }, []);

  const fetchOsfpListForTable = useCallback(async () => {
    if (osfpList.length > 0) {
      return;
    }

    let connectionId = krakenConnections[0]?.id;
    if (!connectionId) {
      try {
        const krakenConnectionsRes = await apiService.getKrakenConnections();
        const connections = krakenConnectionsRes.data?.connections || [];
        if (connections.length > 0) {
          setKrakenConnections(connections);
          connectionId = connections[0]?.id;
        }
      } catch (err) {
        logger.error('Ошибка загрузки подключений Kraken для OSFP:', err);
        return;
      }
    }

    if (!connectionId) {
      return;
    }

    try {
      const res = await apiService.getKrakenOsfpList(connectionId);
      const list = res.data?.osfp_list || res.data?.data || res.data || [];
      setOsfpList(Array.isArray(list) ? list : []);
    } catch (err) {
      logger.error('Ошибка загрузки списка TCP отпечатков для таблицы:', err);
    }
  }, [krakenConnections, osfpList.length]);

  const fetchOsfpListForSandbox = useCallback(async () => {
    await fetchOsfpListForTable();
  }, [fetchOsfpListForTable]);

  useEffect(() => {
    void fetchOsfpListForTable();
  }, [fetchOsfpListForTable]);

  const fetchSandboxStats = useCallback(async (filters = null) => {
    try {
      const apiFilters = convertFiltersToApiParams(filters || filteredInfoRef.current || {});
      const res = await apiService.getSandboxStats(apiFilters);
      if (res.data && res.data.success) {
        const rawStats = res.data.data || {};
        const rawOperatorStats = Array.isArray(rawStats.operator) ? rawStats.operator : [];
        const knownOperators = Array.isArray(filterValues.selected_operator) ? filterValues.selected_operator : [];
        const normalizedOperatorStats = rawOperatorStats.map((item) => {
          const rawValue = String(item?.value || '').trim();
          const matchedOperator = knownOperators.find((operatorName) => operatorsMatch(rawValue, operatorName));
          const canonicalValue = matchedOperator || resolveOperatorByTemplates(rawValue, operatorTemplates);
          const canonicalLower = String(canonicalValue || '').trim().toLowerCase();
          const displayValue = (!canonicalValue || ['unknown', 'none', 'null', 'n/a'].includes(canonicalLower))
            ? 'Неизвестно'
            : canonicalValue;
          return {
            ...item,
            value: displayValue || rawValue,
            filterValue: item?.filterValue || matchedOperator || rawValue,
          };
        });

        setSandboxStats({
          ...rawStats,
          operator: normalizedOperatorStats,
        });
      }
    } catch (err) {
      logger.error('Ошибка загрузки sandbox stats:', err);
    }
  }, [convertFiltersToApiParams, operatorTemplates, filterValues.selected_operator]);

  const handleSandboxToggle = useCallback(() => {
    setSandboxVisible((prev) => {
      if (!prev) {
        setStatsVisible(false);
        fetchSandboxStats();
        void fetchOsfpListForSandbox();
      }
      return !prev;
    });
  }, [fetchSandboxStats, fetchOsfpListForSandbox]);

  const handleStatsToggle = useCallback(() => {
    setStatsVisible((prev) => {
      const next = !prev;
      if (next) {
        setSandboxVisible(false);
        loadProxyPanelExtraStats();
      }
      return next;
    });
  }, [loadProxyPanelExtraStats]);

  const handleSandboxApply = useCallback(async (filters) => {
    const nextFilters = filters || {};
    const newPagination = { ...paginationRef.current, current: 1 };

    paginationUpdateRef.current = true;
    setPagination(newPagination);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', '1');
    setSearchParams(newSearchParams, { replace: true });

    filteredInfoRef.current = nextFilters;
    setFilteredInfo(nextFilters);
    await fetchFilterValues(nextFilters);
    await fetchProxiesOnly(nextFilters, true, newPagination);
    fetchSandboxStats(nextFilters);
  }, [searchParams, setSearchParams, fetchFilterValues, fetchProxiesOnly, fetchSandboxStats]);

  const handleSandboxReset = useCallback(async () => {
    const emptyFilters = {};
    const newPagination = { ...paginationRef.current, current: 1 };

    paginationUpdateRef.current = true;
    setPagination(newPagination);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', '1');
    setSearchParams(newSearchParams, { replace: true });

    filteredInfoRef.current = emptyFilters;
    setFilteredInfo(emptyFilters);
    await fetchFilterValues(emptyFilters);
    await fetchProxiesOnly(emptyFilters, true, newPagination);
    fetchSandboxStats(emptyFilters);
  }, [searchParams, setSearchParams, fetchFilterValues, fetchProxiesOnly, fetchSandboxStats]);

  // Логика плавного появления/исчезновения оверлея загрузки
  const overlayCondition = !isFirstLoad && !creatingProxy && loading && (loadingProgress > 0 || loadingMessage !== '');

  useEffect(() => {
    if (overlayCondition) {
      setOverlayVisible(true);
      setOverlayClosing(false);
    } else if (overlayVisible) {
      // Запускаем анимацию исчезновения
      setOverlayClosing(true);
      const timer = setTimeout(() => {
        setOverlayVisible(false);
        setOverlayClosing(false);
      }, 350); // длительность анимации исчезновения
      return () => clearTimeout(timer);
    }
  }, [overlayCondition]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ранний return для загрузки — ПОСЛЕ всех хуков
  if (loading && safeProxies.length === 0) {
    return (
      <div className="proxy-loading-fullpage">
        <div className="loader-container">
          <div className="loader-ball" />
          <div className="loader-shadow" />
        </div>
      </div>
    );
  }

  const tableLoading = loading && !overlayVisible;

  return (
    <div className="pm-content-fade-in">
      {proxyCheckSummary && (
        <CheckResultBanner
          text={proxyCheckSummary.text}
          content={proxyCheckSummary.content}
          tone={proxyCheckSummary.tone}
          onClose={() => setProxyCheckSummary(null)}
        />
      )}
      {powerToggleBanner && (
        <CheckResultBanner
          text={powerToggleBanner.text}
          tone={powerToggleBanner.tone}
          busy={Boolean(powerToggleBanner.busy)}
          progress={powerToggleBanner.progress}
          autoHideMs={powerToggleBanner.autoHideMs ?? 6000}
          onClose={() => setPowerToggleBanner(null)}
        />
      )}
      {/* Логи создания прокси */}
      {showCreationLogs && (
        <ProxyCreationLogs
          key={logsRefreshKey}
          autoRefresh={false}
        />
      )}

      {/* Новая таблица прокси по макету Figma */}
      <ProxyTable
        data={proxyTableData}
        loading={tableLoading}
        totalCount={pagination.total || 0}
        stats={proxyTableStats}
        currentPage={pagination.current}
        pageSize={pagination.pageSize}
        sortField={({
          client_login: 'client',
          tariff_plan: 'tariff',
          availability_status: 'availability',
          auto_replace_enabled: 'guard',
          server_name: 'server',
          modem: 'modem',
          port: 'port',
          proxy_type: 'type',
          selected_city: 'geo',
          selected_operator: 'operator',
          modem_reconnect_type: 'rotation',
          modem_osfp: 'osFingerprint',
          expires_at: 'expires_at',
        })[sortedInfo.columnKey] || sortedInfo.columnKey || null}
        sortDirection={sortedInfo.order === 'descend' ? 'desc' : 'asc'}
        activeFilters={tableActiveFilters}
        filterOptions={proxyTableFilterOptions}
        osfpList={osfpList}
        onSort={handleProxyTableSort}
        onFilter={handleProxyTableFilter}
        onClearAllFilters={handleClearAllTableFilters}
        onPageChange={handleProxyTablePageChange}
        onPageSizeChange={handleProxyTablePageSizeChange}
        onSync={handleProxyTableSync}
        syncActive={syncing}
        onRefresh={handleProxyTableRefresh}
        onExport={handleProxyTableExport}
        onAdd={handleProxyTableAdd}
        onRowAction={handleProxyTableRowAction}
        onSelectionChange={handleProxyTableSelectionChange}
        selectionResetToken={selectionResetToken}
        onBulkDisable={handleBulkDisableProxies}
        onDelete={(selected) => {
          const items = Array.isArray(selected) ? selected : [selected];
          const proxyIds = items
            .map((row) => row?._original?.proxy_id || row?._original?.id || row?.id)
            .filter(Boolean);
          if (proxyIds.length === 0) return;

          Modal.confirm({
            title: `Удалить ${proxyIds.length} прокси?`,
            content: 'Это действие нельзя отменить.',
            okText: 'Удалить',
            cancelText: 'Отмена',
            okType: 'danger',
            onOk: async () => {
              if (proxyIds.length === 1) {
                await handleDeleteProxy(proxyIds[0]);
              } else {
                try {
                  setLoading(true);
                  message.loading({ content: `Удаление ${proxyIds.length} прокси...`, key: 'bulk-delete', duration: 0 });
                  const response = await apiService.bulkDeleteProxies(proxyIds);
                  const result = response.data;
                  message.destroy('bulk-delete');
                  if (result.deleted_count > 0) {
                    message.success(`Удалено ${result.deleted_count} прокси за ${result.elapsed_time_seconds || 0}с`);
                    setSelectedRowKeys([]);
                    setSelectionResetToken((prev) => prev + 1);
                  }
                  if (result.kraken_errors && result.kraken_errors.length > 0) {
                    message.warning(`Ошибки Kraken: ${result.kraken_errors.length}`);
                  }
                  await fetchProxiesOnly(null, true);
                } catch (error) {
                  message.destroy('bulk-delete');
                  message.error('Ошибка массового удаления: ' + (error.response?.data?.detail || error.message));
                } finally {
                  setLoading(false);
                }
              }
            },
          });
        }}
        onEdit={(selected) => {
          if (Array.isArray(selected) && selected.length > 1) {
            // Массовое редактирование
            setBulkEditModalVisible(true);
          } else {
            // Одиночное редактирование
            const item = Array.isArray(selected) ? selected[0] : selected;
            const proxy = item?._original || safeProxies.find((p) => p.id === item?.id);
            if (proxy) handleEditProxy(proxy);
          }
        }}
        onRefreshCheck={(proxy) => {
          const proxyId = proxy?.id || proxy?._original?.id || proxy?._original?.proxy_id;
          if (proxyId) handleCheckProxy(proxyId);
        }}
        checkingProxies={checkingProxies}
        onReconnect={(proxy) => {
          const proxyId = proxy?._original?.proxy_id || proxy?.id;
          if (!proxyId) return;
          Modal.confirm({
            title: 'Переподключить прокси?',
            content: 'Прокси будет переподключена к другому работающему модему на том же сервере Kraken с сохранением номера порта',
            okText: 'Переподключить',
            cancelText: 'Отмена',
            onOk: () => handleReconnectProxy(proxyId),
          });
        }}
        onToggle={(proxy) => {
          const proxyId = proxy?._original?.proxy_id || proxy?.id;
          const isActive = proxy?._original?.is_active;
          if (proxyId != null) handleToggleProxyStatus(proxyId, isActive);
        }}
        onBulkCheck={handleBulkCheckProxies}
        bulkChecking={bulkChecking}
        onAddToChecker={handleAddToCheckerProxies}
        onAddRowToChecker={handleAddTableRowToChecker}
        onAddToLocationCheck={handleAddToLocationCheckProxies}
        onAddRowToLocationCheck={handleAddRowToLocationCheck}
        onSandbox={handleSandboxToggle}
        sandboxPanel={sandboxVisible ? (
          <ProxySandboxPanel
            data={proxyTableData}
            visible={sandboxVisible}
            onClose={() => setSandboxVisible(false)}
            onApply={handleSandboxApply}
            onReset={handleSandboxReset}
            activeFilters={filteredInfo}
            serverStats={sandboxStats}
            operatorOptions={filterValues.selected_operator || []}
            osFingerprintOptions={filterValues.modem_osfp || []}
            osfpList={osfpList}
          />
        ) : null}
        onStats={handleStatsToggle}
        statsPanel={statsVisible ? (
          <ProxyStatsPanel statusData={mergedStatsData} onClose={handleStatsToggle} />
        ) : null}
      />

      {/* Панель создания прокси (новый дизайн) */}
      <ProxyCreatePanel
        visible={modalVisible}
        onClose={() => {
          if (!creatingProxy) {
            setModalVisible(false);
            setClientMode('existing');
            setSelectedTariffForCreate(null);
            setOsfpList([]);
          }
        }}
        onSubmit={handleCreateProxy}
        submitting={creatingProxy}
        clients={clients}
        tariffPlans={tariffPlans}
        proxyTypes={proxyTypes}
        availableCities={availableCities}
        krakenConnections={krakenConnections}
        filteredKrakenConnections={filteredKrakenConnections}
        availableServers={availableServers}
        filteredServers={filteredServers}
        availableOperators={availableOperators}
        osfpList={osfpList}
        selectedTariffForCreate={selectedTariffForCreate}
        onCityChange={handleCreateCityChange}
        onServerChange={handleServerChange}
        onTariffChange={handleTariffSelectionChange}
        onRefreshTariffs={() => {
          logger.debug('Нажата кнопка обновления тарифных планов в панели создания');
          fetchData();
        }}
        onRefreshOsfp={handleRefreshOsfpList}
        osfpRefreshLoading={osfpRefreshLoading}
      />

      {/* Панель редактирования прокси (тот же дизайн что и создание) */}
      <ProxyEditPanel
        visible={editModalVisible}
        proxy={editingProxy}
        onClose={() => {
          setEditModalVisible(false);
          setEditingProxy(null);
        }}
        onSubmit={handleUpdateProxy}
        submitting={updatingProxy}
        tariffPlans={tariffPlans}
        proxyTypes={proxyTypes}
        availableCities={availableCities}
        krakenConnections={krakenConnections}
        filteredKrakenConnections={filteredKrakenConnections}
        availableServers={availableServers}
        filteredServers={filteredServers}
        availableOperators={allAvailableOperators}
        osfpList={osfpList}
        onCityChange={handleEditCityChange}
        onConnectionChange={handleEditConnectionChange}
        onRefreshOsfp={handleRefreshOsfpList}
        osfpRefreshLoading={osfpRefreshLoading}
        convertKBytesToMbps={convertKBytesToMbps}
      />

      {/* Экспорт прокси — кастомная панель по макету Figma */}
      {exportVisible && typeof document !== 'undefined' && createPortal((
        <div className="pcp-overlay" onClick={(e) => { if (e.target === e.currentTarget) handleCloseExportModal(); }}>
          <div className="pcp pcp--blue" role="dialog" aria-label="Экспорт прокси" ref={(node) => {
            if (node && !exportTemplates.length) {
              apiService.listExportTemplates()
                .then(res => setExportTemplates(res.data?.templates || []))
                .catch(() => {});
            }
          }}>
            {/* Header */}
            <div className="pcp__header">
              <h2 className="pcp__title">экспорт прокси</h2>
              <button type="button" className="pcp__close-btn" onClick={handleCloseExportModal} aria-label="Закрыть" tabIndex={0}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
            </div>

            {/* Body */}
            <div className="pcp__body">
              {/* Сворачиваемая секция переменных (мобильная) */}
              {!exportResult && (
                <div
                  className="exp__vars-toggle"
                  role="button"
                  tabIndex={0}
                  onClick={() => setExportVarsOpen(prev => !prev)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExportVarsOpen(prev => !prev); }}
                  aria-expanded={exportVarsOpen}
                >
                  <span className="exp__vars-toggle-text">Доступные переменные для экспорта</span>
                  <svg className={`exp__vars-toggle-icon${exportVarsOpen ? ' exp__vars-toggle-icon--open' : ''}`} width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}

              {!exportResult && exportVarsOpen && (
                <div className="pcp__section exp__vars-section">
                  <div className="exp__vars-block">
                    <div className="exp__vars-title">Переменные:</div>
                    {[
                      ['{protocol}', 'Тип прокси (HTTP/SOCKS5)'],
                      ['{domain_host}', 'Host'],
                      ['{ip_host}', 'Псевдоним подключения (как в таблице «Подключения»)'],
                      ['{port}', 'Порт прокси'],
                      ['{login}', 'Логин прокси'],
                      ['{password}', 'Пароль прокси'],
                      ['{reconnect_link}', 'Ссылка смены IP'],
                      ['{reconnect_link_action}', 'Ссылка смены IP сразу'],
                    ].map(([variable, desc]) => (
                      <div className="exp__vars-row" key={variable}>
                        <span className="exp__vars-desc">{desc}</span>
                        <span
                          className="exp__vars-badge"
                          role="button"
                          tabIndex={0}
                          aria-label={`Добавить ${variable}`}
                          onClick={() => setCustomPattern(prev => {
                            if (!prev) return variable;
                            if (/[:/\-@]$/.test(prev)) return prev + variable;
                            return prev + ':' + variable;
                          })}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setCustomPattern(prev => {
                            if (!prev) return variable;
                            if (/[:/\-@]$/.test(prev)) return prev + variable;
                            return prev + ':' + variable;
                          }); }}
                        >{variable}</span>
                      </div>
                    ))}
                    <div className="exp__separators">
                      <span className="exp__separators-label">Символы:</span>
                      {['://', '@', ':', '-', '//'].map((sep) => (
                        <span
                          key={sep}
                          className="exp__sep-badge"
                          role="button"
                          tabIndex={0}
                          aria-label={`Добавить ${sep}`}
                          onClick={() => setCustomPattern(prev => prev + sep)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setCustomPattern(prev => prev + sep); }}
                        >{sep}</span>
                      ))}
                    </div>
                  </div>

                  <div className="exp__examples-block">
                    <div className="exp__examples-title">Примеры:</div>
                    <div className="exp__examples-text">
                      • {'{protocol}'}://{'{login}'}:{'{password}'}@{'{ip}'}:{'{port}'}<br/>
                      • {'{ip}'}:{'{port}'}:{'{login}'}:{'{password}'}
                    </div>
                  </div>
                </div>
              )}

              {/* Форма настроек */}
              <div className="pcp__section">
                {/* Область экспорта */}
                <div className="pcp__field">
                  <div className="pcp__label">
                    <span className="pcp__label-required">*</span>
                    <span className="pcp__label-text">Экспорт</span>
                  </div>
                  {exportResult ? (
                    <div className="pcp__text-input pcp__text-input--disabled">
                      {({ all: 'Экспорт всех', client: 'По клиенту', selected: 'Выбранные строки', new: 'Только новые' })[exportScope] || exportScope}
                    </div>
                  ) : (
                    <CustomSelect
                      value={exportScope}
                      onChange={setExportScope}
                      options={[
                        { value: 'all', label: 'Экспорт всех' },
                        { value: 'client', label: 'По клиенту' },
                        { value: 'selected', label: 'Выбранные строки' },
                        { value: 'new', label: 'Только новые (по выбранным)' },
                      ]}
                    />
                  )}
                </div>

                {/* Клиент (если выбран scope = client) */}
                {!exportResult && exportScope === 'client' && (
                  <div className="pcp__field">
                    <div className="pcp__label">
                      <span className="pcp__label-text">Клиент</span>
                    </div>
                    <CustomSelect
                      value={exportClient}
                      onChange={setExportClient}
                      placeholder="Выберите клиента"
                      searchable
                      options={[
                        { value: '', label: 'Все клиенты' },
                        ...(clients || []).map(c => ({ value: c.login, label: c.login })),
                      ]}
                    />
                  </div>
                )}

                {/* Формат экспорта */}
                <div className="pcp__field">
                  <div className="pcp__label">
                    <span className="pcp__label-text">Формат экспорта</span>
                  </div>
                  {exportResult ? (
                    <div className="pcp__text-input pcp__text-input--disabled">
                      {customPattern || selectedTemplate || '—'}
                    </div>
                  ) : (
                    <div className="pcp__input-with-clear">
                      <input
                        type="text"
                        className="pcp__text-input"
                        placeholder="Используйте переменные или выберите шаблон"
                        value={customPattern}
                        onChange={(e) => setCustomPattern(e.target.value)}
                      />
                      {customPattern && (
                        <button
                          type="button"
                          className="pcp__input-clear-btn"
                          onClick={() => setCustomPattern('')}
                          aria-label="Очистить формат"
                          tabIndex={0}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Задать вручную — виден в режиме результата */}
                {exportResult && (
                  <div className="pcp__field">
                    <div className="pcp__label">
                      <span className="pcp__label-text">Задать вручную</span>
                    </div>
                    <div className="pcp__input-with-clear">
                      <input
                        type="text"
                        className="pcp__text-input"
                        placeholder="{ip}:{port}:{login}:{password}"
                        value={customPattern}
                        onChange={(e) => setCustomPattern(e.target.value)}
                      />
                      {customPattern && (
                        <button
                          type="button"
                          className="pcp__input-clear-btn"
                          onClick={() => setCustomPattern('')}
                          aria-label="Очистить формат"
                          tabIndex={0}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Шаблон (только до экспорта) */}
                {!exportResult && (
                  <div className="pcp__field">
                    <div className="pcp__label">
                      <span className="pcp__label-text">Шаблон</span>
                    </div>
                    <CustomSelect
                      value={selectedTemplate || ''}
                      onChange={(val) => setSelectedTemplate(val || undefined)}
                      placeholder="Выберите шаблон"
                      options={[
                        { value: '', label: 'Без шаблона' },
                        ...(exportTemplates || []).map(t => ({ value: t.name, label: t.name })),
                      ]}
                    />
                  </div>
                )}

                {/* До экспорта: переменные + примеры (десктоп) */}
                {!exportResult && (
                  <div className="exp__desktop-vars">
                    <div className="exp__vars-block">
                      <div className="exp__vars-title">Переменные:</div>
                      {[
                        ['{protocol}', 'Тип прокси (HTTP/SOCKS5)'],
                        ['{domain_host}', 'Host'],
                        ['{ip_host}', 'Псевдоним подключения (как в таблице «Подключения»)'],
                        ['{port}', 'Порт прокси'],
                        ['{login}', 'Логин прокси'],
                        ['{password}', 'Пароль прокси'],
                        ['{reconnect_link}', 'Ссылка смены IP'],
                        ['{reconnect_link_action}', 'Ссылка смены IP сразу'],
                      ].map(([variable, desc]) => (
                        <div className="exp__vars-row" key={variable}>
                          <span className="exp__vars-desc">{desc}</span>
                          <span
                            className="exp__vars-badge"
                            role="button"
                            tabIndex={0}
                            aria-label={`Добавить ${variable}`}
                            onClick={() => setCustomPattern(prev => {
                              if (!prev) return variable;
                              if (/[:/\-@]$/.test(prev)) return prev + variable;
                              return prev + ':' + variable;
                            })}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setCustomPattern(prev => {
                              if (!prev) return variable;
                              if (/[:/\-@]$/.test(prev)) return prev + variable;
                              return prev + ':' + variable;
                            }); }}
                          >{variable}</span>
                        </div>
                      ))}
                      <div className="exp__separators">
                        <span className="exp__separators-label">Символы:</span>
                        {['://', '@', ':', '-', '//'].map((sep) => (
                          <span
                            key={sep}
                            className="exp__sep-badge"
                            role="button"
                            tabIndex={0}
                            aria-label={`Добавить ${sep}`}
                            onClick={() => setCustomPattern(prev => prev + sep)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setCustomPattern(prev => prev + sep); }}
                          >{sep}</span>
                        ))}
                      </div>
                    </div>

                    <div className="exp__examples-block">
                      <div className="exp__examples-title">Примеры:</div>
                      <div className="exp__examples-text">
                        • {'{protocol}'}://{'{login}'}:{'{password}'}@{'{ip}'}:{'{port}'}<br/>
                        • {'{ip}'}:{'{port}'}:{'{login}'}:{'{password}'}
                      </div>
                    </div>
                  </div>
                )}

                {/* До экспорта: сохранить как шаблон (inline) */}
                {!exportResult && (
                  <>
                    {inlineTplMode && (
                      <div className="pcp__field">
                        <div className="pcp__label">
                          <span className="pcp__label-text">Название шаблона</span>
                        </div>
                        <input
                          type="text"
                          className="pcp__text-input"
                          placeholder="Введите название"
                          value={inlineTplName}
                          onChange={(e) => setInlineTplName(e.target.value)}
                          autoFocus
                        />
                      </div>
                    )}
                    <button
                      type="button"
                      className={`exp__btn exp__btn--full${inlineTplMode ? ' exp__btn--primary' : ' exp__btn--secondary'}`}
                      disabled={!customPattern || (inlineTplMode && !inlineTplName.trim())}
                      onClick={async () => {
                        if (!inlineTplMode) {
                          setInlineTplMode(true);
                          return;
                        }
                        try {
                          await apiService.createExportTemplate({
                            name: inlineTplName.trim(),
                            pattern: customPattern,
                          });
                          message.success('Шаблон сохранён');
                          setInlineTplMode(false);
                          setInlineTplName('');
                          const templatesRes = await apiService.listExportTemplates();
                          setExportTemplates(templatesRes.data?.templates || []);
                        } catch (e) {
                          message.error(e.response?.data?.detail || 'Ошибка сохранения шаблона');
                        }
                      }}
                      tabIndex={0}
                    >
                      {inlineTplMode ? 'Сохранить' : 'Сохранить как шаблон'}
                    </button>
                  </>
                )}

                {/* После экспорта: кнопки вертикально */}
                {exportResult && (
                  <div className="exp__actions-stack">
                    <button
                      type="button"
                      className="exp__btn exp__btn--secondary exp__btn--full"
                      onClick={() => setExportResult('')}
                      tabIndex={0}
                    >
                      Изменить формат экспорта
                    </button>
                    {inlineTplMode && (
                      <div className="pcp__field">
                        <div className="pcp__label">
                          <span className="pcp__label-text">Название шаблона</span>
                        </div>
                        <input
                          type="text"
                          className="pcp__text-input"
                          placeholder="Введите название"
                          value={inlineTplName}
                          onChange={(e) => setInlineTplName(e.target.value)}
                          autoFocus
                        />
                      </div>
                    )}
                    <button
                      type="button"
                      className={`exp__btn exp__btn--full${inlineTplMode ? ' exp__btn--primary' : ' exp__btn--secondary'}`}
                      disabled={!customPattern || (inlineTplMode && !inlineTplName.trim())}
                      onClick={async () => {
                        if (!inlineTplMode) {
                          setInlineTplMode(true);
                          return;
                        }
                        try {
                          await apiService.createExportTemplate({
                            name: inlineTplName.trim(),
                            pattern: customPattern,
                          });
                          message.success('Шаблон сохранён');
                          setInlineTplMode(false);
                          setInlineTplName('');
                          const templatesRes = await apiService.listExportTemplates();
                          setExportTemplates(templatesRes.data?.templates || []);
                        } catch (e) {
                          message.error(e.response?.data?.detail || 'Ошибка сохранения шаблона');
                        }
                      }}
                      tabIndex={0}
                    >
                      {inlineTplMode ? 'Сохранить' : 'Сохранить как шаблон'}
                    </button>
                  </div>
                )}
              </div>

              {/* Результат экспорта — таблица */}
              {exportResult && (() => {
                const lines = exportResult.split('\n').filter(l => l.trim());
                return (
                  <div className="pcp__section exp__result-section">
                    <div className="exp__result-count">Экспортировано {lines.length} прокси</div>
                    <div className="exp__result-table-wrap">
                      <div className="exp__result-table">
                        <div className="exp__result-col exp__result-col--num">
                          <div className="exp__result-th">№</div>
                          {lines.map((_, i) => (
                            <div className={`exp__result-td${i % 2 === 0 ? ' exp__result-td--alt' : ''}`} key={i}>{i + 1}</div>
                          ))}
                        </div>
                        <div className="exp__result-col exp__result-col--proxy">
                          <div className="exp__result-th">Прокси</div>
                          {lines.map((line, i) => (
                            <div className={`exp__result-td${i % 2 === 0 ? ' exp__result-td--alt' : ''}`} key={i}>
                              <span className="exp__result-line">{line}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Шаблоны */}
              {!exportResult && (exportTemplates || []).length > 0 && (
                <div className="pcp__section">
                  <h3 className="pcp__section-title">Шаблоны</h3>
                  {(exportTemplates || []).map(t => (
                    <div className="exp__tpl-row" key={t.id}>
                      <div className="exp__tpl-info">
                        <span className="exp__tpl-name">{t.name}</span>
                        <span className="exp__tpl-pattern">{t.pattern}</span>
                      </div>
                      <button
                        type="button"
                        className="exp__tpl-delete"
                        onClick={async () => {
                          try {
                            await apiService.deleteExportTemplate(t.id);
                            const templatesRes = await apiService.listExportTemplates();
                            setExportTemplates(templatesRes.data?.templates || []);
                            message.success('Шаблон удалён');
                          } catch (e) {
                            message.error('Ошибка удаления шаблона');
                          }
                        }}
                        aria-label="Удалить шаблон"
                        tabIndex={0}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer — фиксированный внизу */}
            <div className="exp__footer">
              <button
                type="button"
                className="exp__btn exp__btn--secondary"
                onClick={handleCloseExportModal}
                tabIndex={0}
              >
                {exportResult ? 'Закрыть' : 'Отмена'}
              </button>
              <button
                type="button"
                className="exp__btn exp__btn--primary"
                disabled={exporting || (!exportResult && !customPattern && !selectedTemplate)}
                onClick={async () => {
                  if (exportResult) {
                    navigator.clipboard.writeText(exportResult);
                    message.success('Скопировано');
                    return;
                  }
                  try {
                    setExporting(true);
                    const payload = {
                      template_name: selectedTemplate || undefined,
                      custom_pattern: customPattern || undefined,
                    };
                    if (exportScope === 'client' && exportClient) payload.client_login = exportClient;
                    if (exportScope === 'selected' && selectedRowKeys.length) payload.proxy_ids = selectedRowKeys;
                    if (exportScope === 'new' && selectedRowKeys.length) payload.only_new_ids = selectedRowKeys;
                    const res = await apiService.exportProxies(payload);
                    setExportResult(res.data?.text || '');
                  } catch (e) {
                    message.error(e.response?.data?.detail || 'Ошибка экспорта');
                  } finally {
                    setExporting(false);
                  }
                }}
                tabIndex={0}
                aria-label={exportResult ? 'Копировать' : 'Экспортировать'}
              >
                {exporting ? 'Формирование...' : exportResult ? 'Скопировать' : 'Экспортировать'}
              </button>
            </div>
          </div>
        </div>
      ), document.body)}

      {/* Создание шаблона экспорта */}
      <Modal
        title="Создать шаблон экспорта"
        open={createTplVisible}
        onCancel={() => setCreateTplVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={tplForm} layout="vertical" onFinish={async (vals) => {
          try {
            await apiService.createExportTemplate({
              name: vals.name,
              pattern: vals.pattern,
              description: vals.description || undefined,
              is_default: !!vals.is_default});
            message.success('Шаблон создан');
            setCreateTplVisible(false);
            tplForm.resetFields();
            const templatesRes = await apiService.listExportTemplates();
            setExportTemplates(templatesRes.data?.templates || []);
          } catch (e) {
            message.error(e.response?.data?.detail || 'Ошибка создания шаблона');
          }
        }}>
          {/* Памятка по переменным */}
          <Card size="small" title="Доступные переменные" style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '4px', marginBottom: '8px' }}>
                <div><code>{'{protocol}'}</code> - тип прокси</div>
                <div><code>{'{login}'}</code> - логин прокси</div>
                <div><code>{'{password}'}</code> - пароль прокси</div>
                <div><code>{'{ip}'}</code> - внешний IP сервера</div>
                <div><code>{'{port}'}</code> - порт прокси</div>
                <div><code>{'{client}'}</code> - логин клиента</div>
                <div><code>{'{tariff}'}</code> - название тарифа</div>
                <div><code>{'{city}'}</code> - город</div>
                <div><code>{'{operator}'}</code> - мобильный оператор</div>
                <div><code>{'{server}'}</code> - название сервера</div>
              </div>
              <div style={{ padding: '8px', background: '#1a1a1a', borderRadius: '4px', fontSize: '11px', color: '#c2c2c2' }}>
                <strong style={{ color: '#e8ab6a' }}>Примеры:</strong><br/>
                • <code>{'{protocol}'}://{'{login}'}:{'{password}'}@{'{ip}'}:{'{port}'}</code><br/>
                • <code>{'{ip}'}:{'{port}'}:{'{login}'}:{'{password}'}</code>
              </div>
            </div>
          </Card>

          <Form.Item name="name" label="Название" rules={[{ required: true, message: 'Введите название' }]}>
            <Input placeholder="Например: Для BAS" />
          </Form.Item>
          <Form.Item name="pattern" label="Формат" rules={[{ required: true, message: 'Введите формат' }]}>
            <Input placeholder="{protocol}://{login}:{password}@{ip}:{port}" />
          </Form.Item>
          <Form.Item name="description" label="Описание">
            <Input placeholder="Опционально" />
          </Form.Item>
          <Form.Item name="is_default" valuePropName="checked">
            <Checkbox>Сделать по умолчанию</Checkbox>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">Сохранить</Button>
              <Button onClick={() => setCreateTplVisible(false)}>Отмена</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Панель массового редактирования прокси (slide-in справа, как одиночное) */}
      {bulkEditModalVisible && typeof document !== 'undefined' && createPortal((
        <div
          className="pcp-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) { setBulkEditModalVisible(false); setBulkData({}); } }}
        >
          <div className="pcp" role="dialog" aria-label="Массовое редактирование прокси">

            {/* Header */}
            <div className="pcp__header">
              <h2 className="pcp__title">массовое редактирование</h2>
              <button type="button" className="pcp__close-btn" onClick={() => { setBulkEditModalVisible(false); setBulkData({}); }} aria-label="Закрыть" tabIndex={0}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
            </div>

            {/* Body */}
            <div className="pcp__body">

              {/* Info */}
              <div className="pcp__section">
                <div className="pcp__info-box" style={{ background: '#ecf1fe' }}>
                  <p className="pcp__info-box-text" style={{ color: '#5283f4' }}>
                    Выбрано прокси: <strong>{selectedRowKeys.length}</strong>. Будут обновлены только изменённые поля.
                  </p>
                </div>
              </div>

              {/* Тариф и лимиты */}
              <div className="pcp__section">
                <h3 className="pcp__section-title">Тариф и лимиты</h3>
                <div className="pcp__field">
                  <div className="pcp__label"><span className="pcp__label-text">Тарифный план</span></div>
                  <CustomSelect
                    value={bulkData.tariff_plan_name || ''}
                    onChange={(val) => { setBulkField('tariff_plan_name', val || undefined); handleBulkEditTariffChange(val); }}
                    placeholder={`Выберите тариф (${tariffPlans.length} доступно)`}
                    options={[
                      { value: '', label: `Выберите тариф (${tariffPlans.length} доступно)` },
                      ...tariffPlans.map((plan) => ({ value: plan.name, label: plan.name })),
                    ]}
                    searchable
                  />
                </div>
                <div className="pcp__row">
                  <div className="pcp__field">
                    <div className="pcp__label"><span className="pcp__label-text">Тип прокси</span></div>
                    <CustomSelect
                      value={bulkData.proxy_type || ''}
                      onChange={(val) => setBulkField('proxy_type', val || undefined)}
                      placeholder="Не изменять"
                      options={[
                        { value: '', label: 'Не изменять' },
                        ...proxyTypes.map((t) => ({ value: t, label: t.toUpperCase() })),
                      ]}
                    />
                  </div>
                  <div className="pcp__field pcp__field--full-mobile pcp__field--expires">
                    <div className="pcp__label"><span className="pcp__label-text">Срок действия</span></div>
                    <div className="pcp__expires-wrap">
                      <DatePicker
                        value={bulkData.expires_at ? dayjs(bulkData.expires_at) : null}
                        onChange={(date) => {
                          if (date) {
                            setBulkField('expires_at', date.format('YYYY-MM-DD'));
                          } else {
                            setBulkField('expires_at', null);
                          }
                        }}
                        format="DD.MM.YYYY"
                        placeholder="Не изменять"
                        style={{ width: '100%' }}
                        allowClear
                        getPopupContainer={(trigger) => trigger.parentElement}
                        renderExtraFooter={() => (
                          <div className="pcp__expires-footer">
                            {bulkData.expires_at && (() => {
                              const now = dayjs().startOf('day');
                              const exp = dayjs(bulkData.expires_at).startOf('day');
                              const days = exp.diff(now, 'day');
                              const isExpired = days < 0;
                              const isSoon = days >= 0 && days <= 3;
                              let label = '';
                              if (days < 0) label = `Просрочено на ${Math.abs(days)} дн.`;
                              else if (days === 0) label = 'Истекает сегодня';
                              else if (days === 1) label = 'Остался 1 день';
                              else if (days >= 2 && days <= 4) label = `Осталось ${days} дня`;
                              else label = `Осталось ${days} дней`;
                              return (
                                <span className={`pcp__expires-days-hint ${isExpired ? 'pcp__expires-days-hint--expired' : ''} ${isSoon ? 'pcp__expires-days-hint--soon' : ''}`}>
                                  {label}
                                </span>
                              );
                            })()}
                            <div className="pcp__expires-presets">
                              <button
                                type="button"
                                className={`pcp__expires-preset-btn ${bulkData.expires_at === null ? 'pcp__expires-preset-btn--active' : ''}`}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => setBulkField('expires_at', null)}
                              >
                                Безлимит
                              </button>
                              {[30, 60, 90, 180, 365].map((d) => {
                                const target = dayjs().add(d, 'day');
                                const iso = target.format('YYYY-MM-DD');
                                return (
                                  <button
                                    key={d}
                                    type="button"
                                    className={`pcp__expires-preset-btn ${bulkData.expires_at === iso ? 'pcp__expires-preset-btn--active' : ''}`}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => setBulkField('expires_at', iso)}
                                  >
                                    +{d}д
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      />
                    </div>
                    {bulkData.expires_at && (
                      <p
                        className="pcp__expires-time-anchor-hint"
                        style={{ fontSize: 12, marginTop: 6, color: '#5c6470', lineHeight: 1.35 }}
                      >
                        Для каждой прокси окончание в выбранный календарный день — в то же время МСК, что и при её создании.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Размещение */}
              <div className="pcp__section">
                <h3 className="pcp__section-title">Размещение</h3>
                <div className="pcp__row">
                  <div className="pcp__field">
                    <div className="pcp__label"><span className="pcp__label-text">Город</span></div>
                    <CustomSelect
                      value={bulkData.city || ''}
                      onChange={(val) => { setBulkField('city', val || undefined); handleBulkEditCityChange(val); }}
                      placeholder="Не изменять"
                      options={[
                        { value: '', label: 'Не изменять' },
                        ...availableCities.map((c) => ({ value: c, label: c })),
                      ]}
                      searchable
                    />
                  </div>
                  <div className="pcp__field">
                    <div className="pcp__label"><span className="pcp__label-text">Сервер Kraken</span></div>
                    <CustomSelect
                      value={bulkData.connection_id ? String(bulkData.connection_id) : ''}
                      onChange={(val) => { const num = val ? Number(val) : undefined; setBulkField('connection_id', num); handleBulkEditConnectionChange(num || null); }}
                      placeholder="Не изменять"
                      options={[
                        { value: '', label: 'Не изменять' },
                        ...(filteredKrakenConnections.length > 0 ? filteredKrakenConnections : krakenConnections || []).map((conn) => ({ value: String(conn.id), label: `${conn.name} (${conn.server_url})` })),
                      ]}
                      searchable
                    />
                  </div>
                </div>
                <div className="pcp__row">
                  <div className="pcp__field">
                    <div className="pcp__label"><span className="pcp__label-text">Модем</span></div>
                    <CustomSelect
                      value={bulkData.server_id ? String(bulkData.server_id) : ''}
                      onChange={(val) => setBulkField('server_id', val ? Number(val) : undefined)}
                      placeholder="Не изменять"
                      options={[
                        { value: '', label: 'Не изменять' },
                        ...(filteredServers.length > 0 ? filteredServers : availableServers).map((s) => { const octet = (s.ip_address || s.ip || '').split('.')[2]; return { value: String(s.id), label: octet ? `№ ${octet} ${s.name || s.ip}` : (s.name || s.ip) }; }),
                      ]}
                      searchable
                    />
                  </div>
                  <div className="pcp__field">
                    <div className="pcp__label"><span className="pcp__label-text">Оператор</span></div>
                    <CustomSelect
                      value={bulkData.mobile_operator || ''}
                      onChange={handleBulkEditOperatorChange}
                      placeholder="Не изменять"
                      options={[
                        { value: '', label: 'Не изменять' },
                        ...bulkPlacementOperators.map((op) => ({ value: op, label: op })),
                      ]}
                      searchable
                    />
                  </div>
                </div>
              </div>

              {/* Параметры */}
              <div className="pcp__section">
                <h3 className="pcp__section-title">Параметры</h3>
                <div className="pcp__toggle-row">
                  <span className="pcp__toggle-label">
                    {bulkData.is_active === true ? 'Прокси активна' : bulkData.is_active === false ? 'Прокси неактивна' : 'Статус прокси'}
                    {bulkData.is_active === undefined && <span className="pcp__toggle-hint">не изменять</span>}
                  </span>
                  <button
                    type="button"
                    className={`pcp__switch ${bulkData.is_active === true ? 'pcp__switch--on' : bulkData.is_active === false ? 'pcp__switch--off' : 'pcp__switch--indeterminate'}`}
                    onClick={() => setBulkField('is_active', bulkData.is_active === undefined ? true : bulkData.is_active === true ? false : undefined)}
                    role="switch"
                    aria-checked={bulkData.is_active ?? 'mixed'}
                    aria-label="Статус прокси"
                    tabIndex={0}
                  >
                    <span className="pcp__switch-dot" />
                  </button>
                </div>
                <div className="pcp__toggle-row">
                  <span className="pcp__toggle-label">
                    Автозамена устройства при недоступности прокси
                    {bulkData.auto_replace_enabled === undefined && <span className="pcp__toggle-hint">не изменять</span>}
                  </span>
                  <button
                    type="button"
                    className={`pcp__switch ${bulkData.auto_replace_enabled === true ? 'pcp__switch--on' : bulkData.auto_replace_enabled === false ? 'pcp__switch--off' : 'pcp__switch--indeterminate'}`}
                    onClick={() => setBulkField('auto_replace_enabled', bulkData.auto_replace_enabled === undefined ? true : bulkData.auto_replace_enabled === true ? false : undefined)}
                    role="switch"
                    aria-checked={bulkData.auto_replace_enabled ?? 'mixed'}
                    aria-label="Автозамена устройства при недоступности прокси"
                    tabIndex={0}
                  >
                    <span className="pcp__switch-dot" />
                  </button>
                </div>
                <div className="pcp__toggle-row">
                  <span className="pcp__toggle-label">
                    Ссылка смены IP
                    {bulkData.reconnect_link_enabled === undefined && <span className="pcp__toggle-hint">не изменять</span>}
                  </span>
                  <button
                    type="button"
                    className={`pcp__switch ${bulkData.reconnect_link_enabled === true ? 'pcp__switch--on' : bulkData.reconnect_link_enabled === false ? 'pcp__switch--off' : 'pcp__switch--indeterminate'}`}
                    onClick={() => setBulkField('reconnect_link_enabled', bulkData.reconnect_link_enabled === undefined ? true : bulkData.reconnect_link_enabled === true ? false : undefined)}
                    role="switch"
                    aria-checked={bulkData.reconnect_link_enabled ?? 'mixed'}
                    aria-label="Ссылка смены IP"
                    tabIndex={0}
                  >
                    <span className="pcp__switch-dot" />
                  </button>
                </div>
                {bulkData.proxy_type !== 'socks5' && (
                  <>
                    <div className="pcp__toggle-row">
                      <span className="pcp__toggle-label">
                        HTTP Keep-Alive соединения
                        {bulkData.is_keepalive === undefined && <span className="pcp__toggle-hint">не изменять</span>}
                      </span>
                      <button
                        type="button"
                        className={`pcp__switch ${bulkData.is_keepalive === true ? 'pcp__switch--on' : bulkData.is_keepalive === false ? 'pcp__switch--off' : 'pcp__switch--indeterminate'}`}
                        onClick={() => setBulkField('is_keepalive', bulkData.is_keepalive === undefined ? true : bulkData.is_keepalive === true ? false : undefined)}
                        role="switch"
                        aria-checked={bulkData.is_keepalive ?? 'mixed'}
                        aria-label="HTTP Keep-Alive соединения"
                        tabIndex={0}
                      >
                        <span className="pcp__switch-dot" />
                      </button>
                    </div>
                    <div className="pcp__toggle-row">
                      <span className="pcp__toggle-label">
                        Сжатие HTTP-ответов (gzip)
                        {bulkData.is_compression === undefined && <span className="pcp__toggle-hint">не изменять</span>}
                      </span>
                      <button
                        type="button"
                        className={`pcp__switch ${bulkData.is_compression === true ? 'pcp__switch--on' : bulkData.is_compression === false ? 'pcp__switch--off' : 'pcp__switch--indeterminate'}`}
                        onClick={() => setBulkField('is_compression', bulkData.is_compression === undefined ? true : bulkData.is_compression === true ? false : undefined)}
                        role="switch"
                        aria-checked={bulkData.is_compression ?? 'mixed'}
                        aria-label="Сжатие HTTP-ответов (gzip)"
                        tabIndex={0}
                      >
                        <span className="pcp__switch-dot" />
                      </button>
                    </div>
                  </>
                )}
              </div>

              <div className="pcp__section">
                <h3 className="pcp__section-title">Разрешения прокси</h3>
                <div className="pcp__toggle-row">
                  <span className="pcp__toggle-label">
                    Доступ прокси по списку ресурсов
                    {bulkData.kraken_is_allow === undefined && <span className="pcp__toggle-hint">не изменять</span>}
                  </span>
                  <button
                    type="button"
                    className={`pcp__switch ${bulkData.kraken_is_allow === true ? 'pcp__switch--on' : bulkData.kraken_is_allow === false ? 'pcp__switch--off' : 'pcp__switch--indeterminate'}`}
                    onClick={() => setBulkField('kraken_is_allow', bulkData.kraken_is_allow === undefined ? true : bulkData.kraken_is_allow === true ? false : undefined)}
                    role="switch"
                    aria-checked={bulkData.kraken_is_allow ?? 'mixed'}
                    aria-label="Доступ прокси по списку ресурсов"
                    tabIndex={0}
                  >
                    <span className="pcp__switch-dot" />
                  </button>
                </div>
                <div className="pcp__toggle-row">
                  <span className="pcp__toggle-label">
                    Доступ к портам из списка
                    {bulkData.kraken_is_ports === undefined && <span className="pcp__toggle-hint">не изменять</span>}
                  </span>
                  <button
                    type="button"
                    className={`pcp__switch ${bulkData.kraken_is_ports === true ? 'pcp__switch--on' : bulkData.kraken_is_ports === false ? 'pcp__switch--off' : 'pcp__switch--indeterminate'}`}
                    onClick={() => setBulkField('kraken_is_ports', bulkData.kraken_is_ports === undefined ? true : bulkData.kraken_is_ports === true ? false : undefined)}
                    role="switch"
                    aria-checked={bulkData.kraken_is_ports ?? 'mixed'}
                    aria-label="Доступ к портам из списка"
                    tabIndex={0}
                  >
                    <span className="pcp__switch-dot" />
                  </button>
                </div>
                <div className="pcp__field">
                  <div className="pcp__label"><span className="pcp__label-text">Разрешенные ресурсы по ID</span></div>
                  <input
                    type="text"
                    className="pcp__text-input"
                    placeholder={BULK_KRAKEN_LIST_PLACEHOLDER}
                    value={bulkData.kraken_allow ?? ''}
                    onChange={(e) => setBulkField('kraken_allow', e.target.value)}
                    aria-label="Массово: разрешенные ресурсы по ID, через запятую. 0 — очистить у всех"
                    autoComplete="off"
                  />
                </div>
                <div className="pcp__field">
                  <div className="pcp__label"><span className="pcp__label-text">Разрешенные порты</span></div>
                  <input
                    type="text"
                    className="pcp__text-input"
                    placeholder={BULK_KRAKEN_LIST_PLACEHOLDER}
                    value={bulkData.kraken_targetport ?? ''}
                    onChange={(e) => setBulkField('kraken_targetport', e.target.value)}
                    aria-label="Массово: разрешенные порты через запятую. 0 — очистить у всех"
                    autoComplete="off"
                  />
                </div>
              </div>

              {/* Настройки модема */}
              {hasBulkEditModemProxies && (
                <div className="pcp__section">
                  <h3 className="pcp__section-title">Настройки модема</h3>
                  <div className="pcp__info-box" style={{ background: '#ecf1fe' }}>
                    <p className="pcp__info-box-text" style={{ color: '#5283f4' }}>Сохраняются в прокси и применяются при переподключении/автозамене</p>
                  </div>
                  <div className="pcp__row">
                    <div className="pcp__field">
                      <div className="pcp__label"><span className="pcp__label-text">Тип переподключения</span></div>
                      <CustomSelect
                        value={bulkData.modem_reconnect_type || ''}
                        onChange={(val) => setBulkField('modem_reconnect_type', val || undefined)}
                        placeholder="Не изменять"
                        options={[
                          { value: '', label: 'Не изменять' },
                          { value: 'time', label: 'По времени' },
                          { value: 'link', label: 'Ссылка' },
                          { value: 'time_link', label: 'Время + ссылка' },
                        ]}
                      />
                    </div>
                    {bulkData.modem_reconnect_type !== 'link' && (
                      <div className="pcp__field">
                        <div className="pcp__label"><span className="pcp__label-text">Интервал (сек)</span></div>
                        <input type="number" className="pcp__text-input" placeholder="240" value={bulkData.modem_reconnect_interval ?? ''} onChange={(e) => setBulkField('modem_reconnect_interval', e.target.value !== '' ? Number(e.target.value) : undefined)} min={1} max={86400} />
                      </div>
                    )}
                  </div>
                  <div className="pcp__row">
                    <div className="pcp__field">
                      <div className="pcp__label">
                        <span className="pcp__label-text">TCP отпечаток</span>
                        <button
                          type="button"
                          className="pcp__label-action"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleRefreshOsfpList();
                          }}
                          disabled={osfpRefreshLoading}
                          aria-label="Обновить TCP отпечаток"
                          tabIndex={0}
                        >
                          {osfpRefreshLoading ? 'Обновление...' : 'Обновить'}
                        </button>
                      </div>
                      <CustomSelect
                        value={bulkData.modem_osfp_id ? String(bulkData.modem_osfp_id) : ''}
                        onChange={(val) => { const num = val ? Number(val) : undefined; setBulkField('modem_osfp_id', num); if (num) setBulkField('modem_is_osfp', true); }}
                        placeholder="Не изменять"
                        options={[
                          { value: '', label: 'Не изменять' },
                          ...osfpList.map((o) => ({ value: String(o.id), label: o.name || `ID: ${o.id}` })),
                        ]}
                      />
                    </div>
                    <div className="pcp__field">
                      <div className="pcp__toggle-row">
                        <span className="pcp__toggle-label">
                          Включить TCP отпечаток
                          {bulkData.modem_is_osfp === undefined && <span className="pcp__toggle-hint">не изменять</span>}
                        </span>
                        <button
                          type="button"
                          className={`pcp__switch ${bulkData.modem_is_osfp === true ? 'pcp__switch--on' : bulkData.modem_is_osfp === false ? 'pcp__switch--off' : 'pcp__switch--indeterminate'}`}
                          onClick={() => setBulkField('modem_is_osfp', bulkData.modem_is_osfp === undefined ? true : bulkData.modem_is_osfp === true ? false : undefined)}
                          role="switch"
                          aria-checked={bulkData.modem_is_osfp ?? 'mixed'}
                          aria-label="Включить TCP отпечаток"
                          tabIndex={0}
                        >
                          <span className="pcp__switch-dot" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="pcp__footer">
              <button type="button" className="pcp__btn pcp__btn--cancel" onClick={() => { setBulkEditModalVisible(false); setBulkData({}); }} aria-label="Отмена" tabIndex={0}>Отмена</button>
              <button type="button" className="pcp__btn pcp__btn--submit" onClick={() => handleBulkEditProxies(bulkData)} aria-label="Применить" tabIndex={0}>Применить ({selectedRowKeys.length})</button>
            </div>

          </div>
        </div>
      ), document.body)}

      {/* Оверлей прогресса загрузки/обработки */}
      {overlayVisible && (
        <div className={`proxy-loading-overlay${overlayClosing ? ' proxy-loading-overlay--closing' : ''}`}>
          <div className="loader-container">
            <div className="loader-ball" />
            <div className="loader-shadow" />
          </div>
        </div>
      )}

      {/* НОВОЕ: Модальное окно детальной статистики синхронизации */}
      <SyncStatsModal
        visible={syncStatsVisible}
        onClose={() => setSyncStatsVisible(false)}
        stats={syncStats}
        title="Статистика синхронизации прокси"
      />
    </div>
  );
};

export default Proxies;
